import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Play, Pause, Square, SkipBack, SkipForward, Volume2, Mic, Headphones,
  Sliders, Plus, Trash2, Download, Upload, Settings, Grid, Layers,
  Music, Activity, Save, FolderOpen, Undo, Redo, Copy, Scissors,
  X, Clock, Zap, Move, TrendingUp, Globe, Edit3, RotateCcw, Sparkles,
  MousePointer, Triangle, AlignLeft, ChevronUp, Eye
} from 'lucide-react';
import * as Tone from 'tone';

// Import our modular components and utilities
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { projectTemplates } from './constants/projectTemplates';
import { pianoKeyMap, instrumentConfigs, DEFAULT_MUSICAL_TYPING_MAP, NOTE_NAMES } from './constants/instrumentConfigs';
import { SHORTCUTS, matchesShortcut } from './constants/keyboardShortcuts';
import { getVisibleSubdivisions, GRID_DIVISION_OPTIONS } from './utils/gridUtils';
import { getGridStepBeatsFromGridSize, snapBeatToGridStep, approximateDivisionsPerBeatForRuler } from './utils/musicTiming';
import { SynthManager } from './audio/SynthManager';
import { AudioPlayerManager } from './audio/AudioPlayerManager';
import { RoutingManager } from './audio/RoutingManager';
import { WebAudioEngine } from './audio/WebAudioEngine';
import { AudioInputManager } from './audio/AudioInputManager';
import { processAudioFile, formatTime, calculateTimelineLength, analyzeAudioBuffer } from './utils/audioUtils';
import { saveProject, loadProject } from './utils/projectSerializer';
import { downloadBlob, encodeAudioBufferToWavBlob } from './utils/mixdownExport';
import { styles } from './styles/dawStyles';
import { WaveformDisplay } from './components/WaveformDisplay';
import TrackSelectionModal from './components/TrackSelectionModal';
import PianoRoll from './components/PianoRoll';
import AutomationLane, { getAutomationValue } from './components/AutomationLane';
import { TooltipHost } from './components/TooltipHost';
import { LyricsTimelineRow } from './components/LyricsTimelineRow';
import { VOCAL_PRESETS } from './presets/vocal';
import { loadVocalPreset } from './utils/vocalPresets';





// Main DAW component
const DAWWorkstation = () => {
  const { language, setLanguage, t } = useLanguage();

  // Project initialization state
  const [showTemplateSelector, setShowTemplateSelector] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState('empty');
  const [audioEngineReady, setAudioEngineReady] = useState(false);

  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  // Separate playback state used only when recording to avoid conflict with Play button state
  const [isRecordingPlayback, setIsRecordingPlayback] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingStartTime, setRecordingStartTime] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [bpm, setBpm] = useState(120);
  const [timeSignature, setTimeSignature] = useState({ numerator: 4, denominator: 4 });
  const [masterVolume, setMasterVolume] = useState(0.8);
  const [snapToGrid, setSnapToGrid] = useState(true);
  const [gridSize, setGridSize] = useState(16);
  const [loopEnabled, setLoopEnabled] = useState(false);
  const [loopStart, setLoopStart] = useState(null); // beats
  const [loopEnd, setLoopEnd] = useState(null); // beats
  const [isSelectingLoop, setIsSelectingLoop] = useState(false);
  const [metronomeEnabled, setMetronomeEnabled] = useState(false);
  const [showAutomation, setShowAutomation] = useState(false);
  const [quantizeValue, setQuantizeValue] = useState(16);
  const [pixelsPerBeat, setPixelsPerBeat] = useState(50);

  /** Timeline lyrics: { id, startBeat, text }[] — startBeat in quarter-note beats from project start */
  const LYRICS_ROW_HEIGHT = 36;
  const [projectLyrics, setProjectLyrics] = useState([]);
  const [showLyricsRow, setShowLyricsRow] = useState(false);
  /** When lyrics row is visible: true = edit (inputs, drag, delete); false = view-only text */
  const [lyricsRowEditMode, setLyricsRowEditMode] = useState(true);

  // Project identity
  const [projectName, setProjectName] = useState('Untitled Project');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isExportingMixdown, setIsExportingMixdown] = useState(false);
  const loadFileInputRef = useRef(null);

  // History state
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // Track and interface state
  const [tracks, setTracks] = useState([]);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [selectedClip, setSelectedClip] = useState(null);
  // { clipId, x, y } — position of the right-click context menu, null when hidden
  const [clipContextMenu, setClipContextMenu] = useState(null);
  // 'select' = normal pointer/move, 'fade' = fade-in/out drag tool
  const [toolMode, setToolMode] = useState('select');
  const [showPianoRoll, setShowPianoRoll] = useState(false);
  const [showMixer, setShowMixer] = useState(false);
  const [mixerHeight, setMixerHeight] = useState(300); // Mixer height in pixels
  const [isDraggingMixerHandle, setIsDraggingMixerHandle] = useState(false);
  const [isMixerHandleHovered, setIsMixerHandleHovered] = useState(false);
  const [showTrackSelectionModal, setShowTrackSelectionModal] = useState(false);
  const [pianoRollTab, setPianoRollTab] = useState('piano-roll'); // 'instrument', 'chords', 'piano-roll', 'effects'
  const [pianoRollHeight, setPianoRollHeight] = useState(50); // Percentage of screen height
  const [isDraggingPianoRollHandle, setIsDraggingPianoRollHandle] = useState(false);
  const [dragOverTrack, setDragOverTrack] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [draggingClip, setDraggingClip] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDraggingPlayhead, setIsDraggingPlayhead] = useState(false);

  // Track reordering state
  const [draggingTrackId, setDraggingTrackId] = useState(null);
  const [dragOverTrackId, setDragOverTrackId] = useState(null);

  // Piano roll state
  const [pianoRollNotes, setPianoRollNotes] = useState([]);
  const [selectedNotes, setSelectedNotes] = useState([]);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [currentVelocity, setCurrentVelocity] = useState(64); // Default to 50% velocity (64/127)
  const [currentNoteLength, setCurrentNoteLength] = useState(0.25);
  const [pressedKeys, setPressedKeys] = useState(new Set());
  const [pressedDrums, setPressedDrums] = useState(new Set());
  const [cursorMode, setCursorMode] = useState('select'); // 'select' | 'edit'
  const [showDrumKeyMap, setShowDrumKeyMap] = useState(false);
  const [assigningDrumKeyForSample, setAssigningDrumKeyForSample] = useState(null);
  const [showPianoKeyMap, setShowPianoKeyMap] = useState(false);
  const [assigningPianoKeyForNote, setAssigningPianoKeyForNote] = useState(null); // semitone offset being assigned
  const [musicalTypingOctave, setMusicalTypingOctave] = useState(3); // C3 base octave
  const clipboardNotesRef = useRef([]); // copied notes for paste
  const [musicalTypingVelocity, setMusicalTypingVelocity] = useState(98);

  // Automation state
  // automationPoints: { [trackId]: { volume: [{time,value}], pan: [{time,value}] } }
  const [automationPoints, setAutomationPoints] = useState({});
  // automationWriteMode: { [trackId]: 'read' | 'write' } — default is 'read'
  const [automationWriteMode, setAutomationWriteMode] = useState({});
  // automationParam: { [trackId]: 'volume' | 'pan' } — which lane is visible
  const [automationParam, setAutomationParam] = useState({});

  // Audio input state - per track monitoring
  const [trackInputLevels, setTrackInputLevels] = useState({});
  const [monitoringTracks, setMonitoringTracks] = useState(new Set());

  // Refs that mirror automation state so the RAF loop can read them without deps
  const automationPointsRef = useRef({});
  const automationWriteModeRef = useRef({});

  // Refs - using simplified managers
  const animationRef = useRef(null);
  const masterGainRef = useRef(null);
  const recorderRef = useRef(null);
  const metronomeRef = useRef(null);
  const synthManagerRef = useRef(new SynthManager());
  const audioPlayerManagerRef = useRef(new AudioPlayerManager());
  const routingManagerRef = useRef(new RoutingManager());
  const webAudioEngineRef = useRef(new WebAudioEngine());
  const audioInputManagersRef = useRef(new Map()); // Map of trackId -> AudioInputManager
  const timelineRef = useRef(null);
  const playheadRef = useRef(null);
  const playheadHandleRef = useRef(null);
  // playheadRulerRef removed - using unified playhead now
  const suppressRulerClickRef = useRef(false);
  const loopSelectAnchorBeatRef = useRef(null);
  const loopSelectDownXRef = useRef(0);
  const loopRestartInProgressRef = useRef(false);
  const startPlaybackAtRef = useRef(null);

  // Refs for stable keyboard handling and MIDI recording timing
  const tracksRef = useRef(tracks);
  useEffect(() => { tracksRef.current = tracks; }, [tracks]);

  const selectedTrackRef = useRef(selectedTrack);
  useEffect(() => { selectedTrackRef.current = selectedTrack; }, [selectedTrack]);

  const showPianoRollRef = useRef(showPianoRoll);
  useEffect(() => { showPianoRollRef.current = showPianoRoll; }, [showPianoRoll]);

  const isDraggingPlayheadRef = useRef(isDraggingPlayhead);
  useEffect(() => { isDraggingPlayheadRef.current = isDraggingPlayhead; }, [isDraggingPlayhead]);

  const pressedKeysRef = useRef(pressedKeys);
  useEffect(() => { pressedKeysRef.current = pressedKeys; }, [pressedKeys]);

  const currentTimeRef = useRef(currentTime);
  useEffect(() => { currentTimeRef.current = currentTime; }, [currentTime]);

  const isRecordingRef = useRef(isRecording);
  useEffect(() => { isRecordingRef.current = isRecording; }, [isRecording]);

  const audioEngineReadyRef = useRef(audioEngineReady);
  useEffect(() => { audioEngineReadyRef.current = audioEngineReady; }, [audioEngineReady]);

  const snapToGridRef = useRef(snapToGrid);
  useEffect(() => { snapToGridRef.current = snapToGrid; }, [snapToGrid]);

  const gridSizeRef = useRef(gridSize);
  useEffect(() => { gridSizeRef.current = gridSize; }, [gridSize]);

  const currentNoteLengthRef = useRef(currentNoteLength);
  useEffect(() => { currentNoteLengthRef.current = currentNoteLength; }, [currentNoteLength]);

  const currentVelocityRef = useRef(currentVelocity);
  useEffect(() => { currentVelocityRef.current = currentVelocity; }, [currentVelocity]);

  const showDrumKeyMapRef = useRef(showDrumKeyMap);
  useEffect(() => { showDrumKeyMapRef.current = showDrumKeyMap; }, [showDrumKeyMap]);

  const assigningDrumKeyForSampleRef = useRef(assigningDrumKeyForSample);
  useEffect(() => { assigningDrumKeyForSampleRef.current = assigningDrumKeyForSample; }, [assigningDrumKeyForSample]);

  const showPianoKeyMapRef = useRef(showPianoKeyMap);
  useEffect(() => { showPianoKeyMapRef.current = showPianoKeyMap; }, [showPianoKeyMap]);

  const assigningPianoKeyForNoteRef = useRef(assigningPianoKeyForNote);
  useEffect(() => { assigningPianoKeyForNoteRef.current = assigningPianoKeyForNote; }, [assigningPianoKeyForNote]);

  const musicalTypingOctaveRef = useRef(musicalTypingOctave);
  useEffect(() => { musicalTypingOctaveRef.current = musicalTypingOctave; }, [musicalTypingOctave]);

  const musicalTypingVelocityRef = useRef(musicalTypingVelocity);
  useEffect(() => { musicalTypingVelocityRef.current = musicalTypingVelocity; }, [musicalTypingVelocity]);

  // Keep automation refs in sync for RAF loop access
  useEffect(() => { automationPointsRef.current = automationPoints; }, [automationPoints]);
  useEffect(() => { automationWriteModeRef.current = automationWriteMode; }, [automationWriteMode]);

  // Single source of truth for positioning the playhead DOM nodes.
  // Always reads the live scrollLeft from the DOM so it is correct on
  // scroll, zoom change, beat change, or any re-render.
  const syncPlayheadDOM = useCallback((beatTime) => {
    const timelineEl = timelineRef.current;
    if (!timelineEl) return;
    const scrollLeft = timelineEl.scrollLeft;
    const containerWidth = timelineEl.clientWidth;
    const playheadLeft = 280 + beatTime * pixelsPerBeat - scrollLeft;
    const display = (playheadLeft >= 280 && playheadLeft <= containerWidth) ? 'block' : 'none';
    if (playheadRef.current) {
      playheadRef.current.style.left = `${playheadLeft}px`;
      playheadRef.current.style.display = display;
    }
    if (playheadHandleRef.current) {
      playheadHandleRef.current.style.left = `${playheadLeft - 4}px`;
      playheadHandleRef.current.style.display = display;
    }
  }, [pixelsPerBeat]);

  // Keep playhead in the right place when user scrolls (not playing)
  useEffect(() => {
    const timelineEl = timelineRef.current;
    if (!timelineEl) return;
    const handleScroll = () => syncPlayheadDOM(currentTimeRef.current);
    timelineEl.addEventListener('scroll', handleScroll);
    return () => timelineEl.removeEventListener('scroll', handleScroll);
  }, [syncPlayheadDOM]);

  // Re-sync when the beat position changes (click-to-relocate, stop, zoom change)
  useEffect(() => {
    syncPlayheadDOM(currentTimeRef.current);
  }, [currentTime, syncPlayheadDOM]);

  const handlePianoKeyClickRef = useRef(null);

  const midiRecordingContextRef = useRef(null); // { trackId, clipId, clipStart }
  const recordedMidiThisTakeRef = useRef(false);
  const activeNotesByKeyRef = useRef(new Map()); // key -> { noteId, trackId, clipId, startBeat }

  const drumSampleNameToMidi = React.useMemo(() => {
    const map = new Map();
    const drumMap = instrumentConfigs?.drums?.drumMap || {};
    Object.entries(drumMap).forEach(([midi, info]) => {
      if (info?.name) map.set(info.name, Number(midi));
    });
    return map;
  }, []);

  const beatsPerBar = Math.max(1, timeSignature?.numerator ?? 4);
  const alignBeatsToBar = useCallback((beats) => {
    const safeBeats = Number.isFinite(beats) ? Math.max(0, beats) : 0;
    return Math.ceil(safeBeats / beatsPerBar) * beatsPerBar;
  }, [beatsPerBar]);

  const requiredTimelineBeats = alignBeatsToBar(calculateTimelineLength(tracks));
  // Ensure minimum timeline covers at least the viewport width (estimate ~1200px) plus buffer
  const minInitialBeats = Math.max(requiredTimelineBeats, alignBeatsToBar(Math.ceil((window.innerWidth * 3) / pixelsPerBeat)));
  const [timelineBeats, setTimelineBeats] = useState(minInitialBeats);

  useEffect(() => {
    setTimelineBeats(prev => Math.max(prev, requiredTimelineBeats));
  }, [requiredTimelineBeats]);

  useEffect(() => {
    const maxLyricBeat = projectLyrics.reduce((m, l) => Math.max(m, Number(l.startBeat) || 0), 0);
    if (maxLyricBeat <= 0) return;
    setTimelineBeats(prev => Math.max(prev, alignBeatsToBar(Math.ceil(maxLyricBeat) + beatsPerBar)));
  }, [projectLyrics, alignBeatsToBar, beatsPerBar]);

  useEffect(() => {
    setTimelineBeats(prev => alignBeatsToBar(prev));
  }, [alignBeatsToBar]);

  const timelineWidth = timelineBeats * pixelsPerBeat;
  const timelineBars = Math.ceil(timelineBeats / beatsPerBar);
  const pixelsPerBar = beatsPerBar * pixelsPerBeat;
  const baseDivisionsPerBeat = approximateDivisionsPerBeatForRuler(gridSize || 16);

  // Track lanes: only bar lines + beat (1/4 note) lines, no finer subdivisions
  const trackLaneGridCSS = React.useMemo(() => {
    const barPx = pixelsPerBeat * beatsPerBar;
    return {
      backgroundImage: [
        // beat lines
        `repeating-linear-gradient(to right, transparent 0px, transparent ${pixelsPerBeat - 1}px, rgba(255,255,255,0.06) ${pixelsPerBeat - 1}px, rgba(255,255,255,0.06) ${pixelsPerBeat}px)`,
        // bar lines
        `repeating-linear-gradient(to right, transparent 0px, transparent ${barPx - 1}px, rgba(255,255,255,0.18) ${barPx - 1}px, rgba(255,255,255,0.18) ${barPx}px)`,
      ].join(', '),
      backgroundSize: `${pixelsPerBeat}px 100%, ${barPx}px 100%`,
    };
  }, [pixelsPerBeat, beatsPerBar]);

  // Finest subdivision pixel size for ruler CSS background (shows when zoomed in)
  const rulerTicks = React.useMemo(() => {
    const subs = getVisibleSubdivisions(pixelsPerBeat, beatsPerBar, baseDivisionsPerBeat);
    const finestDiv = subs.length > 0 ? subs[subs.length - 1].divisionsPerBeat : 1;
    const pxPerTick = pixelsPerBeat / finestDiv;
    return { pxPerTick };
  }, [pixelsPerBeat, beatsPerBar, baseDivisionsPerBeat]);

  const loopRegion = React.useMemo(() => {
    if (loopStart == null || loopEnd == null) return null;
    const start = Math.max(0, Math.min(loopStart, loopEnd));
    const end = Math.max(loopStart, loopEnd);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
    return { start, end };
  }, [loopStart, loopEnd]);

  const loopEnabledRef = useRef(loopEnabled);
  loopEnabledRef.current = loopEnabled;

  const loopRegionRef = useRef(loopRegion);
  loopRegionRef.current = loopRegion;

  const selectedClipData = selectedClip
    ? (() => {
      const track = tracks.find(t => (t.clips || []).some(c => c.id === selectedClip));
      return track?.clips?.find(c => c.id === selectedClip) || null;
    })()
    : null;

  const ensureTimelineCoversPx = useCallback((targetRightPx) => {
    const requiredBeats = Math.ceil(targetRightPx / pixelsPerBeat);
    const requiredAlignedBeats = alignBeatsToBar(requiredBeats);
    setTimelineBeats(prev => Math.max(prev, requiredAlignedBeats));
  }, [alignBeatsToBar, pixelsPerBeat]);

  // Infinite horizontal timeline: extend bars as the user scrolls or as playback/recording advances.
  useEffect(() => {
    const el = timelineRef.current;
    if (!el) return;

    const extendTimeline = () => {
      // Always extend timeline to cover current scroll position plus large buffer
      const visibleWidth = el.clientWidth || window.innerWidth;
      const rightEdge = el.scrollLeft + visibleWidth;
      // Always ensure we have at least 3x viewport width of content ahead
      ensureTimelineCoversPx(rightEdge + visibleWidth * 3);
    };

    const handleScroll = () => {
      extendTimeline();
    };

    // Handle wheel events to detect scrolling attempts even at the edge
    const handleWheel = (e) => {
      // Extend on any horizontal scroll attempt (positive deltaX = scroll right)
      // Also extend on shift+vertical scroll which some browsers use for horizontal
      if (e.deltaX > 0 || (e.shiftKey && e.deltaY > 0)) {
        extendTimeline();
      }
    };

    // Handle pointer/touch move for trackpad gestures
    const handlePointerMove = (e) => {
      if (e.pointerType === 'touch' || e.pressure > 0) {
        extendTimeline();
      }
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    el.addEventListener('wheel', handleWheel, { passive: true });
    el.addEventListener('pointermove', handlePointerMove, { passive: true });

    // Initial call - use requestAnimationFrame to ensure layout is complete
    requestAnimationFrame(() => {
      extendTimeline();
    });

    // Also set up an interval to check periodically (backup for edge cases)
    const intervalId = setInterval(() => {
      const visibleWidth = el.clientWidth || window.innerWidth;
      const rightEdge = el.scrollLeft + visibleWidth;
      const scrollableWidth = el.scrollWidth;
      // If near the right edge, extend
      if (scrollableWidth - rightEdge < 1000) {
        extendTimeline();
      }
    }, 500);

    return () => {
      el.removeEventListener('scroll', handleScroll);
      el.removeEventListener('wheel', handleWheel);
      el.removeEventListener('pointermove', handlePointerMove);
      clearInterval(intervalId);
    };
  }, [ensureTimelineCoversPx]);

  useEffect(() => {
    if (!Number.isFinite(currentTime)) return;
    const targetBeats = currentTime + beatsPerBar * 8;
    setTimelineBeats(prev => Math.max(prev, alignBeatsToBar(targetBeats)));
  }, [currentTime, beatsPerBar, alignBeatsToBar]);

  // Toggle audio input monitoring for a specific track
  const toggleAudioMonitoring = async (trackId) => {
    const isCurrentlyMonitoring = monitoringTracks.has(trackId);

    if (isCurrentlyMonitoring) {
      // Stop monitoring this track
      console.log('🎙️ Stopping audio monitoring for track:', trackId);

      const manager = audioInputManagersRef.current.get(trackId);
      if (manager) {
        manager.dispose();
        audioInputManagersRef.current.delete(trackId);
      }

      // Update state
      setMonitoringTracks(prev => {
        const newSet = new Set(prev);
        newSet.delete(trackId);
        return newSet;
      });

      setTrackInputLevels(prev => {
        const newLevels = { ...prev };
        delete newLevels[trackId];
        return newLevels;
      });

    } else {
      // Start monitoring this track
      console.log('🎙️ Starting audio monitoring for track:', trackId);

      try {
        const manager = new AudioInputManager();
        const success = await manager.initialize();

        if (success) {
          // Set level update callback for this specific track
          manager.setLevelUpdateCallback((level) => {
            setTrackInputLevels(prev => ({
              ...prev,
              [trackId]: level
            }));
          });

          // Store the manager
          audioInputManagersRef.current.set(trackId, manager);

          // Update monitoring state
          setMonitoringTracks(prev => new Set([...prev, trackId]));

          console.log('🎙️ Audio monitoring started successfully for track:', trackId);
        } else {
          console.error('❌ Failed to initialize audio monitoring for track:', trackId);
          alert('Failed to initialize microphone. Please check:\n1. Microphone permissions\n2. Microphone is not muted\n3. No other app is using the microphone');
        }
      } catch (error) {
        console.error('❌ Error starting audio monitoring for track:', trackId, error);
        alert(`Error accessing microphone: ${error.message}`);
      }
    }
  };

  // Add clip to track helper function
  const addClipToTrack = (trackId, clip) => {
    setTracks(prevTracks => {
      return prevTracks.map(track => {
        if (track.id === trackId) {
          return {
            ...track,
            clips: [...track.clips, clip]
          };
        }
        return track;
      });
    });
  };

  // Clean up all audio resources
  const cleanupAllAudio = () => {
    // Stop transport
    Tone.Transport.stop();
    Tone.Transport.cancel();

    // Clean up Web Audio Engine resources
    webAudioEngineRef.current.dispose();

    // Clean up all audio input resources
    audioInputManagersRef.current.forEach((manager, trackId) => {
      console.log(`🎙️ Disposing audio input manager for track: ${trackId}`);
      manager.dispose();
    });
    audioInputManagersRef.current.clear();

    // Reset audio input states
    setMonitoringTracks(new Set());
    setTrackInputLevels({});

    // Clean up legacy resources
    synthManagerRef.current.disposeAll();
    audioPlayerManagerRef.current.disposeAll();
  };

  // Create project from template (ensure proper initialization)
  const createProjectFromTemplate = async (templateKey) => {
    console.log(`🎬 Creating project from template: ${templateKey}`);

    if (isPlaying) {
      handleStop();
    }
    cleanupAllAudio();

    const template = projectTemplates[templateKey];
    const newTracks = template.tracks.map(track => ({
      ...track,
      effects: {
        reverb: 0.2, delay: 0, chorus: 0,
        eq: { low: 0, mid: 0, high: 0 },
        compressor: { threshold: -20, ratio: 4, attack: 0.003, release: 0.25 },
        delayParams: {
          delayTime: 0.25,
          feedback: 0.3
        },
        chorusParams: {
          rate: 0.5,
          depth: 0.5
        },
        compressor: {
          threshold: -24,
          ratio: 4,
          attack: 0.003,
          release: 0.25,
          knee: 30,
          makeupGain: 0,
          outputGain: 0
        },
        distortion: {
          preGain: 0,
          drive: 0,
          tone: 5000,
          mode: 'clean'
        }
      },
      automation: { volume: [], pan: [] }
    }));

    setTracks(newTracks);
    setSelectedTrack(newTracks.length > 0 ? newTracks[0].id : null);
    setSelectedClip(null);
    setCurrentTime(0);
    setProjectLyrics([]);
    setShowLyricsRow(false);
    setShowTemplateSelector(false);

    // Make sure audio engine is initialized before tracks
    setTimeout(async () => {
      await initializeAudioEngine();

      // Wait a bit to ensure audio engine is fully ready
      setTimeout(() => {
        initializeTracks(newTracks);
        saveToHistory();

        // 如果有MIDI轨道，创建一个测试片段
        const firstMidiTrack = newTracks.find(t => t.type === 'midi');
        if (firstMidiTrack) {
          setTimeout(() => {
            console.log('🎼 Creating test clip for template...');
            createMidiClip(firstMidiTrack.id, 0);
          }, 500);
        }
      }, 200);
    }, 100);
  };


  // Initialize audio engine with Web Audio API
  const initializeAudioEngine = async () => {
    try {
      console.log('🎛️ Initializing Web Audio engine...');

      // Initialize Web Audio Engine
      await webAudioEngineRef.current.initialize();

      // Make the audio engine instance available globally for debugging
      window.webAudioEngine = webAudioEngineRef.current;

      console.log('✅ Web Audio context started');

      // Set master volume
      webAudioEngineRef.current.setMasterVolume(masterVolume);
      console.log('✅ Master gain created');

      // Set playback end callback
      webAudioEngineRef.current.setPlaybackEndCallback(() => {
        console.log('🎵 Audio playback ended callback triggered');
        setIsPlaying(false);
        setIsPaused(false);
      });

      // For backward compatibility, we'll keep the Tone.js objects
      // but they won't be used for actual audio processing
      if (masterGainRef.current) {
        masterGainRef.current.dispose();
      }
      masterGainRef.current = new Tone.Gain(masterVolume).toDestination();

      // Initialize managers for backward compatibility
      routingManagerRef.current.initialize(masterGainRef.current);
      synthManagerRef.current.initialize(routingManagerRef.current.getTrackInput.bind(routingManagerRef.current));
      audioPlayerManagerRef.current.initialize(masterGainRef.current, routingManagerRef.current.getTrackInput.bind(routingManagerRef.current));

      // Create recorder (still using Tone.js for now)
      if (recorderRef.current) {
        recorderRef.current.dispose();
      }
      recorderRef.current = new Tone.Recorder();
      masterGainRef.current.connect(recorderRef.current);
      console.log('✅ Recorder created');

      // Create metronome (still using Tone.js for now)
      if (metronomeRef.current) {
        metronomeRef.current.dispose();
      }
      metronomeRef.current = new Tone.Synth({
        oscillator: { type: 'sine' },
        envelope: { attack: 0.001, decay: 0.1, sustain: 0, release: 0.1 }
      }).toDestination();
      console.log('✅ Metronome created');

      setAudioEngineReady(true);
      console.log('✅ Audio engine ready');

      // Play a short test note to confirm audio works
      setTimeout(() => {
        try {
          // Test with Web Audio API
          const testSynth = webAudioEngineRef.current.createSynth('test', 'piano');
          testSynth.triggerAttackRelease('C4', 0.5, null, 0.3);
          console.log('🎵 Audio engine test successful');
        } catch (error) {
          console.warn('Audio engine test failed:', error);
        }
      }, 500);

    } catch (error) {
      console.error('❌ Failed to initialize audio engine:', error);
      setAudioEngineReady(false);
    }
  };

  // 保存历史记录
  const saveToHistory = useCallback(() => {
    const currentState = {
      tracks: JSON.parse(JSON.stringify(tracks)),
      selectedTrack,
      bpm,
      masterVolume,
      projectLyrics: JSON.parse(JSON.stringify(projectLyrics)),
      showLyricsRow,
      lyricsRowEditMode,
      timestamp: Date.now()
    };

    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(currentState);

    if (newHistory.length > 50) {
      newHistory.shift();
    }

    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [tracks, selectedTrack, bpm, masterVolume, projectLyrics, showLyricsRow, lyricsRowEditMode, history, historyIndex]);

  // 撤销/重做
  const handleUndo = () => {
    if (historyIndex > 0) {
      const prevIndex = historyIndex - 1;
      const prevState = history[prevIndex];
      setTracks(JSON.parse(JSON.stringify(prevState.tracks)));
      setSelectedTrack(prevState.selectedTrack);
      setBpm(prevState.bpm);
      setMasterVolume(prevState.masterVolume);
      setProjectLyrics(JSON.parse(JSON.stringify(prevState.projectLyrics ?? [])));
      setShowLyricsRow(prevState.showLyricsRow ?? false);
      setLyricsRowEditMode(prevState.lyricsRowEditMode ?? true);
      setHistoryIndex(prevIndex);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const nextIndex = historyIndex + 1;
      const nextState = history[nextIndex];
      setTracks(JSON.parse(JSON.stringify(nextState.tracks)));
      setSelectedTrack(nextState.selectedTrack);
      setBpm(nextState.bpm);
      setMasterVolume(nextState.masterVolume);
      setProjectLyrics(JSON.parse(JSON.stringify(nextState.projectLyrics ?? [])));
      setShowLyricsRow(nextState.showLyricsRow ?? false);
      setLyricsRowEditMode(nextState.lyricsRowEditMode ?? true);
      setHistoryIndex(nextIndex);
    }
  };

  // ── Save / Load project ──

  const handleSaveProject = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      await saveProject({
        projectName,
        bpm,
        timeSignature,
        masterVolume,
        snapToGrid,
        gridSize,
        pixelsPerBeat,
        currentTime: currentTimeRef.current,
        loopStart,
        loopEnd,
        loopEnabled,
        metronomeEnabled,
        showAutomation,
        tracks,
        automationPoints,
        automationWriteMode,
        automationParam,
        projectLyrics,
        showLyricsRow,
        lyricsRowEditMode,
      });
    } catch (err) {
      console.error('[Save] Error saving project:', err);
      alert(`Save failed: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  /** Offline render full timeline → WAV (Download toolbar control). */
  const handleExportMixdown = async () => {
    if (isExportingMixdown) return;
    if (!audioEngineReady || !webAudioEngineRef.current) {
      alert(t.audioEngineNotReady);
      return;
    }
    const projectTracks = tracksRef.current?.length ? tracksRef.current : tracks;
    const hasClips = projectTracks.some(t => (t.clips || []).length > 0);
    if (!hasClips) {
      alert('Add at least one clip before exporting audio.');
      return;
    }

    setIsExportingMixdown(true);
    try {
      await webAudioEngineRef.current.initialize();

      if (isPlaying || isRecordingPlayback) {
        webAudioEngineRef.current.stopGlobalPlayback();
        try {
          Tone.Transport.stop();
          Tone.Transport.cancel();
        } catch (_) { /* ignore */ }
        setIsPlaying(false);
        setIsRecordingPlayback(false);
        setIsPaused(false);
      }

      const buffer = await webAudioEngineRef.current.renderMixdown({
        tracks: projectTracks,
        bpm,
        masterVolume,
        automationPoints,
        tailSeconds: 2,
      });

      if (!buffer) {
        alert('Nothing to render.');
        return;
      }

      const safeBase = (projectName || 'mixdown').replace(/[^a-z0-9_\- ]/gi, '_').trim() || 'mixdown';
      const blob = encodeAudioBufferToWavBlob(buffer);
      downloadBlob(blob, `${safeBase}.wav`);
    } catch (err) {
      console.error('[Export mixdown]', err);
      alert(`Export failed: ${err.message || err}`);
    } finally {
      setIsExportingMixdown(false);
    }
  };

  const handleLoadProject = async (file) => {
    if (!file || isLoading) return;

    // Warn before replacing an existing project
    if (tracks.length > 0) {
      const ok = window.confirm(
        'Opening a project will replace your current project. Any unsaved changes will be lost.\n\nContinue?'
      );
      if (!ok) return;
    }

    setIsLoading(true);
    try {
      // Resolve the raw Web Audio AudioContext from Tone.js
      const toneCtx = Tone.getContext();
      const audioCtx = toneCtx.rawContext ?? toneCtx._context ?? toneCtx;
      const state = await loadProject(file, audioCtx);

      // Stop any current playback before replacing state
      if (isPlaying || isRecordingPlayback) {
        webAudioEngineRef.current?.stopGlobalPlayback?.();
        setIsPlaying(false);
        setIsRecordingPlayback(false);
        setIsPaused(false);
      }

      // Restore all state
      setProjectName(state.projectName);
      setBpm(state.bpm);
      setTimeSignature(state.timeSignature);
      setMasterVolume(state.masterVolume);
      setSnapToGrid(state.snapToGrid);
      setGridSize(state.gridSize);
      setPixelsPerBeat(state.pixelsPerBeat);
      setLoopStart(state.loopStart);
      setLoopEnd(state.loopEnd);
      setLoopEnabled(state.loopEnabled);
      setMetronomeEnabled(state.metronomeEnabled);
      setShowAutomation(state.showAutomation);
      setAutomationPoints(state.automationPoints);
      setAutomationWriteMode(state.automationWriteMode);
      setAutomationParam(state.automationParam);
      setTracks(state.tracks);
      setProjectLyrics(state.projectLyrics ?? []);
      setShowLyricsRow(state.showLyricsRow ?? false);
      setLyricsRowEditMode(state.lyricsRowEditMode ?? true);

      // Reset current playhead
      currentTimeRef.current = state.currentTime;
      setCurrentTime(state.currentTime);

      // Clear selections
      setSelectedClip(null);
      setSelectedTrack(null);
      setSelectedNotes([]);

      // Close piano roll if open, and exit landing page if we're on it
      setShowPianoRoll(false);
      setShowTemplateSelector(false);
    } catch (err) {
      console.error('[Load] Error loading project:', err);
      alert(`Load failed: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Global keyboard shortcuts (capture phase) ──
  // Uses centralized SHORTCUTS definitions from keyboardShortcuts.js
  useEffect(() => {
    const handleGlobalShortcuts = (e) => {
      const activeEl = document.activeElement;
      const isTyping =
        activeEl &&
        (activeEl.tagName === 'INPUT' ||
          activeEl.tagName === 'TEXTAREA' ||
          activeEl.tagName === 'SELECT' ||
          activeEl.isContentEditable);

      // Undo / Redo always work (even while typing in some cases)
      if (matchesShortcut(e, SHORTCUTS.REDO) || matchesShortcut(e, SHORTCUTS.REDO_ALT)) {
        e.preventDefault();
        e.stopPropagation();
        handleRedo();
        return;
      }
      if (matchesShortcut(e, SHORTCUTS.UNDO)) {
        e.preventDefault();
        e.stopPropagation();
        handleUndo();
        return;
      }

      // Skip the rest while typing in text fields
      if (isTyping) return;

      // ── Piano Roll note shortcuts ──
      if (showPianoRoll && selectedClip) {
        const clipTrack = tracks.find(t => (t.clips || []).some(c => c.id === selectedClip));
        const clipData = clipTrack?.clips?.find(c => c.id === selectedClip);

        // Delete selected notes
        if (selectedNotes.length > 0 && (matchesShortcut(e, SHORTCUTS.DELETE) || matchesShortcut(e, SHORTCUTS.DELETE_BACKSPACE))) {
          e.preventDefault();
          e.stopPropagation();
          setTracks(prev => prev.map(track => ({
            ...track,
            clips: track.clips.map(clip => {
              if (clip.id !== selectedClip) return clip;
              return {
                ...clip,
                notes: (clip.notes || []).filter(n => !selectedNotes.includes(n.id))
              };
            })
          })));
          setSelectedNotes([]);
          saveToHistory();
          return;
        }

        // Copy selected notes
        if (selectedNotes.length > 0 && matchesShortcut(e, SHORTCUTS.COPY)) {
          e.preventDefault();
          e.stopPropagation();
          if (clipData) {
            const notesToCopy = (clipData.notes || []).filter(n => selectedNotes.includes(n.id));
            if (notesToCopy.length > 0) {
              const minStart = Math.min(...notesToCopy.map(n => n.start));
              clipboardNotesRef.current = notesToCopy.map(n => ({
                ...n,
                start: n.start - minStart
              }));
            }
          }
          return;
        }

        // Cut selected notes (copy + delete)
        if (selectedNotes.length > 0 && matchesShortcut(e, SHORTCUTS.CUT)) {
          e.preventDefault();
          e.stopPropagation();
          if (clipData) {
            const notesToCopy = (clipData.notes || []).filter(n => selectedNotes.includes(n.id));
            if (notesToCopy.length > 0) {
              const minStart = Math.min(...notesToCopy.map(n => n.start));
              clipboardNotesRef.current = notesToCopy.map(n => ({
                ...n,
                start: n.start - minStart
              }));
            }
          }
          setTracks(prev => prev.map(track => ({
            ...track,
            clips: track.clips.map(clip => {
              if (clip.id !== selectedClip) return clip;
              return {
                ...clip,
                notes: (clip.notes || []).filter(n => !selectedNotes.includes(n.id))
              };
            })
          })));
          setSelectedNotes([]);
          saveToHistory();
          return;
        }

        // Paste notes at the current playhead position relative to clip start
        if (clipboardNotesRef.current.length > 0 && matchesShortcut(e, SHORTCUTS.PASTE)) {
          e.preventDefault();
          e.stopPropagation();
          const clipStart = clipData?.start || 0;
          const pasteTime = Math.max(0, currentTime - clipStart);
          const gridInterval = getGridStepBeatsFromGridSize(gridSize);
          const snappedPaste = snapToGrid
            ? Math.round(pasteTime / gridInterval) * gridInterval
            : pasteTime;

          const newNotes = clipboardNotesRef.current.map(n => ({
            ...n,
            id: Date.now() + Math.random(),
            start: snappedPaste + n.start
          }));
          const newNoteIds = newNotes.map(n => n.id);

          setTracks(prev => prev.map(track => ({
            ...track,
            clips: track.clips.map(clip => {
              if (clip.id !== selectedClip) return clip;
              const allNotes = [...(clip.notes || []), ...newNotes];
              const gridStep = getGridStepBeatsFromGridSize(gridSize);
              const maxEnd = allNotes.reduce((m, n) => Math.max(m, (n.start || 0) + (n.duration || 0)), 0);
              const requiredDuration = Math.max(clip.duration || 0, Math.ceil(maxEnd / gridStep) * gridStep);
              return { ...clip, duration: requiredDuration, notes: allNotes };
            })
          })));
          setSelectedNotes(newNoteIds);
          saveToHistory();
          return;
        }

        // Select all notes in clip
        if (matchesShortcut(e, SHORTCUTS.SELECT_ALL)) {
          e.preventDefault();
          e.stopPropagation();
          if (clipData) {
            setSelectedNotes((clipData.notes || []).map(n => n.id));
          }
          return;
        }

        // Duplicate selected notes (paste in place, offset by shortest note duration)
        if (selectedNotes.length > 0 && matchesShortcut(e, SHORTCUTS.DUPLICATE)) {
          e.preventDefault();
          e.stopPropagation();
          if (clipData) {
            const notesToDup = (clipData.notes || []).filter(n => selectedNotes.includes(n.id));
            if (notesToDup.length > 0) {
              const maxEnd = Math.max(...notesToDup.map(n => (n.start || 0) + (n.duration || 0)));
              const minStart = Math.min(...notesToDup.map(n => n.start));
              const offset = maxEnd - minStart;
              const newNotes = notesToDup.map(n => ({
                ...n,
                id: Date.now() + Math.random(),
                start: n.start + offset
              }));
              const newNoteIds = newNotes.map(n => n.id);

              setTracks(prev => prev.map(track => ({
                ...track,
                clips: track.clips.map(clip => {
                  if (clip.id !== selectedClip) return clip;
                  const allNotes = [...(clip.notes || []), ...newNotes];
                  const gridStep = getGridStepBeatsFromGridSize(gridSize);
                  const maxEnd2 = allNotes.reduce((m, n) => Math.max(m, (n.start || 0) + (n.duration || 0)), 0);
                  const requiredDuration = Math.max(clip.duration || 0, Math.ceil(maxEnd2 / gridStep) * gridStep);
                  return { ...clip, duration: requiredDuration, notes: allNotes };
                })
              })));
              setSelectedNotes(newNoteIds);
              saveToHistory();
            }
          }
          return;
        }
      }

      // Delete selected clip (when no notes are selected or piano roll is closed)
      if (selectedClip && selectedNotes.length === 0 && (matchesShortcut(e, SHORTCUTS.DELETE) || matchesShortcut(e, SHORTCUTS.DELETE_BACKSPACE))) {
        e.preventDefault();
        e.stopPropagation();
        audioPlayerManagerRef.current.disposePlayer(selectedClip);
        setTracks(prev => prev.map(track => ({
          ...track,
          clips: track.clips.filter(clip => clip.id !== selectedClip)
        })));
        setSelectedClip(null);
        saveToHistory();
        return;
      }
    };

    window.addEventListener('keydown', handleGlobalShortcuts, true);
    return () => window.removeEventListener('keydown', handleGlobalShortcuts, true);
  }, [historyIndex, history, showPianoRoll, selectedClip, selectedNotes, tracks, currentTime, gridSize, snapToGrid]);

  // Initialize tracks with Web Audio Engine
  const initializeTracks = (tracksToInit = tracks) => {
    console.log(`🎛️ Initializing ${tracksToInit.length} tracks with Web Audio Engine...`);

    tracksToInit.forEach(track => {
      if (track.type === 'midi' && audioEngineReady) {
        // Create synth in Web Audio Engine
        const synth = webAudioEngineRef.current.createSynth(track.id, track.instrument, track.soundfontInstrument);
        console.log(`🎹 Track ${track.id} synth created in Web Audio Engine:`, !!synth);
      }

      // We'll initialize audio input only when the user clicks the record button
      if (track.name === 'Voice & Mic' && audioEngineReady) {
        console.log(`🎙️ Voice & Mic track ${track.id} found - will initialize audio input when needed`);
      }

      // Update track parameters in Web Audio Engine
      webAudioEngineRef.current.updateTrackParams(track.id, {
        volume: track.volume,
        pan: track.pan
      });

      // Update track effects
      if (track.effects) {
        webAudioEngineRef.current.updateTrackEffects(track.id, track.effects);
      }
    });

    console.log('✅ Track initialization with Web Audio Engine complete');
  };

  // 清理音频资源
  useEffect(() => {
    return () => {
      if (audioEngineReady) {
        cleanupAllAudio();
        masterGainRef.current?.dispose();
        recorderRef.current?.dispose();
        metronomeRef.current?.dispose();
      }
    };
  }, [audioEngineReady]);

  // Update master volume
  useEffect(() => {
    // Update Web Audio Engine master volume
    if (audioEngineReady) {
      webAudioEngineRef.current.setMasterVolume(masterVolume);
    }

    // Update legacy Tone.js master gain for compatibility
    if (masterGainRef.current) {
      masterGainRef.current.gain.rampTo(masterVolume, 0.1);
    }
  }, [masterVolume, audioEngineReady]);

  // 节拍器
  useEffect(() => {
    if (metronomeEnabled && (isPlaying || isRecording) && audioEngineReady) {
      const metronomeLoop = new Tone.Loop(time => {
        if (metronomeRef.current) {
          metronomeRef.current.triggerAttackRelease("C5", "16n", time);
        }
      }, "4n");
      metronomeLoop.start(0);

      return () => metronomeLoop.dispose();
    }
  }, [metronomeEnabled, isPlaying, isRecording, audioEngineReady]);

  const startPlaybackAt = useCallback((requestedStartBeat, { resetTransport = false } = {}) => {
    const safeRequested = Number.isFinite(requestedStartBeat) ? Math.max(0, requestedStartBeat) : 0;
    const region = loopRegionRef.current;
    const shouldLoop = loopEnabledRef.current && !!region;
    const startBeat = shouldLoop && (safeRequested < region.start || safeRequested >= region.end)
      ? region.start
      : safeRequested;

    console.log(`▶️ Starting playback from ${startBeat} beats${shouldLoop ? ' (loop)' : ''}`);

    const projectTracks = Array.isArray(tracksRef.current) ? tracksRef.current : [];

    // 收集所有音频和MIDI片段
    let hasValidTracks = false;
    const allClips = [];
    projectTracks.forEach(track => {
      if (track.mute) {
        console.log(`⏭️ Skipping muted track ${track.id}`);
        return;
      }

      hasValidTracks = true;
      console.log(`🎼 Processing track ${track.id} "${track.name}" (${track.type}) with ${(track.clips || []).length} clips`);

      (track.clips || []).forEach(clip => {
        allClips.push({ ...clip, trackId: track.id, trackType: track.type, trackVolume: track.volume, trackPan: track.pan });
      });
    });

    const audioClips = allClips.filter(clip => clip.type === 'audio' && clip.audioBuffer);

    // Fast check for any MIDI events at/after startBeat (so we don't start an empty transport)
    let hasMidiEvents = false;
    projectTracks.forEach(track => {
      if (track.mute || track.type !== 'midi') return;
      (track.clips || []).forEach(clip => {
        (clip.notes || []).forEach(note => {
          const noteStartBeat = (Number(clip.start) || 0) + (Number(note.start) || 0);
          if (noteStartBeat - startBeat >= 0) hasMidiEvents = true;
        });
      });
    });

    if (audioClips.length === 0 && !hasMidiEvents) {
      console.log('⚠️ No content to play from this position');
      if (!hasValidTracks) {
        alert(t.atLeastOneTrack);
      }
      return;
    }

    // Reset legacy transport only for fresh starts (avoid canceling Tone.Loop metronome on loop restarts)
    if (resetTransport) {
      Tone.Transport.stop();
      Tone.Transport.cancel();
    }
    audioPlayerManagerRef.current.stopGlobalPlayback();
    Tone.Transport.bpm.value = bpm;

    // Start WebAudioEngine clock + audio clips (clock runs even if audioClips is empty)
    webAudioEngineRef.current.startGlobalPlayback(audioClips, startBeat, projectTracks, bpm, { keepAlive: shouldLoop });
    const engineStartTime = webAudioEngineRef.current.startTime;

    // Schedule MIDI notes
    let totalMidiNotes = 0;
    const midiTracks = projectTracks.filter(track => track.type === 'midi' && !track.mute);
    midiTracks.forEach(track => {
      if ((track.clips || []).length === 0) {
        console.log(`  📋 No clips in MIDI track ${track.id}`);
        return;
      }

      const isDrumsTrack = track.instrument === 'drums' || (track.name || '').toLowerCase().includes('drum');

      // Ensure synth exists in Web Audio Engine (non-drum tracks)
      let synth = null;
      if (!isDrumsTrack) {
        synth = webAudioEngineRef.current.getSynth(track.id);
        if (!synth) {
          console.log(`🎹 Creating missing synth for track ${track.id} with Web Audio Engine`);
          synth = webAudioEngineRef.current.createSynth(track.id, track.instrument, track.soundfontInstrument);
        }

        if (!synth) {
          console.warn(`⚠️ Failed to get/create synth for track ${track.id}`);
          return;
        }
      }

      (track.clips || []).forEach((clip, clipIndex) => {
        if (!clip.notes || clip.notes.length === 0) {
          console.log(`  📋 MIDI Clip ${clipIndex + 1}: "${clip.name}" - No notes`);
          return;
        }

        console.log(`  📋 MIDI Clip ${clipIndex + 1}: "${clip.name}" - ${clip.notes.length} notes`);

        // contentOffset: how many beats into the note array the visible window starts (from left trim)
        const contentOffset = Number(clip.contentOffset) || 0;
        const clipDurationBeats = Number(clip.duration) || Infinity;

        // Determine number of loop iterations to schedule
        const isLooping = clip.loop && clip.sourceDuration > 0;
        const sourceDurBeats = isLooping ? clip.sourceDuration : clipDurationBeats;
        const numLoopIters = isLooping ? Math.ceil(clipDurationBeats / sourceDurBeats) : 1;

        for (let loopIter = 0; loopIter < numLoopIters; loopIter++) {
          const loopOffsetBeats = loopIter * sourceDurBeats;

          clip.notes.forEach((note, noteIndex) => {
            const noteRelStart = (Number(note.start) || 0) - contentOffset;
            // Skip notes outside the source window
            if (noteRelStart + (note.duration || 0) <= 0) return;
            if (noteRelStart >= sourceDurBeats) return;

            // Position within the full clip duration (with loop offset)
            const noteClipRelStart = loopOffsetBeats + noteRelStart;
            if (noteClipRelStart >= clipDurationBeats) return;

            const noteStartTime = (Number(clip.start) || 0) + noteClipRelStart - startBeat;
            if (noteStartTime < 0) return;

            try {
              const noteVelocity = (note.velocity ?? 1.0) * track.volume;
              const startTimeSeconds = webAudioEngineRef.current.beatsToSeconds(noteStartTime, bpm);
              const absoluteStartTime = engineStartTime + startTimeSeconds;

              if (isDrumsTrack && webAudioEngineRef.current?.playDrumSampleByName) {
                const drumName = instrumentConfigs?.drums?.drumMap?.[note.pitch]?.name;
                if (drumName) {
                  webAudioEngineRef.current.playDrumSampleByName(drumName, track.id, Math.min(1, noteVelocity), absoluteStartTime);
                  totalMidiNotes++;
                  return;
                }
              }

              const noteName = Tone.Frequency(note.pitch, "midi").toNote();
              // Clamp note so it doesn't spill past the clip boundary
              const maxNoteDur = clipDurationBeats - noteClipRelStart;
              const noteDurBeats = Math.min(note.duration || 0.25, maxNoteDur);
              const durationSeconds = noteDurBeats * (60 / bpm);

              if (!synth) return;
              synth.triggerAttackRelease(noteName, durationSeconds, absoluteStartTime, noteVelocity);
              totalMidiNotes++;
            } catch (error) {
              console.error(`Error scheduling note ${noteIndex} (loop ${loopIter}):`, error);
            }
          });
        }
      });
    });

    console.log(`🎵 Scheduled ${totalMidiNotes} MIDI notes and ${audioClips.length} audio clips for playback`);

    setCurrentTime(startBeat);
    currentTimeRef.current = startBeat;
    setIsPlaying(true);
    setIsPaused(false);

    // Keep Tone.Transport moving for metronome/compatibility
    Tone.Transport.position = `${Math.floor(startBeat / beatsPerBar)}:${Math.floor(startBeat % beatsPerBar)}:${Math.floor((startBeat % 1) * 4)}`;
    if (Tone.Transport.state !== 'started') {
      Tone.Transport.start();
    }
  }, [bpm, beatsPerBar, t]);

  startPlaybackAtRef.current = startPlaybackAt;

  // Playback control with Web Audio API
  const handlePlay = async () => {
    if (!audioEngineReady) {
      console.error('❌ Audio engine not ready');
      alert(t.audioEngineNotReady);
      return;
    }

    // Resume audio context if needed
    await webAudioEngineRef.current.initialize();
    await Tone.start(); // For backward compatibility

    if (isPlaying) {
      console.log('⏸️ Stopping playback');
      // Stop Web Audio Engine playback
      webAudioEngineRef.current.stopGlobalPlayback();

      // Also stop Tone.js for backward compatibility
      Tone.Transport.stop();
      Tone.Transport.cancel();
      audioPlayerManagerRef.current.stopGlobalPlayback();

      // Sync the final time from ref to state
      setCurrentTime(currentTimeRef.current);
      setIsPlaying(false);
      return;
    }

    // Always start playback from the stored playhead position,
    // regardless of whether the playhead is currently visible in the scroll window.
    startPlaybackAt(currentTimeRef.current, { resetTransport: true });
  };

  // Start playback using the same logic as handlePlay, but for recording context.
  // Excludes tracks that are currently being monitored/recorded.
  const startPlaybackForRecording = () => {
    try {
      // Clear any prior schedule
      webAudioEngineRef.current.stopGlobalPlayback();

      // Collect all clips from non-muted, non-monitored tracks
      const monitoringIds = new Set(Array.from(monitoringTracks));
      const allClips = [];
      tracks.forEach(track => {
        if (track.mute || monitoringIds.has(track.id)) return;
        track.clips.forEach(clip => {
          allClips.push({ ...clip, trackId: track.id, trackType: track.type, trackVolume: track.volume, trackPan: track.pan });
        });
      });

      // Audio clips via WebAudioEngine
      const audioClips = allClips.filter(clip => clip.type === 'audio' && clip.audioBuffer);
      // Always start the WebAudioEngine transport clock so the playhead advances during recording,
      // even if there are no audio clips (e.g. MIDI-only sessions).
      webAudioEngineRef.current.startGlobalPlayback(audioClips, currentTime, tracks, bpm);

      // MIDI via WebAudioEngine synth scheduling (copy of handlePlay but skip monitored tracks)
      const midiTracks = tracks.filter(track => track.type === 'midi' && !track.mute && !monitoringIds.has(track.id));
      midiTracks.forEach(track => {
        if (track.clips.length === 0) return;

        const isDrumsTrack = track.instrument === 'drums' || (track.name || '').toLowerCase().includes('drum');

        // Ensure synth exists in Web Audio Engine (non-drum tracks)
        let synth = null;
        if (!isDrumsTrack) {
          synth = webAudioEngineRef.current.getSynth(track.id);
          if (!synth) {
            synth = webAudioEngineRef.current.createSynth(track.id, track.instrument, track.soundfontInstrument);
          }
          if (!synth) return;
        }

        track.clips.forEach((clip) => {
          if (!clip.notes || clip.notes.length === 0) return;
          const contentOffset = Number(clip.contentOffset) || 0;
          const clipDurationBeats = Number(clip.duration) || Infinity;
          clip.notes.forEach((note) => {
            const noteRelStart = (Number(note.start) || 0) - contentOffset;
            if (noteRelStart + (note.duration || 0) <= 0) return;
            if (noteRelStart >= clipDurationBeats) return;
            const noteStartTime = clip.start + noteRelStart - currentTime;
            if (noteStartTime >= 0) {
              try {
                const noteVelocity = (note.velocity ?? 1.0) * track.volume;
                const startTimeSeconds = webAudioEngineRef.current.beatsToSeconds(noteStartTime, bpm);
                const absoluteStartTime = webAudioEngineRef.current.context.currentTime + startTimeSeconds;

                if (isDrumsTrack && webAudioEngineRef.current?.playDrumSampleByName) {
                  const drumName = instrumentConfigs?.drums?.drumMap?.[note.pitch]?.name;
                  if (drumName) {
                    webAudioEngineRef.current.playDrumSampleByName(drumName, track.id, Math.min(1, noteVelocity), absoluteStartTime);
                    return;
                  }
                }

                const noteName = Tone.Frequency(note.pitch, "midi").toNote();
                const maxNoteDur = clipDurationBeats - noteRelStart;
                const noteDurBeats = Math.min(note.duration || 0.25, maxNoteDur);
                const durationSeconds = noteDurBeats * (60 / bpm);
                if (!synth) return;
                synth.triggerAttackRelease(noteName, durationSeconds, absoluteStartTime, noteVelocity);
              } catch (error) {
                console.error('Error scheduling MIDI note during recording:', error);
              }
            }
          });
        });
      });

      // Keep playhead moving
      Tone.Transport.position = `${Math.floor(currentTime / 4)}:${Math.floor(currentTime % 4)}:${Math.floor((currentTime % 1) * 4)}`;
      if (Tone.Transport.state !== 'started') {
        Tone.Transport.start();
      }

      setIsRecordingPlayback(true);
      console.log('✅ Recording accompaniment playback started');
    } catch (err) {
      console.warn('Failed starting playback for recording:', err);
      setIsRecordingPlayback(false);
    }
  };

  const stopPlaybackForRecording = () => {
    try {
      webAudioEngineRef.current.stopGlobalPlayback();
    } catch (e) {
      console.warn('Error stopping recording playback:', e);
    }
    setIsRecordingPlayback(false);
  };

  const handleStop = () => {
    console.log('⏹️ Stopping all playback');

    // If we're recording, stop the recording too
    if (isRecordingRef.current) {
      handleRecord();
    }

    // Stop Web Audio Engine playback
    webAudioEngineRef.current.stopGlobalPlayback();

    // Legacy code for compatibility (will remove later)
    Tone.Transport.stop();
    Tone.Transport.cancel();
    Tone.Transport.position = "0:0:0";
    audioPlayerManagerRef.current.stopGlobalPlayback();

    // Update state
    setIsPlaying(false);
    setIsPaused(false);
    setCurrentTime(0);

    console.log('✅ All playback stopped and reset to beginning');
  };

  // New recording functionality - records from all monitoring tracks
  const handleRecord = async () => {
    if (!audioEngineReady) {
      alert(t.audioEngineNotReady);
      return;
    }

    try {
      if (!isRecordingRef.current) {
        // START RECORDING
        console.log('🎙️ Starting recording for all monitoring tracks...');

        // Ensure audio contexts are running so the playhead clock advances during recording
        // (especially for MIDI-only recording when the user hasn't pressed Play yet).
        await webAudioEngineRef.current.initialize();
        try {
          await Tone.start(); // Transport/metronome compatibility
        } catch (e) {
          console.warn('Tone.start() failed during record start:', e);
        }
        Tone.Transport.bpm.value = bpm;

        // Find all armed tracks: audio uses monitoring (mic), MIDI uses key events
        const monitoringTrackIds = Array.from(monitoringTracks);
        const armedMidiTrackIds = tracks.filter(t => t.type === 'midi' && t.armed).map(t => t.id);
        console.log(`🎙️ Found ${monitoringTrackIds.length} monitoring tracks:`, monitoringTrackIds);
        console.log(`🎹 Found ${armedMidiTrackIds.length} armed MIDI tracks:`, armedMidiTrackIds);

        if (monitoringTrackIds.length === 0 && armedMidiTrackIds.length === 0) {
          alert('No tracks are armed. Click the R button on the track you want to record.');
          return;
        }

        // Start recording on all monitoring (audio input) tracks
        let audioRecordingStarted = false;
        for (const trackId of monitoringTrackIds) {
          const manager = audioInputManagersRef.current.get(trackId);
          if (manager) {
            const success = manager.startRecording();
            if (success) {
              audioRecordingStarted = true;
              console.log(`🎙️ Recording started for track: ${trackId}`);
            } else {
              console.warn(`🎙️ Failed to start recording for track: ${trackId}`);
            }
          }
        }

        if (monitoringTrackIds.length > 0 && !audioRecordingStarted) {
          alert('Failed to start recording on any monitored tracks.');
          return;
        }

        // While recording, also play other tracks just like pressing Play (excluding monitored tracks)
        if (!isPlaying && !isRecordingPlayback) {
          startPlaybackForRecording();
        }

        // Store the recording start time
        setRecordingStartTime(currentTime);
        isRecordingRef.current = true;
        setIsRecording(true);
        console.log(`🎙️ Recording started on all monitoring tracks at time: ${currentTime.toFixed(2)} beats`);

        // Prepare MIDI recording target for the selected (armed) MIDI track so key presses write notes into a clip
        recordedMidiThisTakeRef.current = false;
        const selected = tracks.find(t => t.id === selectedTrack);

        if (selected && selected.type === 'midi' && selected.armed) {
          const existingClip = selected.clips?.find(c => c.id === selectedClip && c.type === 'midi');
          let clipId = existingClip?.id || null;
          let clipStart = typeof existingClip?.start === 'number' ? existingClip.start : null;

          if (!clipId) {
            const gridInterval = getGridStepBeatsFromGridSize(gridSize);
            const startTime = snapToGrid ? Math.floor(currentTime / gridInterval) * gridInterval : currentTime;
            const snappedClipStart = Math.round(startTime * 4) / 4;
            clipStart = snappedClipStart;
            clipId = createMidiClip(selected.id, snappedClipStart);
          }

          if (clipId && typeof clipStart === 'number') {
            midiRecordingContextRef.current = { trackId: selected.id, clipId, clipStart };
          } else {
            midiRecordingContextRef.current = null;
          }
        } else {
          midiRecordingContextRef.current = null;
        }

        // Start Transport to move the playhead during recording
        if (!isPlaying) {
          console.log('🎙️ Starting Transport for recording playhead movement');
          Tone.Transport.start();
        }

      } else {
        // STOP RECORDING
        console.log('🎙️ Stopping recording for all monitoring tracks...');

        const monitoringTrackIds = Array.from(monitoringTracks);
        const recordingPromises = [];

        // Stop recording on all monitoring tracks
        for (const trackId of monitoringTrackIds) {
          const manager = audioInputManagersRef.current.get(trackId);
          if (manager && manager.isRecording) {
            console.log(`🎙️ Stopping recording for track: ${trackId}`);
            recordingPromises.push(
              manager.stopRecording().then(audioBuffer => ({
                trackId,
                audioBuffer
              }))
            );
          }
        }

        isRecordingRef.current = false;
        setIsRecording(false);
        midiRecordingContextRef.current = null;

        // Stop Transport if we're not playing back content
        if (!isPlaying) {
          console.log('🎙️ Stopping Transport after recording');
          Tone.Transport.stop();
          // Reset Transport position to where recording started
          Tone.Transport.position = 0;
        }

        // Stop the separate recording playback if it was started
        if (isRecordingPlayback) {
          stopPlaybackForRecording();
        }

        // Wait for all recordings to complete
        const recordings = await Promise.all(recordingPromises);
        console.log(`🎙️ Completed ${recordings.length} recordings`);

        // Create clips for each successful recording
        recordings.forEach(({ trackId, audioBuffer }) => {
          if (audioBuffer) {
            const track = tracks.find(t => t.id === trackId);
            const trackName = track ? track.name : `Track ${trackId}`;

            // Calculate duration in beats from recorded buffer
            const durationInBeats = (audioBuffer.duration * bpm) / 60;
            // Precompute a lightweight waveform for rendering
            const waveform = analyzeAudioBuffer(audioBuffer);

            const recDuration = Math.max(0.25, durationInBeats);
            const newClip = {
              id: `${trackId}-${Date.now()}`,
              name: `${trackName} Recording ${new Date().toLocaleTimeString()}`,
              type: 'audio',
              start: recordingStartTime,
              audioBuffer: audioBuffer,
              duration: recDuration,
              sourceDuration: recDuration,
              loop: false,
              audioLength: audioBuffer.duration,
              waveform,
              trackId: trackId,
              color: '#e74c3c'
            };

            addClipToTrack(trackId, newClip);
            console.log(`🎙️ Created clip for track ${trackId}: ${newClip.name}`);
          } else {
            console.warn(`🎙️ No audio recorded for track: ${trackId}`);
          }
        });

        if (recordedMidiThisTakeRef.current) {
          saveToHistory();
        }
        recordedMidiThisTakeRef.current = false;

        console.log('🎙️ Recording completed and clips created');
      }
    } catch (error) {
      console.error('🎙️ Recording error:', error);
      isRecordingRef.current = false;
      setIsRecording(false);
      alert(t.recordingFailed + ': ' + error.message);
    }
  };

  const getGridInterval = useCallback(() => {
    return getGridStepBeatsFromGridSize(Number(gridSizeRef.current) || 16);
  }, []);

  const snapBeatToGrid = useCallback((beats) => {
    const safeBeats = Number.isFinite(beats) ? beats : 0;
    if (!snapToGridRef.current) return Math.max(0, safeBeats);
    return Math.max(0, snapBeatToGridStep(safeBeats, getGridInterval()));
  }, [getGridInterval]);

  const addLyricAtBeat = useCallback((beat) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const b = Math.max(0, Number(beat) || 0);
    setProjectLyrics(prev =>
      [...prev, { id, startBeat: b, text: '' }].sort((a, b) => a.startBeat - b.startBeat)
    );
    setTimeout(() => saveToHistory(), 0);
  }, [saveToHistory]);

  const updateLyricText = useCallback((id, text) => {
    setProjectLyrics(prev => prev.map(l => (l.id === id ? { ...l, text } : l)));
  }, []);

  const deleteLyric = useCallback((id) => {
    setProjectLyrics(prev => prev.filter(l => l.id !== id));
    setTimeout(() => saveToHistory(), 0);
  }, [saveToHistory]);

  const moveLyricToBeat = useCallback((id, beat) => {
    const b = Math.max(0, Number(beat) || 0);
    setProjectLyrics((prev) => {
      const cur = prev.find((l) => l.id === id);
      if (cur && Math.abs((Number(cur.startBeat) || 0) - b) < 1e-9) return prev;
      return prev
        .map((l) => (l.id === id ? { ...l, startBeat: b } : l))
        .sort((a, c) => a.startBeat - c.startBeat);
    });
    setTimeout(() => saveToHistory(), 0);
  }, [saveToHistory]);

  const getBeatFromRulerClientX = useCallback((clientX, rulerEl) => {
    if (!rulerEl) return 0;
    const rect = rulerEl.getBoundingClientRect();
    const relativeX = clientX - rect.left;
    const beats = Math.max(0, relativeX / pixelsPerBeat);
    return snapBeatToGrid(beats);
  }, [pixelsPerBeat, snapBeatToGrid]);

  const handleTimeRulerMouseDown = (e) => {
    if (e.button !== 0) return;
    if (isDraggingPlayheadRef.current) return;
    e.preventDefault();

    const rulerEl = e.currentTarget;
    const anchorBeat = getBeatFromRulerClientX(e.clientX, rulerEl);
    loopSelectAnchorBeatRef.current = anchorBeat;
    loopSelectDownXRef.current = e.clientX;
    suppressRulerClickRef.current = false;

    const handleMouseMove = (ev) => {
      const dx = Math.abs(ev.clientX - loopSelectDownXRef.current);
      if (dx < 4) return;

      setIsSelectingLoop(true);
      suppressRulerClickRef.current = true;

      const currentBeat = getBeatFromRulerClientX(ev.clientX, rulerEl);
      const start = Math.min(loopSelectAnchorBeatRef.current ?? 0, currentBeat);
      const end = Math.max(loopSelectAnchorBeatRef.current ?? 0, currentBeat);
      setLoopStart(start);
      setLoopEnd(end);
    };

    const handleMouseUp = (ev) => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);

      setIsSelectingLoop(false);

      const dx = Math.abs(ev.clientX - loopSelectDownXRef.current);
      if (dx < 4) {
        suppressRulerClickRef.current = false;
        return;
      }

      suppressRulerClickRef.current = true;

      const endBeat = getBeatFromRulerClientX(ev.clientX, rulerEl);
      const start = Math.min(loopSelectAnchorBeatRef.current ?? 0, endBeat);
      const end = Math.max(loopSelectAnchorBeatRef.current ?? 0, endBeat);

      const minLen = snapToGridRef.current ? getGridInterval() : 0.001;
      if (end - start < minLen) {
        setLoopStart(null);
        setLoopEnd(null);
        return;
      }

      setLoopStart(start);
      setLoopEnd(end);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // 时间轴点击播放
  const handleTimelineClick = (e) => {
    if (isDraggingPlayhead) return;

    // Use the same beat calculation as getBeatFromRulerClientX:
    // The ruler is position:sticky so rect.left is constant on screen.
    // clientX - rect.left gives the visual pixel offset, which already maps
    // directly to the absolute beat position (same as getBeatFromRulerClientX).
    const rulerEl = e.currentTarget;
    const rect = rulerEl.getBoundingClientRect();
    const relativeX = e.clientX - rect.left;
    const clickTime = Math.max(0, relativeX / pixelsPerBeat);

    const snappedTime = snapBeatToGrid(clickTime);

    // If a loop region is active and the click lands outside it, clear the selection
    if (loopRegion && (snappedTime < loopRegion.start || snappedTime > loopRegion.end)) {
      setLoopStart(null);
      setLoopEnd(null);
    }

    // Update both state and ref
    currentTimeRef.current = snappedTime;
    setCurrentTime(snappedTime);

    // Click is always on the visible ruler, so the new position is always in view — show it
    syncPlayheadDOM(snappedTime);

    if (isPlaying) {
      startPlaybackAtRef.current?.(snappedTime, { resetTransport: false });
    }
  };

  const handleTimeRulerClick = (e) => {
    if (suppressRulerClickRef.current) {
      suppressRulerClickRef.current = false;
      return;
    }
    handleTimelineClick(e);
  };

  // 播放头拖拽处理
  const handlePlayheadMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingPlayhead(true);

    const wasPlaying = isPlaying;
    if (isPlaying) {
      console.log('Pausing for playhead drag');
      Tone.Transport.stop();
      Tone.Transport.cancel();
      setIsPlaying(false);
      setIsPaused(true);
    }

    const handleMouseMove = (e) => {
      if (!timelineRef.current) return;

      const timelineRect = timelineRef.current.getBoundingClientRect();
      // Account for scroll position
      const scrollLeft = timelineRef.current.scrollLeft || 0;
      const relativeX = Math.max(0, e.clientX - timelineRect.left + scrollLeft);
      const newTime = relativeX / pixelsPerBeat; // 每N px = 1拍

      let snappedTime = newTime;
      if (snapToGrid) {
        const gridInterval = getGridStepBeatsFromGridSize(gridSize);
        snappedTime = Math.round(newTime / gridInterval) * gridInterval;
      }

      console.log(`Dragging playhead to: ${snappedTime.toFixed(2)} beats`);

      // Update both state and ref
      currentTimeRef.current = snappedTime;
      setCurrentTime(snappedTime);
      syncPlayheadDOM(snappedTime);
    };

    const handleMouseUp = () => {
      setIsDraggingPlayhead(false);

      if (wasPlaying) {
        console.log('Resuming playback from dragged position');
        setTimeout(() => {
          handlePlay();
        }, 50);
      }

      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Update track parameters
  const updateTrack = (trackId, updates, addToHistory = true) => {
    console.log(`APP: Updating track ${trackId} with:`, updates);

    setTracks(prevTracks => {
      const newTracks = prevTracks.map(track => {
        if (track.id === trackId) {
          const updatedTrack = { ...track, ...updates };

          // Update track parameters in Web Audio Engine
          if (audioEngineReady && (updates.volume !== undefined || updates.pan !== undefined)) {
            console.log(`APP: Sending track ${trackId} updates to WebAudioEngine:`, {
              volume: updates.volume !== undefined ? updates.volume : track.volume,
              pan: updates.pan !== undefined ? updates.pan : track.pan
            });

            webAudioEngineRef.current.updateTrackParams(trackId, {
              volume: updates.volume !== undefined ? updates.volume : track.volume,
              pan: updates.pan !== undefined ? updates.pan : track.pan
            });

            // Also update effects if they exist
            if (track.effects) {
              webAudioEngineRef.current.updateTrackEffects(trackId, track.effects);
            }

            // Comment out the test tone - we don't need it now that we're debugging
            /*
            if (updates.pan !== undefined) {
              console.log(`APP: Creating test tone with pan ${updates.pan} for verification`);
              const testTone = webAudioEngineRef.current.createTestTone(updates.pan);
              setTimeout(() => {
                if (testTone) testTone.stop();
              }, 500);
            }
            */
          }

          if (audioEngineReady && updates.effects !== undefined) {
            webAudioEngineRef.current.updateTrackEffects(trackId, updatedTrack.effects);
          }

          // We're now using WebAudioEngine exclusively, no need for legacy audio updates
          // Keeping this comment as a marker that we've removed the legacy code

          return updatedTrack;
        }
        return track;
      });

      return newTracks;
    });

    if (addToHistory) {
      setTimeout(() => saveToHistory(), 0);
    }
  };

  const vocalPresetTitleKeys = {
    clean_vocal: 'vocalPresetClean',
    pop_vocal: 'vocalPresetPop',
    rap_vocal: 'vocalPresetRap',
    warm_vocal: 'vocalPresetWarm',
    radio_voice: 'vocalPresetRadio',
  };

  const handleApplyVocalPreset = (preset) => {
    if (!selectedTrack || !preset) return;
    const track = tracks.find(tr => tr.id === selectedTrack);
    if (!track) return;
    const { effects, vocalPresetId } = loadVocalPreset(track, preset);
    updateTrack(selectedTrack, { effects, vocalPresetId }, true);
  };

  // Track reordering handlers
  const handleTrackReorderStart = (e, trackId) => {
    e.stopPropagation();
    setDraggingTrackId(trackId);
    e.dataTransfer.effectAllowed = 'move';
    // Add a transparent drag image
    const dragImage = document.createElement('div');
    dragImage.style.opacity = '0';
    document.body.appendChild(dragImage);
    e.dataTransfer.setDragImage(dragImage, 0, 0);
    setTimeout(() => document.body.removeChild(dragImage), 0);
  };

  const handleTrackReorderOver = (e, targetTrackId) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggingTrackId && draggingTrackId !== targetTrackId) {
      setDragOverTrackId(targetTrackId);
    }
  };

  const handleTrackReorderDrop = (e, targetTrackId) => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggingTrackId || draggingTrackId === targetTrackId) {
      setDraggingTrackId(null);
      setDragOverTrackId(null);
      return;
    }

    // Reorder tracks
    setTracks(prevTracks => {
      const newTracks = [...prevTracks];
      const dragIndex = newTracks.findIndex(t => t.id === draggingTrackId);
      const dropIndex = newTracks.findIndex(t => t.id === targetTrackId);

      if (dragIndex === -1 || dropIndex === -1) return prevTracks;

      const [draggedTrack] = newTracks.splice(dragIndex, 1);
      newTracks.splice(dropIndex, 0, draggedTrack);

      return newTracks;
    });

    setDraggingTrackId(null);
    setDragOverTrackId(null);
    saveToHistory();
  };

  const handleTrackReorderEnd = () => {
    setDraggingTrackId(null);
    setDragOverTrackId(null);
  };

  // Add track with proper effects initialization
  const addTrack = (type = 'midi', instrument = null, customName = null) => {
    console.log(`➕ Adding new ${type} track with instrument: ${instrument}`);

    const newTrackId = Math.max(...tracks.map(t => t.id), 0) + 1;
    const defaultInstrument = type === 'midi' ? (instrument || 'piano') : null;

    const newTrack = {
      id: newTrackId,
      name: customName || (type === 'midi' ?
        `${t.tracks} ${tracks.filter(t => t.type === 'midi').length + 1}` :
        `${t.tracks} ${tracks.filter(t => t.type === 'audio').length + 1}`),
      type: type, volume: 0.8, pan: 0, mute: false, solo: false, armed: false,
      color: ['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6'][tracks.length % 5],
      instrument: defaultInstrument, clips: [],
      effects: {
        reverb: 0.2, delay: 0, chorus: 0,
        eq: { low: 0, mid: 0, high: 0 },
        compressor: { threshold: -20, ratio: 4, attack: 0.003, release: 0.25 },
        // Add reverb parameters
        reverbParams: {
          decayTime: 1.5,
          preDelay: 0.03
        },
        // Add delay parameters
        delayParams: {
          delayTime: 0.25,
          feedback: 0.3
        },
        chorusParams: {
          rate: 0.5,
          depth: 0.5
        },
        compressor: {
          threshold: -24,
          ratio: 4,
          attack: 0.003,
          release: 0.25,
          knee: 30,
          makeupGain: 0,
          outputGain: 0
        },
        distortion: {
          preGain: 0,
          drive: 0,
          tone: 5000,
          mode: 'clean'
        }
      },
      automation: { volume: [], pan: [] }
    };

    // 先更新状态，再创建合成器
    setTracks([...tracks, newTrack]);
    setSelectedTrack(newTrackId);

    // 立即创建合成器（如果是MIDI轨道且音频引擎就绪）
    if (type === 'midi' && audioEngineReady) {
      console.log(`🎹 Creating synth for new track ${newTrackId}`);
      setTimeout(() => {
        const synth = synthManagerRef.current.createSynth(newTrackId, defaultInstrument);
        if (synth) {
          console.log(`✅ Synth created successfully for track ${newTrackId}`);
          // 播放一个测试音符确认工作正常
          try {
            synth.triggerAttackRelease('C4', '8n', undefined, 0.5);
            console.log('🎵 Test note played successfully');
          } catch (error) {
            console.error('❌ Test note failed:', error);
          }
        } else {
          console.error(`❌ Failed to create synth for track ${newTrackId}`);
        }
      }, 100);
    }

    saveToHistory();
  };

  // Handle track selection from modal
  const handleTrackSelection = async (type, instrument, categoryName, file = null) => {
    if (file) {
      // Handle file import
      try {
        await handleAudioFileProcessing(file, null, currentTime);
      } catch (error) {
        console.error('Failed to import audio file:', error);
        alert(`${t.fileProcessingFailed}: ${error.message}`);
      }
    } else {
      // Handle regular track creation
      addTrack(type, instrument, categoryName);

      // Automatically show piano roll for MIDI tracks
      if (type === 'midi') {
        setTimeout(() => {
          const newTrackId = Math.max(...tracks.map(t => t.id), 0) + 1;
          // Create a new MIDI clip for the track
          const newClipId = createMidiClip(newTrackId, currentTime);
          setSelectedClip(newClipId);
          setShowPianoRoll(true);
        }, 150); // Small delay to ensure track is created
      }
    }
  };

  // 删除片段
  const deleteClip = (clipId) => {
    // 如果是音频片段，清理播放器
    audioPlayerManagerRef.current.disposePlayer(clipId);

    setTracks(prev => prev.map(track => ({
      ...track,
      clips: track.clips.filter(clip => clip.id !== clipId)
    })));

    setSelectedClip(null);
    saveToHistory();
  };

  // 移动片段到另一个轨道
  const moveClipToTrack = (clipId, targetTrackId) => {
    let clipToMove = null;
    let sourceTrackId = null;

    tracks.forEach(track => {
      const clip = track.clips.find(c => c.id === clipId);
      if (clip) {
        clipToMove = clip;
        sourceTrackId = track.id;
      }
    });

    if (!clipToMove || sourceTrackId === targetTrackId) return;

    const sourceTrack = tracks.find(t => t.id === sourceTrackId);
    const targetTrack = tracks.find(t => t.id === targetTrackId);

    if (!sourceTrack || !targetTrack) return;

    if (sourceTrack.type !== targetTrack.type) {
      alert(language === 'zh' ? '只能在相同类型的轨道间移动片段' : 'Can only move clips between tracks of the same type');
      return;
    }

    setTracks(prev => prev.map(track => {
      if (track.id === sourceTrackId) {
        return {
          ...track,
          clips: track.clips.filter(c => c.id !== clipId)
        };
      } else if (track.id === targetTrackId) {
        return {
          ...track,
          clips: [...track.clips, clipToMove]
        };
      }
      return track;
    }));

    if (clipToMove.type === 'audio') {
      const playerData = audioPlayerManagerRef.current.players.get(clipId);
      if (playerData) {
        playerData.gainNode.gain.rampTo(targetTrack.volume * (clipToMove.gain || 1), 0.1);
        playerData.panNode.pan.rampTo(targetTrack.pan, 0.1);
      }
    }

    saveToHistory();
  };

  // 片段拖拽处理
  const handleClipMouseDown = (e, clip, trackId) => {
    if (e.button !== 0) return; // ignore right-click — handled by onContextMenu
    e.preventDefault();
    e.stopPropagation();

    setSelectedClip(clip.id);
    setDraggingClip(clip.id);

    // Automatically show piano roll for MIDI clips
    const track = tracks.find(t => t.id === trackId);
    if (track && track.type === 'midi') {
      setSelectedTrack(trackId);
      setShowPianoRoll(true);
    }

    const startX = e.clientX;
    const startY = e.clientY;
    const initialLeft = clip.start * pixelsPerBeat;

    const handleMouseMove = (e) => {
      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;

      setDragOffset({ x: deltaX, y: deltaY });
    };

    const handleMouseUp = (e) => {
      if (Math.abs(e.clientX - startX) > 5 || Math.abs(e.clientY - startY) > 5) {
        const deltaX = e.clientX - startX;
        const deltaY = e.clientY - startY;
        let newTime = Math.max(0, (initialLeft + deltaX) / pixelsPerBeat);

        if (snapToGrid) {
          const gridInterval = getGridStepBeatsFromGridSize(gridSize);
          newTime = Math.round(newTime / gridInterval) * gridInterval;
        }

        const trackHeight = showAutomation ? 200 : 100;
        const currentTrackIndex = tracks.findIndex(t => t.id === trackId);
        const targetTrackIndex = currentTrackIndex + Math.round(deltaY / trackHeight);

        if (targetTrackIndex >= 0 && targetTrackIndex < tracks.length) {
          const targetTrack = tracks[targetTrackIndex];

          if (targetTrack.id !== trackId) {
            moveClipToTrack(clip.id, targetTrack.id);
          }
        }

        setTracks(prev => prev.map(track => ({
          ...track,
          clips: track.clips.map(c =>
            c.id === clip.id ? { ...c, start: newTime } : c
          )
        })));

        saveToHistory();
      }

      setDraggingClip(null);
      setDragOffset({ x: 0, y: 0 });
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Clip trim — non-destructive, Logic Pro semantics:
  //
  // AUDIO: trimStart/trimEnd are seconds into the original audioBuffer.
  //   • Left edge drag  → trimStart moves, clip.start moves, right timeline edge stays fixed.
  //   • Right edge drag → trimEnd moves, clip.duration changes, left edge stays fixed.
  //   • Cannot extend trimEnd past fullBufferDuration (original file boundary).
  //   • Cannot extend trimStart below 0.
  //
  // MIDI: contentOffset is beats into the note array where the visible window starts.
  //   • Left edge drag  → contentOffset moves, clip.start moves, right edge stays fixed.
  //                        Cannot pull left past contentOffset === 0 (reveals all notes).
  //   • Right edge drag → only clip.duration changes, no hard upper limit
  //                        (empty space beyond last note is valid).
  //   • Notes outside the window are masked but never deleted.
  const handleClipTrimMouseDown = (e, clip, trackId, edge) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedClip(clip.id);

    const startX = e.clientX;
    const isMidi = clip.type === 'midi';

    // ── Snapshot initial values ────────────────────────────────────────────
    const initialClipStart = clip.start;
    const initialDuration = clip.duration;
    // The timeline position of the right edge never changes during a left-edge drag
    const initialRightEdge = initialClipStart + initialDuration;

    // Audio bounds
    const fullBufferDuration = clip.audioLength ||
      (clip.audioBuffer ? clip.audioBuffer.duration : initialDuration * 60 / bpm);
    const initialTrimStart = typeof clip.trimStart === 'number' ? clip.trimStart : 0;
    const initialTrimEnd = typeof clip.trimEnd === 'number' ? clip.trimEnd : fullBufferDuration;

    // MIDI bounds
    const initialContentOffset = typeof clip.contentOffset === 'number' ? clip.contentOffset : 0;

    const gridInterval = snapToGrid ? getGridStepBeatsFromGridSize(gridSize) : 0;
    const minDuration = gridInterval || (1 / 16);

    const handleMouseMove = (moveE) => {
      const deltaX = moveE.clientX - startX;
      const deltaBeats = deltaX / pixelsPerBeat;

      setTracks(prev => prev.map(track => ({
        ...track,
        clips: track.clips.map(c => {
          if (c.id !== clip.id) return c;

          // ── LEFT EDGE ─────────────────────────────────────────────────────
          if (edge === 'left') {
            let newClipStart = initialClipStart + deltaBeats;

            // Snap the new left edge position
            if (snapToGrid && gridInterval > 0) {
              newClipStart = Math.round(newClipStart / gridInterval) * gridInterval;
            }

            // Clamp: can't go before timeline start
            newClipStart = Math.max(0, newClipStart);

            // Clamp: can't pull left for MIDI past contentOffset === 0
            // (that would reveal content before the beginning of the note array)
            if (isMidi) {
              const maxLeftShift = initialContentOffset; // beats we can pull left
              newClipStart = Math.max(initialClipStart - maxLeftShift, newClipStart);
            } else {
              // Audio: can't pull left further than trimStart === 0
              const maxAudioLeftShift = initialTrimStart * bpm / 60; // beats
              newClipStart = Math.max(initialClipStart - maxAudioLeftShift, newClipStart);
            }

            // Right edge stays pinned
            const newDuration = Math.max(minDuration, initialRightEdge - newClipStart);
            // Re-clamp newClipStart in case minDuration pushed it
            const clampedStart = initialRightEdge - newDuration;

            if (isMidi) {
              const offsetDelta = clampedStart - initialClipStart; // negative = reveal more
              const newContentOffset = Math.max(0, initialContentOffset + offsetDelta);
              return { ...c, start: clampedStart, duration: newDuration, contentOffset: newContentOffset };
            } else {
              // trimStart shifts by the same beat-delta converted to seconds
              const startDeltaBeats = clampedStart - initialClipStart;
              const newTrimStart = Math.max(0, initialTrimStart + startDeltaBeats * 60 / bpm);
              return { ...c, start: clampedStart, duration: newDuration, trimStart: newTrimStart };
            }

            // ── RIGHT EDGE ────────────────────────────────────────────────────
          } else {
            let newDuration = initialDuration + deltaBeats;

            // Snap the new right edge position
            if (snapToGrid && gridInterval > 0) {
              const snappedEnd = Math.round((initialClipStart + newDuration) / gridInterval) * gridInterval;
              newDuration = snappedEnd - initialClipStart;
            }

            newDuration = Math.max(minDuration, newDuration);

            if (isMidi) {
              // MIDI: no hard upper limit — empty space after last note is allowed.
              // Keep sourceDuration in sync unless it's already looping.
              return {
                ...c,
                duration: newDuration,
                sourceDuration: c.loop ? c.sourceDuration : newDuration,
              };
            } else {
              // Audio: cannot extend trimEnd past the original file boundary
              const newTrimEnd = initialTrimStart + newDuration * 60 / bpm;
              const clampedTrimEnd = Math.min(fullBufferDuration, newTrimEnd);
              const clampedDuration = (clampedTrimEnd - initialTrimStart) * bpm / 60;
              const finalDuration = Math.max(minDuration, clampedDuration);
              return {
                ...c,
                duration: finalDuration,
                sourceDuration: c.loop ? c.sourceDuration : finalDuration,
                trimEnd: clampedTrimEnd,
              };
            }
          }
        })
      })));
    };

    const handleMouseUp = () => {
      saveToHistory();
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Fade drag — drag the fade-in or fade-out triangle handle on an audio clip.
  // edge: 'in' (left) or 'out' (right)
  // Dragging right on 'in' increases fadeIn duration; left decreases it.
  // Dragging left on 'out' increases fadeOut duration; right decreases it.
  const handleFadeDragMouseDown = (e, clip, edge) => {
    if (toolMode !== 'fade') return;   // only active in fade tool mode
    e.preventDefault();
    e.stopPropagation();
    setSelectedClip(clip.id);

    const startX = e.clientX;
    const initialFadeIn = typeof clip.fadeIn === 'number' ? clip.fadeIn : 0;
    const initialFadeOut = typeof clip.fadeOut === 'number' ? clip.fadeOut : 0;

    // Maximum fade duration: half the clip's duration in seconds, so in + out ≤ clipDuration
    const clipDurationSec = clip.duration * 60 / bpm;

    const handleMouseMove = (moveE) => {
      const deltaX = moveE.clientX - startX;
      const deltaSec = (deltaX / pixelsPerBeat) * (60 / bpm);

      setTracks(prev => prev.map(track => ({
        ...track,
        clips: track.clips.map(c => {
          if (c.id !== clip.id) return c;

          if (edge === 'in') {
            const newFadeIn = Math.max(0, Math.min(
              initialFadeIn + deltaSec,
              clipDurationSec - (c.fadeOut || 0) - 0.01
            ));
            return { ...c, fadeIn: newFadeIn };
          } else {
            const newFadeOut = Math.max(0, Math.min(
              initialFadeOut - deltaSec,   // drag left = larger fade-out
              clipDurationSec - (c.fadeIn || 0) - 0.01
            ));
            return { ...c, fadeOut: newFadeOut };
          }
        })
      })));
    };

    const handleMouseUp = () => {
      saveToHistory();
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Loop drag — dragging the upper-right corner of a clip to extend it as a loop.
  // • Sets clip.loop = true, clip.sourceDuration = original content length
  // • Extends clip.duration (the total visual length) by dragging right
  // • clip.sourceDuration stays fixed — it is the repeating unit
  const handleClipLoopMouseDown = (e, clip, trackId) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedClip(clip.id);

    const startX = e.clientX;
    const initialDuration = clip.duration;
    // Lock in the source length the moment looping begins
    const sourceDur = clip.sourceDuration ?? clip.duration;

    const gridInterval = snapToGrid ? getGridStepBeatsFromGridSize(gridSize) : 0;

    const handleMouseMove = (moveE) => {
      const deltaBeats = (moveE.clientX - startX) / pixelsPerBeat;
      let newDuration = initialDuration + deltaBeats;

      if (snapToGrid && gridInterval > 0) {
        const snappedEnd = Math.round((clip.start + newDuration) / gridInterval) * gridInterval;
        newDuration = snappedEnd - clip.start;
      }

      // Minimum = one full source cycle
      newDuration = Math.max(sourceDur, newDuration);

      setTracks(prev => prev.map(track => ({
        ...track,
        clips: track.clips.map(c => {
          if (c.id !== clip.id) return c;
          return {
            ...c,
            duration: newDuration,
            sourceDuration: sourceDur,
            loop: true,
          };
        })
      })));
    };

    const handleMouseUp = () => {
      saveToHistory();
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Mixer handle drag handlers
  const handleMixerHandleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingMixerHandle(true);

    const startY = e.clientY;
    const startHeight = mixerHeight;

    const handleMouseMove = (e) => {
      const deltaY = startY - e.clientY; // Inverted because mixer grows upward
      const newHeight = Math.max(150, Math.min(600, startHeight + deltaY)); // Min 150px, max 600px
      setMixerHeight(newHeight);
    };

    const handleMouseUp = () => {
      setIsDraggingMixerHandle(false);
      setIsMixerHandleHovered(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Knob drag handler for reverb pedal
  const handleKnobDrag = (e, trackId, paramType, initialValue) => {
    e.preventDefault();
    e.stopPropagation();

    const startY = e.clientY;
    const startValue = initialValue;

    const handleMouseMove = (e) => {
      const deltaY = startY - e.clientY; // Inverted for intuitive control
      let sensitivity = 0.005; // Base sensitivity
      let newValue = startValue;

      switch (paramType) {
        case 'eqLow':
          sensitivity = 0.2;
          newValue = Math.max(-15, Math.min(15, startValue + deltaY * sensitivity));
          updateTrack(trackId, {
            effects: {
              ...tracks.find(t => t.id === trackId).effects,
              eq: {
                ...tracks.find(t => t.id === trackId).effects?.eq,
                low: newValue
              }
            }
          }, false);

          if (audioEngineReady && webAudioEngineRef.current) {
            try {
              webAudioEngineRef.current.updateTrackEffects(trackId, {
                ...tracks.find(t => t.id === trackId).effects,
                eq: {
                  ...tracks.find(t => t.id === trackId).effects?.eq,
                  low: newValue
                }
              });
            } catch (error) {
              console.error('Error updating EQ low:', error);
            }
          }
          break;

        case 'eqMid':
          sensitivity = 0.2;
          newValue = Math.max(-15, Math.min(15, startValue + deltaY * sensitivity));
          updateTrack(trackId, {
            effects: {
              ...tracks.find(t => t.id === trackId).effects,
              eq: {
                ...tracks.find(t => t.id === trackId).effects?.eq,
                mid: newValue
              }
            }
          }, false);

          if (audioEngineReady && webAudioEngineRef.current) {
            try {
              webAudioEngineRef.current.updateTrackEffects(trackId, {
                ...tracks.find(t => t.id === trackId).effects,
                eq: {
                  ...tracks.find(t => t.id === trackId).effects?.eq,
                  mid: newValue
                }
              });
            } catch (error) {
              console.error('Error updating EQ mid:', error);
            }
          }
          break;

        case 'eqHigh':
          sensitivity = 0.2;
          newValue = Math.max(-15, Math.min(15, startValue + deltaY * sensitivity));
          updateTrack(trackId, {
            effects: {
              ...tracks.find(t => t.id === trackId).effects,
              eq: {
                ...tracks.find(t => t.id === trackId).effects?.eq,
                high: newValue
              }
            }
          }, false);

          if (audioEngineReady && webAudioEngineRef.current) {
            try {
              webAudioEngineRef.current.updateTrackEffects(trackId, {
                ...tracks.find(t => t.id === trackId).effects,
                eq: {
                  ...tracks.find(t => t.id === trackId).effects?.eq,
                  high: newValue
                }
              });
            } catch (error) {
              console.error('Error updating EQ high:', error);
            }
          }
          break;

        case 'reverb':
          newValue = Math.max(0, Math.min(1, startValue + deltaY * sensitivity));
          updateTrack(trackId, {
            effects: {
              ...tracks.find(t => t.id === trackId).effects,
              reverb: newValue
            }
          }, false);

          if (audioEngineReady) {
            webAudioEngineRef.current.updateTrackEffects(trackId, {
              ...tracks.find(t => t.id === trackId).effects,
              reverb: newValue
            });
          }
          break;

        case 'decay':
          sensitivity = 0.02;
          newValue = Math.max(0.1, Math.min(10, startValue + deltaY * sensitivity));
          updateTrack(trackId, {
            effects: {
              ...tracks.find(t => t.id === trackId).effects,
              reverbParams: {
                ...tracks.find(t => t.id === trackId).effects?.reverbParams,
                decayTime: newValue
              }
            }
          }, false);

          if (audioEngineReady) {
            webAudioEngineRef.current.updateTrackEffects(trackId, {
              ...tracks.find(t => t.id === trackId).effects,
              reverbParams: {
                ...tracks.find(t => t.id === trackId).effects?.reverbParams,
                decayTime: newValue
              }
            });
          }
          break;

        case 'preDelay':
          sensitivity = 0.0005;
          newValue = Math.max(0, Math.min(0.2, startValue + deltaY * sensitivity));
          updateTrack(trackId, {
            effects: {
              ...tracks.find(t => t.id === trackId).effects,
              reverbParams: {
                ...tracks.find(t => t.id === trackId).effects?.reverbParams,
                preDelay: newValue
              }
            }
          }, false);

          if (audioEngineReady) {
            webAudioEngineRef.current.updateTrackEffects(trackId, {
              ...tracks.find(t => t.id === trackId).effects,
              reverbParams: {
                ...tracks.find(t => t.id === trackId).effects?.reverbParams,
                preDelay: newValue
              }
            });
          }
          break;

        case 'delay':
          newValue = Math.max(0, Math.min(1, startValue + deltaY * sensitivity));
          updateTrack(trackId, {
            effects: {
              ...tracks.find(t => t.id === trackId).effects,
              delay: newValue
            }
          }, false);

          if (audioEngineReady) {
            webAudioEngineRef.current.updateTrackEffects(trackId, {
              ...tracks.find(t => t.id === trackId).effects,
              delay: newValue
            });
          }
          break;

        case 'delayTime':
          sensitivity = 0.01;
          newValue = Math.max(0.01, Math.min(2, startValue + deltaY * sensitivity));
          updateTrack(trackId, {
            effects: {
              ...tracks.find(t => t.id === trackId).effects,
              delayParams: {
                ...tracks.find(t => t.id === trackId).effects?.delayParams,
                delayTime: newValue
              }
            }
          }, false);

          if (audioEngineReady) {
            webAudioEngineRef.current.updateTrackEffects(trackId, {
              ...tracks.find(t => t.id === trackId).effects,
              delayParams: {
                ...tracks.find(t => t.id === trackId).effects?.delayParams,
                delayTime: newValue
              }
            });
          }
          break;

        case 'delayFeedback':
          sensitivity = 0.005;
          newValue = Math.max(0, Math.min(0.95, startValue + deltaY * sensitivity));
          updateTrack(trackId, {
            effects: {
              ...tracks.find(t => t.id === trackId).effects,
              delayParams: {
                ...tracks.find(t => t.id === trackId).effects?.delayParams,
                feedback: newValue
              }
            }
          }, false);

          if (audioEngineReady) {
            webAudioEngineRef.current.updateTrackEffects(trackId, {
              ...tracks.find(t => t.id === trackId).effects,
              delayParams: {
                ...tracks.find(t => t.id === trackId).effects?.delayParams,
                feedback: newValue
              }
            });
          }
          break;

        case 'chorus':
          newValue = Math.max(0, Math.min(1, startValue + deltaY * sensitivity));
          updateTrack(trackId, {
            effects: {
              ...tracks.find(t => t.id === trackId).effects,
              chorus: newValue
            }
          }, false);

          if (audioEngineReady) {
            webAudioEngineRef.current.updateTrackEffects(trackId, {
              ...tracks.find(t => t.id === trackId).effects,
              chorus: newValue
            });
          }
          break;

        case 'chorusRate':
          sensitivity = 0.02;
          newValue = Math.max(0.1, Math.min(10, startValue + deltaY * sensitivity));
          updateTrack(trackId, {
            effects: {
              ...tracks.find(t => t.id === trackId).effects,
              chorusParams: {
                ...tracks.find(t => t.id === trackId).effects?.chorusParams,
                rate: newValue
              }
            }
          }, false);

          if (audioEngineReady) {
            webAudioEngineRef.current.updateTrackEffects(trackId, {
              ...tracks.find(t => t.id === trackId).effects,
              chorusParams: {
                ...tracks.find(t => t.id === trackId).effects?.chorusParams,
                rate: newValue
              }
            });
          }
          break;

        case 'chorusDepth':
          sensitivity = 0.005;
          newValue = Math.max(0, Math.min(1, startValue + deltaY * sensitivity));
          updateTrack(trackId, {
            effects: {
              ...tracks.find(t => t.id === trackId).effects,
              chorusParams: {
                ...tracks.find(t => t.id === trackId).effects?.chorusParams,
                depth: newValue
              }
            }
          }, false);

          if (audioEngineReady) {
            webAudioEngineRef.current.updateTrackEffects(trackId, {
              ...tracks.find(t => t.id === trackId).effects,
              chorusParams: {
                ...tracks.find(t => t.id === trackId).effects?.chorusParams,
                depth: newValue
              }
            });
          }
          break;

        case 'compressorThreshold':
          sensitivity = 0.5;
          newValue = Math.max(-60, Math.min(0, startValue + deltaY * sensitivity));
          updateTrack(trackId, {
            effects: {
              ...tracks.find(t => t.id === trackId).effects,
              compressor: {
                ...tracks.find(t => t.id === trackId).effects?.compressor,
                threshold: newValue
              }
            }
          }, false);

          if (audioEngineReady) {
            webAudioEngineRef.current.updateTrackEffects(trackId, {
              ...tracks.find(t => t.id === trackId).effects,
              compressor: {
                ...tracks.find(t => t.id === trackId).effects?.compressor,
                threshold: newValue
              }
            });
          }
          break;

        case 'compressorRatio':
          sensitivity = 0.1;
          newValue = Math.max(1, Math.min(20, startValue + deltaY * sensitivity));
          updateTrack(trackId, {
            effects: {
              ...tracks.find(t => t.id === trackId).effects,
              compressor: {
                ...tracks.find(t => t.id === trackId).effects?.compressor,
                ratio: newValue
              }
            }
          }, false);

          if (audioEngineReady) {
            webAudioEngineRef.current.updateTrackEffects(trackId, {
              ...tracks.find(t => t.id === trackId).effects,
              compressor: {
                ...tracks.find(t => t.id === trackId).effects?.compressor,
                ratio: newValue
              }
            });
          }
          break;

        case 'compressorAttack':
          sensitivity = 0.0005;
          newValue = Math.max(0.001, Math.min(0.1, startValue + deltaY * sensitivity));
          updateTrack(trackId, {
            effects: {
              ...tracks.find(t => t.id === trackId).effects,
              compressor: {
                ...tracks.find(t => t.id === trackId).effects?.compressor,
                attack: newValue
              }
            }
          }, false);

          if (audioEngineReady) {
            webAudioEngineRef.current.updateTrackEffects(trackId, {
              ...tracks.find(t => t.id === trackId).effects,
              compressor: {
                ...tracks.find(t => t.id === trackId).effects?.compressor,
                attack: newValue
              }
            });
          }
          break;

        case 'compressorRelease':
          sensitivity = 0.005;
          newValue = Math.max(0.01, Math.min(1, startValue + deltaY * sensitivity));
          updateTrack(trackId, {
            effects: {
              ...tracks.find(t => t.id === trackId).effects,
              compressor: {
                ...tracks.find(t => t.id === trackId).effects?.compressor,
                release: newValue
              }
            }
          }, false);

          if (audioEngineReady) {
            webAudioEngineRef.current.updateTrackEffects(trackId, {
              ...tracks.find(t => t.id === trackId).effects,
              compressor: {
                ...tracks.find(t => t.id === trackId).effects?.compressor,
                release: newValue
              }
            });
          }
          break;

        case 'compressorMakeupGain':
          sensitivity = 0.2;
          newValue = Math.max(-20, Math.min(20, startValue + deltaY * sensitivity));
          updateTrack(trackId, {
            effects: {
              ...tracks.find(t => t.id === trackId).effects,
              compressor: {
                ...tracks.find(t => t.id === trackId).effects?.compressor,
                makeupGain: newValue
              }
            }
          }, false);

          if (audioEngineReady) {
            webAudioEngineRef.current.updateTrackEffects(trackId, {
              ...tracks.find(t => t.id === trackId).effects,
              compressor: {
                ...tracks.find(t => t.id === trackId).effects?.compressor,
                makeupGain: newValue
              }
            });
          }
          break;

        case 'compressorOutputGain':
          sensitivity = 0.2;
          newValue = Math.max(-20, Math.min(20, startValue + deltaY * sensitivity));
          updateTrack(trackId, {
            effects: {
              ...tracks.find(t => t.id === trackId).effects,
              compressor: {
                ...tracks.find(t => t.id === trackId).effects?.compressor,
                outputGain: newValue
              }
            }
          }, false);

          if (audioEngineReady) {
            webAudioEngineRef.current.updateTrackEffects(trackId, {
              ...tracks.find(t => t.id === trackId).effects,
              compressor: {
                ...tracks.find(t => t.id === trackId).effects?.compressor,
                outputGain: newValue
              }
            });
          }
          break;

        case 'distortionPreGain':
          sensitivity = 0.2;
          newValue = Math.max(-20, Math.min(20, startValue + deltaY * sensitivity));
          updateTrack(trackId, {
            effects: {
              ...tracks.find(t => t.id === trackId).effects,
              distortion: {
                ...tracks.find(t => t.id === trackId).effects?.distortion,
                preGain: newValue
              }
            }
          }, false);

          if (audioEngineReady && webAudioEngineRef.current) {
            try {
              webAudioEngineRef.current.updateTrackEffects(trackId, {
                ...tracks.find(t => t.id === trackId).effects,
                distortion: {
                  ...tracks.find(t => t.id === trackId).effects?.distortion,
                  preGain: newValue
                }
              });
            } catch (error) {
              console.error('Error updating distortion pre-gain:', error);
            }
          }
          break;

        case 'distortionDrive':
          sensitivity = 0.2;
          newValue = Math.max(-20, Math.min(20, startValue + deltaY * sensitivity));
          updateTrack(trackId, {
            effects: {
              ...tracks.find(t => t.id === trackId).effects,
              distortion: {
                ...tracks.find(t => t.id === trackId).effects?.distortion,
                drive: newValue
              }
            }
          }, false);

          if (audioEngineReady && webAudioEngineRef.current) {
            try {
              webAudioEngineRef.current.updateTrackEffects(trackId, {
                ...tracks.find(t => t.id === trackId).effects,
                distortion: {
                  ...tracks.find(t => t.id === trackId).effects?.distortion,
                  drive: newValue
                }
              });
            } catch (error) {
              console.error('Error updating distortion drive:', error);
            }
          }
          break;

        case 'distortionTone':
          sensitivity = 50;
          newValue = Math.max(200, Math.min(8000, startValue + deltaY * sensitivity));
          updateTrack(trackId, {
            effects: {
              ...tracks.find(t => t.id === trackId).effects,
              distortion: {
                ...tracks.find(t => t.id === trackId).effects?.distortion,
                tone: newValue
              }
            }
          }, false);

          if (audioEngineReady && webAudioEngineRef.current) {
            try {
              webAudioEngineRef.current.updateTrackEffects(trackId, {
                ...tracks.find(t => t.id === trackId).effects,
                distortion: {
                  ...tracks.find(t => t.id === trackId).effects?.distortion,
                  tone: newValue
                }
              });
            } catch (error) {
              console.error('Error updating distortion tone:', error);
            }
          }
          break;
      }
    };

    const handleMouseUp = () => {
      saveToHistory();
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // 音频文件处理
  // Handle audio file processing with track management
  const handleAudioFileProcessing = async (file, trackId, startTime) => {
    try {
      const audioClip = await processAudioFile(file, trackId, startTime, bpm);

      let targetTrack = tracks.find(t => t.id === trackId);

      if (!targetTrack) {
        const newTrackId = Math.max(...tracks.map(t => t.id), 0) + 1;
        const newTrack = {
          id: newTrackId,
          name: `Audio ${tracks.filter(t => t.type === 'audio').length + 1}`,
          type: 'audio', volume: 0.8, pan: 0, mute: false, solo: false, armed: false,
          color: ['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6'][tracks.length % 5],
          instrument: null, clips: [audioClip],
          effects: {
            reverb: 0.2, delay: 0, chorus: 0,
            eq: { low: 0, mid: 0, high: 0 },
            compressor: { threshold: -20, ratio: 4, attack: 0.003, release: 0.25 },
            // Add reverb parameters
            reverbParams: {
              decayTime: 1.5,
              preDelay: 0.03
            },
            // Add delay parameters
            delayParams: {
              delayTime: 0.25,
              feedback: 0.3
            }
          },
          automation: { volume: [], pan: [] }
        };

        setTracks(prev => [...prev, newTrack]);
        targetTrack = newTrack;
      } else {
        setTracks(prev => prev.map(track =>
          track.id === trackId
            ? { ...track, clips: [...track.clips, audioClip] }
            : track
        ));
      }

      // Create audio player in Web Audio Engine
      webAudioEngineRef.current.createPlayer(
        audioClip.id,
        audioClip.audioBuffer,
        targetTrack.volume,
        targetTrack.pan,
        targetTrack.id
      );

      console.log(`Created audio player in Web Audio Engine for clip ${audioClip.id} with pan=${targetTrack.pan}`);

      console.log(`✅ Audio file processed: ${audioClip.name}`);
      saveToHistory();

    } catch (error) {
      console.error('Processing audio file failed:', error);
      alert(`${t.fileProcessingFailed} ${file.name}: ${error.message}`);
    }
  };

  // 创建MIDI片段（确保音符格式正确）
  const createMidiClip = (trackId, startTime = currentTime) => {
    const track = tracks.find(t => t.id === trackId);
    if (!track || track.type !== 'midi') return null;

    console.log(`📋 Creating MIDI clip for track ${trackId} at ${startTime}`);

    const newClip = {
      id: Date.now() + Math.random(),
      name: `MIDI Clip ${track.clips.length + 1}`,
      start: Math.round(startTime * 4) / 4,
      duration: 4,
      sourceDuration: 4,
      loop: false,
      type: 'midi',
      notes: []
    };

    setTracks(prev => prev.map(t =>
      t.id === trackId
        ? { ...t, clips: [...t.clips, newClip] }
        : t
    ));

    setSelectedClip(newClip.id);
    console.log(`✅ Empty MIDI clip created: ${newClip.id}`);

    return newClip.id;
  };

  // 钢琴键点击处理（修复测试音符播放）
  const handlePianoKeyClick = (midiNote) => {
    console.log(`🎹 Piano key clicked: MIDI note ${midiNote}`);

    const track = tracks.find(t => t.id === selectedTrack);
    if (!track || track.type !== 'midi') {
      alert(t.selectMidiTrack);
      return;
    }

    if (!audioEngineReady || !webAudioEngineRef.current?.context) {
      console.warn('🎹 Audio engine not ready yet, skipping sound preview');
      return;
    }

    // Ensure synth exists in Web Audio Engine and play test note
    try {
      let synth = webAudioEngineRef.current.getSynth(track.id);
      if (!synth) {
        console.log(`🎹 Creating synth for track ${track.id} in Web Audio Engine`);
        synth = webAudioEngineRef.current.createSynth(track.id, track.instrument, track.soundfontInstrument);
      }

      if (synth) {
        const noteName = Tone.Frequency(midiNote, "midi").toNote();
        console.log(`🎵 Playing test note: ${noteName} on track ${track.id} using Web Audio Engine`);
        synth.triggerAttackRelease(noteName, 0.5, null, track.volume);
      } else {
        console.error('❌ Failed to create/get synth in Web Audio Engine for test note');
      }
    } catch (error) {
      console.error('Error playing test note:', error);
    }

    // 添加音符到片段
    let targetClipId = selectedClip;
    if (!targetClipId) {
      console.log('No clip selected, creating new clip...');
      targetClipId = createMidiClip(selectedTrack, currentTime);
    }

    if (targetClipId) {
      addNoteToClip(midiNote, 0); // 在片段开始处添加音符
    }
  };

  useEffect(() => {
    handlePianoKeyClickRef.current = handlePianoKeyClick;
  });

  // Close key mapping UIs when leaving piano roll or switching track type
  useEffect(() => {
    if (!showPianoRoll) {
      setShowDrumKeyMap(false);
      setAssigningDrumKeyForSample(null);
      setShowPianoKeyMap(false);
      setAssigningPianoKeyForNote(null);
      return;
    }

    const track = tracks.find(t => t.id === selectedTrack);
    const isDrums = track?.instrument === 'drums' || (track?.name || '').toLowerCase().includes('drum');
    if (!isDrums) {
      setShowDrumKeyMap(false);
      setAssigningDrumKeyForSample(null);
    }
    if (isDrums) {
      setShowPianoKeyMap(false);
      setAssigningPianoKeyForNote(null);
    }
  }, [showPianoRoll, selectedTrack, tracks]);

  // 键盘事件处理
  useEffect(() => {
    const handleKeyDown = (e) => {
      const activeEl = document.activeElement;
      const isTyping =
        activeEl &&
        (activeEl.tagName === 'INPUT' ||
          activeEl.tagName === 'TEXTAREA' ||
          activeEl.tagName === 'SELECT' ||
          activeEl.isContentEditable);
      if (isTyping) return;

      if (!showPianoRollRef.current || isDraggingPlayheadRef.current) return;

      const tracksSnapshot = tracksRef.current || [];
      const selectedTrackId = selectedTrackRef.current;
      const track = tracksSnapshot.find(t => t.id === selectedTrackId);
      if (!track || track.type !== 'midi') return;

      const isDrums = track.instrument === 'drums' || (track.name || '').toLowerCase().includes('drum');

      // Drum mode: key assignment + preview + (optional) recording into MIDI clip
      if (isDrums) {
        if (e.ctrlKey || e.metaKey || e.altKey) return;
        const normalizedKey = (e.key || '').length === 1 ? e.key.toUpperCase() : e.key;

        // When assigning, capture the next key press to map it
        if (showDrumKeyMapRef.current && assigningDrumKeyForSampleRef.current) {
          if (e.key === 'Escape') {
            assigningDrumKeyForSampleRef.current = null;
            setAssigningDrumKeyForSample(null);
            return;
          }
          if (!normalizedKey || normalizedKey.length !== 1) return;

          e.preventDefault();
          e.stopPropagation();

          const sampleToAssign = assigningDrumKeyForSampleRef.current;

          setTracks(prev => prev.map(t => {
            if (t.id !== track.id) return t;
            const prevMap = t.drumKeyMap || {};
            const nextMap = { ...prevMap };

            // Ensure unique mapping: one key -> one sample, one sample -> one key
            Object.keys(nextMap).forEach(k => {
              if (k === normalizedKey || nextMap[k] === sampleToAssign) {
                delete nextMap[k];
              }
            });

            nextMap[normalizedKey] = sampleToAssign;
            return { ...t, drumKeyMap: nextMap };
          }));

          assigningDrumKeyForSampleRef.current = null;
          setAssigningDrumKeyForSample(null);
          return;
        }

        const sampleName = track.drumKeyMap?.[normalizedKey];
        if (!sampleName) return;
        if (pressedKeysRef.current?.has(e.key)) return;

        e.preventDefault();
        e.stopPropagation();

        const newPressedKeys = new Set(pressedKeysRef.current || []);
        newPressedKeys.add(e.key);
        pressedKeysRef.current = newPressedKeys;
        setPressedKeys(newPressedKeys);

        // Immediate preview
        if (audioEngineReadyRef.current && webAudioEngineRef.current?.playDrumSampleByName) {
          webAudioEngineRef.current.playDrumSampleByName(sampleName, track.id, 0.9);
        }

        // Record into MIDI clip while recording is active and the track is armed
        if (isRecordingRef.current && track.armed) {
          const pitch = drumSampleNameToMidi.get(sampleName);
          const ctx = midiRecordingContextRef.current;

          if (typeof pitch === 'number' && Number.isFinite(pitch) && ctx && ctx.trackId === track.id) {
            const absTime = currentTimeRef.current || 0;
            const gridInterval = getGridStepBeatsFromGridSize(gridSizeRef.current || 16);
            const snappedAbsTime = snapToGridRef.current
              ? Math.round(absTime / gridInterval) * gridInterval
              : absTime;
            const start = Math.max(0, snappedAbsTime - ctx.clipStart);

            const duration = typeof currentNoteLengthRef.current === 'number' ? currentNoteLengthRef.current : 0.25;
            const velocityRaw = currentVelocityRef.current;
            const velocity = typeof velocityRaw === 'number'
              ? (velocityRaw > 1 ? Math.min(1, Math.max(0, velocityRaw / 127)) : Math.min(1, Math.max(0, velocityRaw)))
              : 0.8;

            const newNote = {
              id: Date.now() + Math.random(),
              pitch,
              start,
              duration,
              velocity
            };

            setTracks(prev => prev.map(t => {
              if (t.id !== track.id) return t;
              const clips = (t.clips || []).map(c => {
                if (c.id !== ctx.clipId) return c;
                const notes = [...(c.notes || []), newNote];
                const step = getGridStepBeatsFromGridSize(gridSizeRef.current || 16);
                const maxEnd = notes.reduce((m, n) => Math.max(m, (n.start || 0) + (n.duration || 0)), 0);
                const requiredDuration = Math.max(c.duration || 0, Math.ceil(maxEnd / step) * step);
                return { ...c, duration: requiredDuration, notes };
              });
              return { ...t, clips };
            }));

            recordedMidiThisTakeRef.current = true;
          }
        }

        return;
      }

      // Melodic instruments: Musical Typing with custom key map
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      const upperKey = (e.key || '').length === 1 ? e.key.toUpperCase() : e.key;

      // When assigning a key in the piano key map editor
      if (showPianoKeyMapRef.current && assigningPianoKeyForNoteRef.current !== null) {
        if (e.key === 'Escape') {
          assigningPianoKeyForNoteRef.current = null;
          setAssigningPianoKeyForNote(null);
          return;
        }
        const assignKey = upperKey.length === 1 ? upperKey : null;
        if (!assignKey) return;

        e.preventDefault();
        e.stopPropagation();

        const semitoneToAssign = assigningPianoKeyForNoteRef.current;

        setTracks(prev => prev.map(t => {
          if (t.id !== track.id) return t;
          const prevMap = t.pianoKeyMapCustom || { ...DEFAULT_MUSICAL_TYPING_MAP };
          const nextMap = { ...prevMap };

          Object.keys(nextMap).forEach(k => {
            if (k === assignKey || nextMap[k] === semitoneToAssign) {
              delete nextMap[k];
            }
          });

          nextMap[assignKey] = semitoneToAssign;
          return { ...t, pianoKeyMapCustom: nextMap };
        }));

        assigningPianoKeyForNoteRef.current = null;
        setAssigningPianoKeyForNote(null);
        return;
      }

      // While the key map modal is open, consume all keys but don't play/record
      if (showPianoKeyMapRef.current) {
        // Still allow octave/velocity shortcuts
        if (upperKey === 'Z') { e.preventDefault(); setMusicalTypingOctave(prev => Math.max(0, prev - 1)); return; }
        if (upperKey === 'X') { e.preventDefault(); setMusicalTypingOctave(prev => Math.min(8, prev + 1)); return; }
        if (upperKey === 'C') { e.preventDefault(); setMusicalTypingVelocity(prev => Math.max(1, prev - 10)); return; }
        if (upperKey === 'V') { e.preventDefault(); setMusicalTypingVelocity(prev => Math.min(127, prev + 10)); return; }
        return;
      }

      // Octave shift: Z = down, X = up
      if (upperKey === 'Z') {
        e.preventDefault();
        setMusicalTypingOctave(prev => Math.max(0, prev - 1));
        return;
      }
      if (upperKey === 'X') {
        e.preventDefault();
        setMusicalTypingOctave(prev => Math.min(8, prev + 1));
        return;
      }

      // Velocity adjust: C = down, V = up
      if (upperKey === 'C') {
        e.preventDefault();
        setMusicalTypingVelocity(prev => Math.max(1, prev - 10));
        return;
      }
      if (upperKey === 'V') {
        e.preventDefault();
        setMusicalTypingVelocity(prev => Math.min(127, prev + 10));
        return;
      }

      // Look up in the track's custom key map or default
      const keyMapping = track.pianoKeyMapCustom || DEFAULT_MUSICAL_TYPING_MAP;
      const semitoneOffset = keyMapping[upperKey];
      if (semitoneOffset === undefined) return;
      if (pressedKeysRef.current?.has(e.key)) return;

      e.preventDefault();
      e.stopPropagation();

      const newPressedKeys = new Set(pressedKeysRef.current || []);
      newPressedKeys.add(e.key);
      pressedKeysRef.current = newPressedKeys;
      setPressedKeys(newPressedKeys);

      // Calculate MIDI note: C of the base octave = (octave + 1) * 12
      const baseOctave = musicalTypingOctaveRef.current;
      const midiNote = Math.min(127, Math.max(0, (baseOctave + 1) * 12 + semitoneOffset));

      // Use musical typing velocity
      const mtVelocity = musicalTypingVelocityRef.current;
      currentVelocityRef.current = mtVelocity;

      // Preview sound — hold until key release
      if (audioEngineReadyRef.current && webAudioEngineRef.current?.context) {
        try {
          let synth = webAudioEngineRef.current.getSynth(track.id);
          if (!synth) {
            synth = webAudioEngineRef.current.createSynth(track.id, track.instrument, track.soundfontInstrument);
          }
          if (synth && synth.triggerAttack) {
            const noteName = Tone.Frequency(midiNote, "midi").toNote();
            synth.triggerAttack(noteName, null, track.volume);
            // Store note name + trackId so we can release on keyup
            const activeInfo = activeNotesByKeyRef.current.get(e.key) || {};
            activeInfo.noteName = noteName;
            activeInfo.synthTrackId = track.id;
            activeNotesByKeyRef.current.set(e.key, activeInfo);
          }
        } catch (err) {
          console.warn('Musical Typing: audio preview skipped', err.message);
        }
      }

      // Record into MIDI clip while recording is active and the track is armed
      if (isRecordingRef.current && track.armed) {
        const ctx = midiRecordingContextRef.current;
        if (ctx && ctx.trackId === track.id) {
          const absTime = currentTimeRef.current || 0;
          const gridInterval = getGridStepBeatsFromGridSize(gridSizeRef.current || 16);
          const snappedAbsTime = snapToGridRef.current
            ? Math.round(absTime / gridInterval) * gridInterval
            : absTime;
          const start = Math.max(0, snappedAbsTime - ctx.clipStart);
          const minDuration = gridInterval || 0.25;
          const velocity = mtVelocity > 1 ? Math.min(1, Math.max(0, mtVelocity / 127)) : Math.min(1, Math.max(0, mtVelocity));

          const noteId = Date.now() + Math.random();
          const newNote = {
            id: noteId,
            pitch: midiNote,
            start,
            duration: minDuration,
            velocity
          };

          // Merge recording info with the sound preview info already stored
          const existing = activeNotesByKeyRef.current.get(e.key) || {};
          activeNotesByKeyRef.current.set(e.key, {
            ...existing,
            noteId,
            trackId: track.id,
            clipId: ctx.clipId,
            startBeat: snappedAbsTime
          });

          setTracks(prev => prev.map(t => {
            if (t.id !== track.id) return t;
            const clips = (t.clips || []).map(c => {
              if (c.id !== ctx.clipId) return c;
              const notes = [...(c.notes || []), newNote];
              const step = getGridStepBeatsFromGridSize(gridSizeRef.current || 16);
              const maxEnd = notes.reduce((m, n) => Math.max(m, (n.start || 0) + (n.duration || 0)), 0);
              const requiredDuration = Math.max(c.duration || 0, Math.ceil(maxEnd / step) * step);
              return { ...c, duration: requiredDuration, notes };
            });
            return { ...t, clips };
          }));

          recordedMidiThisTakeRef.current = true;
        }
      }
    };

    const handleKeyUp = (e) => {
      if (pressedKeysRef.current?.has(e.key)) {
        const newPressedKeys = new Set(pressedKeysRef.current || []);
        newPressedKeys.delete(e.key);
        pressedKeysRef.current = newPressedKeys;
        setPressedKeys(newPressedKeys);
      }

      const activeNote = activeNotesByKeyRef.current.get(e.key);
      if (activeNote) {
        activeNotesByKeyRef.current.delete(e.key);

        // Release the preview sound
        if (activeNote.noteName && activeNote.synthTrackId != null && webAudioEngineRef.current) {
          try {
            const synth = webAudioEngineRef.current.getSynth(activeNote.synthTrackId);
            if (synth && synth.triggerRelease) {
              synth.triggerRelease(activeNote.noteName);
            }
          } catch (err) { /* ignore */ }
        }

        // Finalize note duration for recorded notes
        if (activeNote.noteId && activeNote.startBeat != null) {
          const absTime = currentTimeRef.current || 0;
          const gridInterval = getGridStepBeatsFromGridSize(gridSizeRef.current || 16);
          const minDuration = gridInterval || 0.25;
          const rawDuration = Math.max(minDuration, absTime - activeNote.startBeat);
          const duration = snapToGridRef.current
            ? Math.max(minDuration, Math.round(rawDuration / gridInterval) * gridInterval)
            : rawDuration;

          setTracks(prev => prev.map(t => {
            if (t.id !== activeNote.trackId) return t;
            const clips = (t.clips || []).map(c => {
              if (c.id !== activeNote.clipId) return c;
              const notes = (c.notes || []).map(n =>
                n.id === activeNote.noteId ? { ...n, duration } : n
              );
              const step = getGridStepBeatsFromGridSize(gridSizeRef.current || 16);
              const maxEnd = notes.reduce((m, n) => Math.max(m, (n.start || 0) + (n.duration || 0)), 0);
              const requiredDuration = Math.max(c.duration || 0, Math.ceil(maxEnd / step) * step);
              return { ...c, duration: requiredDuration, notes };
            });
            return { ...t, clips };
          }));
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Handle piano roll height resizing
  const handlePianoRollResizeStart = (e) => {
    e.preventDefault();
    setIsDraggingPianoRollHandle(true);
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDraggingPianoRollHandle) {
        const windowHeight = window.innerHeight;
        // Reverse the calculation: subtract from 100 to invert the behavior
        // Lower mouse position = larger piano roll height
        const invertedPercentage = 100 - ((e.clientY / windowHeight) * 100);
        const newHeight = Math.max(20, Math.min(80, invertedPercentage));
        setPianoRollHeight(newHeight);
      }
    };

    const handleMouseUp = () => {
      if (isDraggingPianoRollHandle) {
        setIsDraggingPianoRollHandle(false);
      }
    };

    if (isDraggingPianoRollHandle) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingPianoRollHandle]);

  // Real-time update current time (supports audio, MIDI and recording)
  // Optimized: Update playhead position via DOM for smooth animation, 
  // only update React state periodically to avoid re-render lag
  const lastStateUpdateRef = useRef(0);

  useEffect(() => {
    const updateTime = () => {
      if (isPlaying || isRecording) {
        let nextTime = currentTimeRef.current;
        // Prefer the WebAudioEngine clock (matches Web Audio playback/recording timing)
        if (webAudioEngineRef.current?.isPlaying) {
          const engineTime = webAudioEngineRef.current.getCurrentPlaybackTime(bpm);
          nextTime = engineTime;
        } else if (audioPlayerManagerRef.current.isGlobalPlaying) {
          // Legacy fallback (Tone.js audio player manager)
          const audioTime = audioPlayerManagerRef.current.getCurrentPlaybackTime(bpm);
          nextTime = audioTime;
        } else {
          // Legacy fallback (Tone.Transport time)
          const transportSeconds = Tone.Transport.seconds;
          const transportBeats = (transportSeconds * bpm) / 60;
          nextTime = transportBeats;
        }

        // Loop: when reaching the selected region end, restart from loop start.
        if (isPlaying && !isRecording) {
          const region = loopRegionRef.current;
          if (loopEnabledRef.current && region && nextTime >= region.end && !loopRestartInProgressRef.current) {
            loopRestartInProgressRef.current = true;
            try {
              startPlaybackAtRef.current?.(region.start, { resetTransport: false });
            } finally {
              loopRestartInProgressRef.current = false;
            }
            animationRef.current = requestAnimationFrame(updateTime);
            return;
          }
        }

        // Update unified playhead position directly via DOM for smooth animation.
        // Hide the playhead when its beat is outside the visible scroll window.
        // Update the ref for other code that needs current time
        currentTimeRef.current = nextTime;
        syncPlayheadDOM(nextTime);

        // Apply track automation: for each track in READ or WRITE mode,
        // interpolate volume/pan and apply to the Web Audio graph.
        if (webAudioEngineRef.current?.context) {
          const apPoints = automationPointsRef.current;
          const apModes = automationWriteModeRef.current;
          const currentTracks = tracksRef.current;
          currentTracks.forEach(track => {
            const mode = apModes[track.id] || 'read';
            const trackData = apPoints[track.id];
            if (!trackData) return;
            const graph = webAudioEngineRef.current.getTrackGraph(track.id);
            if (!graph) return;

            if (trackData.volume && trackData.volume.length > 0) {
              const vol = getAutomationValue(trackData.volume, nextTime, track.volume);
              graph.volume.gain.setTargetAtTime(vol, webAudioEngineRef.current.context.currentTime, 0.015);
            }
            if (trackData.pan && trackData.pan.length > 0) {
              const pan = getAutomationValue(trackData.pan, nextTime, track.pan);
              graph.stereoPanner.pan.setTargetAtTime(pan, webAudioEngineRef.current.context.currentTime, 0.015);
            }
          });
        }

        // Only update React state every 100ms to reduce re-renders
        // This keeps the time display updated without causing lag
        const now = performance.now();
        if (now - lastStateUpdateRef.current > 100) {
          lastStateUpdateRef.current = now;
          setCurrentTime(nextTime);
        }

        animationRef.current = requestAnimationFrame(updateTime);
      }
    };

    if (isPlaying || isRecording) {
      animationRef.current = requestAnimationFrame(updateTime);
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, isRecording, bpm, pixelsPerBeat, syncPlayheadDOM]);

  // Piano roll functionality
  const handlePianoRollOpen = () => {
    const currentTrack = tracks.find(t => t.id === selectedTrack);
    if (!currentTrack || currentTrack.type !== 'midi') {
      alert(t.onlyMidiTracks);
      return;
    }

    // Ensure synth exists in Web Audio Engine for this track
    if (audioEngineReady && !webAudioEngineRef.current.getSynth(currentTrack.id)) {
      console.log(`Creating synth for track ${currentTrack.id} in Web Audio Engine`);
      webAudioEngineRef.current.createSynth(currentTrack.id, currentTrack.instrument, currentTrack.soundfontInstrument);
    }

    if (!selectedClip) {
      const newClipId = createMidiClip(selectedTrack, currentTime);
      setSelectedClip(newClipId);
    }

    setShowPianoRoll(true);
  };

  // 添加音符到片段
  const addNoteToClip = (pitch, start, duration = currentNoteLength, velocity = currentVelocity) => {
    if (!selectedClip) {
      console.warn('⚠️ No clip selected, creating new clip');
      const track = tracks.find(t => t.id === selectedTrack);
      if (track && track.type === 'midi') {
        createMidiClip(selectedTrack, currentTime);
        return;
      }
      return;
    }

    console.log(`🎵 Adding note: pitch=${pitch}, start=${start}, duration=${duration}, velocity=${velocity}`);

    // Normalize velocity: accept 0..127 or 0..1
    const normalizedVelocity = typeof velocity === 'number'
      ? (velocity > 1 ? Math.min(1, Math.max(0, velocity / 127)) : Math.min(1, Math.max(0, velocity)))
      : 0.8;

    const newNote = {
      id: Date.now() + Math.random(),
      pitch,
      start,
      duration,
      velocity: normalizedVelocity
    };

    setTracks(prev => prev.map(track => ({
      ...track,
      clips: track.clips.map(clip => {
        if (clip.id === selectedClip) {
          const notes = [...(clip.notes || []), newNote];
          // Ensure clip is long enough to contain all notes
          const gridStep = getGridStepBeatsFromGridSize(gridSize); // e.g., 0.25 when gridSize=16
          const maxEnd = notes.reduce((m, n) => Math.max(m, (n.start || 0) + (n.duration || 0)), 0);
          const requiredDuration = Math.max(clip.duration || 0, Math.ceil(maxEnd / gridStep) * gridStep);

          const updatedClip = {
            ...clip,
            duration: requiredDuration,
            notes
          };
          console.log(`✅ Note added to clip ${selectedClip}. New clip duration: ${updatedClip.duration}`);
          return updatedClip;
        }
        return clip;
      })
    })));

    saveToHistory();
  };

  // 删除音符
  const deleteNoteFromClip = (noteId) => {
    if (!selectedClip) return;

    setTracks(prev => prev.map(track => ({
      ...track,
      clips: track.clips.map(clip => {
        if (clip.id === selectedClip) {
          return {
            ...clip,
            notes: (clip.notes || []).filter(note => note.id !== noteId)
          };
        }
        return clip;
      })
    })));

    saveToHistory();
  };

  // 更新音符
  const updateNoteInClip = (noteId, updatedNote) => {
    if (!selectedClip) return;

    setTracks(prev => prev.map(track => ({
      ...track,
      clips: track.clips.map(clip => {
        if (clip.id === selectedClip) {
          const notes = (clip.notes || []).map(note =>
            note.id === noteId ? updatedNote : note
          );
          // Only expand (never shrink) the clip to fit all notes
          const gridStep = getGridStepBeatsFromGridSize(gridSize);
          const maxEnd = notes.reduce((m, n) => Math.max(m, (n.start || 0) + (n.duration || 0)), 0);
          const requiredDuration = Math.max(clip.duration || 0, Math.ceil(maxEnd / gridStep) * gridStep);

          return {
            ...clip,
            duration: requiredDuration,
            notes
          };
        }
        return clip;
      })
    })));
  };

  // 鼓垫点击处理
  const handleDrumPadClick = (midiNote, drumName) => {
    const track = tracks.find(t => t.id === selectedTrack);
    if (!track || track.type !== 'midi') {
      return;
    }

    // 设置鼓垫按下状态
    const newPressedDrums = new Set(pressedDrums);
    newPressedDrums.add(drumName);
    setPressedDrums(newPressedDrums);

    // 200ms后释放
    setTimeout(() => {
      setPressedDrums(prev => {
        const updated = new Set(prev);
        updated.delete(drumName);
        return updated;
      });
    }, 200);

    handlePianoKeyClick(midiNote);
  };

  // 拖拽处理
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
      setDragOverTrack(null);
    }
  };

  const handleTrackDragOver = (e, trackId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverTrack(trackId);
  };

  const handleFileDrop = async (e, trackId) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setDragOverTrack(null);

    const files = Array.from(e.dataTransfer.files);
    const audioFiles = files.filter(file =>
      file.type.startsWith('audio/') ||
      /\.(mp3|wav|m4a|aac|ogg|flac)$/i.test(file.name)
    );

    // if (audioFiles.length === 0) {
    //   alert(`${t.pleaseDropAudioFiles} (${t.supportedFormats})`);
    //   return;
    // }

    const timelineRect = e.currentTarget.getBoundingClientRect();
    const relativeX = e.clientX - timelineRect.left;
    const startTime = Math.max(0, (relativeX / pixelsPerBeat));

    console.log(`📥 Dropping ${audioFiles.length} files at ${startTime.toFixed(2)} beats`);

    for (const file of audioFiles) {
      await handleAudioFileProcessing(file, trackId, startTime);
    }
  };

  // 自动化时间轴点击处理
  // Get automation nodes for a track+param
  const getAutomationNodes = useCallback((trackId, param) => {
    return automationPoints[trackId]?.[param] || [];
  }, [automationPoints]);

  // Set automation nodes for a track+param
  const setAutomationNodes = useCallback((trackId, param, newNodes) => {
    setAutomationPoints(prev => ({
      ...prev,
      [trackId]: {
        ...(prev[trackId] || {}),
        [param]: newNodes,
      }
    }));
  }, []);

  // Get write mode for a track — defaults to 'read'
  const getTrackAutomationMode = useCallback((trackId) => {
    return automationWriteMode[trackId] || 'read';
  }, [automationWriteMode]);

  // Toggle automation mode for a track: read ↔ write
  const cycleTrackAutomationMode = useCallback((trackId) => {
    setAutomationWriteMode(prev => {
      const current = prev[trackId] || 'read';
      return { ...prev, [trackId]: current === 'read' ? 'write' : 'read' };
    });
  }, []);

  // Get active param for a track's automation lane
  const getTrackAutomationParam = useCallback((trackId) => {
    return automationParam[trackId] || 'volume';
  }, [automationParam]);

  const setTrackAutomationParam = useCallback((trackId, param) => {
    setAutomationParam(prev => ({ ...prev, [trackId]: param }));
  }, []);

  // Landing page
  if (showTemplateSelector) {
    return (
      <div style={styles.templateContainer}>
        <div style={styles.templateBox}>
          <div style={styles.templateHeader}>
            <h1 style={styles.templateTitle}>{t.title}</h1>
            <p style={styles.templateSubtitle}>{t.selectTemplate}</p>
          </div>

          <div style={styles.templateGrid}>
            {Object.entries(t.templates).map(([key, name]) => {
              const icons = {
                empty: <Music size={28} />,
                piano: <Music size={28} />,
                band: <Layers size={28} />,
                electronic: <Zap size={28} />,
                hiphop: <Activity size={28} />,
                acoustic: <Volume2 size={28} />
              };

              return (
                <div
                  key={key}
                  style={{
                    ...styles.templateCard,
                    backgroundColor: selectedTemplate === key ? '#3b82f6' : '#334155',
                    borderColor: selectedTemplate === key ? '#60a5fa' : 'transparent',
                  }}
                  onClick={() => setSelectedTemplate(key)}
                  onMouseEnter={(e) => {
                    if (selectedTemplate !== key) {
                      e.target.style.backgroundColor = '#475569';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedTemplate !== key) {
                      e.target.style.backgroundColor = '#334155';
                    }
                  }}
                >
                  <div style={{
                    color: selectedTemplate === key ? '#ffffff' : '#94a3b8',
                    marginBottom: '10px'
                  }}>
                    {icons[key]}
                  </div>
                  <h3 style={{
                    ...styles.templateCardTitle,
                    color: selectedTemplate === key ? '#ffffff' : '#e2e8f0'
                  }}>
                    {name}
                  </h3>
                  <p style={{
                    ...styles.templateCardDesc,
                    color: selectedTemplate === key ? '#e2e8f0' : '#64748b'
                  }}>
                    {t.templateDesc[key]}
                  </p>
                </div>
              );
            })}
          </div>

          <div style={styles.templateFooter}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <label style={{ fontSize: '14px', color: '#9ca3af' }}>{t.language}:</label>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                style={styles.languageSelect}
                title={t.ttLanguage}
              >
                <option value="zh">{t.chinese}</option>
                <option value="en">{t.english}</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              {/* Open existing .ibom project from landing page */}
              <button
                style={{
                  ...styles.createProjectBtn,
                  backgroundColor: '#334155',
                  border: '1px solid #475569',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
                title={t.ttOpenProject}
                onClick={() => loadFileInputRef.current?.click()}
                disabled={isLoading}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#475569'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#334155'; }}
              >
                <Upload size={16} />
                {isLoading ? 'Opening…' : 'Open Project (.ibom)'}
              </button>

              {/* Hidden file input shared with toolbar */}
              <input
                ref={loadFileInputRef}
                type="file"
                accept=".ibom"
                style={{ display: 'none' }}
                onChange={e => {
                  const file = e.target.files?.[0];
                  if (file) handleLoadProject(file);
                  e.target.value = '';
                }}
              />

              <button
                style={styles.createProjectBtn}
                title={t.createProject}
                onClick={() => createProjectFromTemplate(selectedTemplate)}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
              >
                {t.createProject}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const pianoRollTrack = tracks.find(t => t.id === selectedTrack);
  const isDrumPianoRoll =
    showPianoRoll &&
    pianoRollTrack &&
    (pianoRollTrack.instrument === 'drums' || (pianoRollTrack.name || '').toLowerCase().includes('drum'));
  const drumKeyMap = pianoRollTrack?.drumKeyMap || {};
  const drumMapConfig = instrumentConfigs?.drums?.drumMap || {};
  const drumMidiOrder = [36, 38, 42, 46, 49, 51, 45, 47, 43, 57];
  const drumKeyMapSamples = drumMidiOrder
    .filter(midi => drumMapConfig[midi])
    .map(midi => ({ midi, ...drumMapConfig[midi] }));
  const getAssignedKeyForSample = (sampleName) =>
    Object.entries(drumKeyMap).find(([, name]) => name === sampleName)?.[0] || '';

  return (
    <div style={styles.mainContainer}>

      {/* Top toolbar */}
      <div style={styles.topToolbar}>
        <h1 style={styles.appTitle}>
          <TooltipHost label={t.title} placement="below">
            <span>{t.title}</span>
          </TooltipHost>
          <TooltipHost label={audioEngineReady ? t.ttEngineOn : t.ttEngineOff} placement="below">
            <span
              style={{
                ...styles.engineStatus,
                color: audioEngineReady ? '#10b981' : '#ef4444',
                cursor: 'default',
              }}
            >
              {audioEngineReady ? '●' : '○'}
            </span>
          </TooltipHost>
        </h1>
        <div style={styles.toolbarButtons}>
          <TooltipHost label={t.ttLanguage} placement="below">
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              style={styles.languageSelect}
            >
              <option value="zh">{t.chinese}</option>
              <option value="en">{t.english}</option>
            </select>
          </TooltipHost>

          <TooltipHost label={t.ttProjectName} placement="below">
            <input
              type="text"
              value={projectName}
              onChange={e => setProjectName(e.target.value)}
              onKeyDown={e => e.stopPropagation()}
              style={{
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.18)',
                borderRadius: '6px',
                color: '#e2e8f0',
                fontSize: '13px',
                padding: '3px 8px',
                width: '160px',
                outline: 'none',
                marginRight: '2px',
              }}
            />
          </TooltipHost>

          <TooltipHost label={t.ttNewProject} placement="below">
            <button
              type="button"
              style={styles.toolbarBtn}
              onClick={() => setShowTemplateSelector(true)}
            >
              <FolderOpen size={20} />
            </button>
          </TooltipHost>

          <TooltipHost label={t.ttOpenProject} placement="below">
            <button
              type="button"
              style={{
                ...styles.toolbarBtn,
                opacity: isLoading ? 0.5 : 1,
                color: isLoading ? '#94a3b8' : '#e2e8f0',
              }}
              onClick={() => loadFileInputRef.current?.click()}
              disabled={isLoading}
            >
              <Upload size={20} />
            </button>
          </TooltipHost>
          <input
            ref={loadFileInputRef}
            type="file"
            accept=".ibom"
            style={{ display: 'none' }}
            onChange={e => {
              const file = e.target.files?.[0];
              if (file) handleLoadProject(file);
              e.target.value = '';
            }}
          />

          <TooltipHost label={t.ttSaveProject} placement="below">
            <button
              type="button"
              style={{
                ...styles.toolbarBtn,
                opacity: isSaving ? 0.5 : 1,
                color: isSaving ? '#94a3b8' : '#e2e8f0',
              }}
              onClick={handleSaveProject}
              disabled={isSaving}
            >
              <Save size={20} />
            </button>
          </TooltipHost>

          <TooltipHost label={t.ttExportWav} placement="below">
            <button
              type="button"
              style={{
                ...styles.toolbarBtn,
                opacity: isExportingMixdown ? 0.5 : 1,
                color: isExportingMixdown ? '#94a3b8' : '#e2e8f0',
              }}
              onClick={handleExportMixdown}
              disabled={isExportingMixdown}
            >
              <Download size={20} />
            </button>
          </TooltipHost>
          <TooltipHost label={`${t.undo} (Ctrl/Cmd+Z)`} placement="below">
            <button
              type="button"
              style={{
                ...styles.toolbarBtn,
                opacity: historyIndex > 0 ? 1 : 0.5
              }}
              onClick={handleUndo}
              disabled={historyIndex <= 0}
            >
              <Undo size={20} />
            </button>
          </TooltipHost>
          <TooltipHost label={`${t.redo} (Ctrl/Cmd+Shift+Z)`} placement="below">
            <button
              type="button"
              style={{
                ...styles.toolbarBtn,
                opacity: historyIndex < history.length - 1 ? 1 : 0.5
              }}
              onClick={handleRedo}
              disabled={historyIndex >= history.length - 1}
            >
              <Redo size={20} />
            </button>
          </TooltipHost>
          <TooltipHost label={showMixer ? `${t.mixer} (${t.close})` : t.mixer} placement="below">
            <button
              type="button"
              style={{
                ...styles.toolbarBtn,
                backgroundColor: showMixer ? '#3b82f6' : '#334155'
              }}
              onClick={() => setShowMixer(!showMixer)}
            >
              <Sliders size={20} />
            </button>
          </TooltipHost>

        </div>
      </div>

      {/* Transport control bar */}
      <div style={styles.transportBar}>
        <TooltipHost label={t.ttGoToStart}>
          <button
            type="button"
            style={styles.transportBtn}
            onClick={() => {
              handleStop();
              setCurrentTime(0);
            }}
          >
            <SkipBack size={20} />
          </button>
        </TooltipHost>
        <TooltipHost label={isPlaying ? t.ttPause : t.play}>
          <button
            type="button"
            style={styles.playBtn}
            onClick={handlePlay}
            disabled={!audioEngineReady}
          >
            {isPlaying ? <Pause size={24} /> : <Play size={24} />}
          </button>
        </TooltipHost>
        <TooltipHost label={t.ttStopTransport}>
          <button
            type="button"
            style={styles.transportBtn}
            onClick={handleStop}
          >
            <Square size={20} />
          </button>
        </TooltipHost>
        <TooltipHost label={t.ttRecord}>
          <button
            type="button"
            style={{
              ...styles.recordBtn,
              backgroundColor: isRecording ? '#ef4444' : '#334155'
            }}
            onClick={handleRecord}
            disabled={!audioEngineReady}
          >
            <Mic size={20} />
          </button>
        </TooltipHost>

        <TooltipHost label={t.ttTimeCode}>
          <div style={styles.timeDisplay}>
            {formatTime(currentTime)}
          </div>
        </TooltipHost>

        <TooltipHost label={t.ttBpm}>
          <div style={styles.bpmControl}>
            <label style={{ fontSize: '14px' }}>BPM:</label>
            <input
              type="number"
              value={bpm}
              onChange={(e) => {
                const newBpm = Math.max(60, Math.min(200, Number(e.target.value)));
                setBpm(newBpm);
              }}
              style={styles.bpmInput}
              min="60"
              max="200"
            />
          </div>
        </TooltipHost>

        <TooltipHost label={!loopRegion ? t.ttLoopNoRegion : (loopEnabled ? t.ttLoopActive : t.ttLoopPrompt)}>
          <button
            type="button"
            style={{
              ...styles.transportBtn,
              backgroundColor: loopEnabled ? '#10b981' : '#334155',
              opacity: loopRegion ? 1 : 0.5,
              cursor: loopRegion ? 'pointer' : 'not-allowed'
            }}
            onClick={() => {
              if (!loopRegion) return;
              const next = !loopEnabledRef.current;
              loopEnabledRef.current = next;
              loopRegionRef.current = loopRegion;
              setLoopEnabled(next);

              if (webAudioEngineRef.current) {
                webAudioEngineRef.current.keepAlive = next;
              }

              if (next && isPlaying) {
                startPlaybackAtRef.current?.(loopRegion.start, { resetTransport: false });
              }
            }}
            disabled={!loopRegion}
          >
            <RotateCcw size={20} />
          </button>
        </TooltipHost>

        <TooltipHost label={t.metronome}>
          <button
            type="button"
            style={{
              ...styles.transportBtn,
              backgroundColor: metronomeEnabled ? '#3b82f6' : '#334155'
            }}
            onClick={() => setMetronomeEnabled(!metronomeEnabled)}
          >
            <Clock size={20} />
          </button>
        </TooltipHost>

        <TooltipHost label={t.ttAutomationLanes}>
          <button
            type="button"
            style={{
              ...styles.transportBtn,
              backgroundColor: showAutomation ? '#a855f7' : '#334155',
              color: showAutomation ? 'white' : '#9ca3af'
            }}
            onClick={() => setShowAutomation(!showAutomation)}
          >
            <TrendingUp size={20} />
          </button>
        </TooltipHost>

        <TooltipHost label={t.ttLyricsRow}>
          <button
            type="button"
            style={{
              ...styles.transportBtn,
              backgroundColor: showLyricsRow ? '#0ea5e9' : '#334155',
              color: showLyricsRow ? 'white' : '#9ca3af'
            }}
            onClick={() => setShowLyricsRow(v => !v)}
          >
            <AlignLeft size={20} />
          </button>
        </TooltipHost>

        <TooltipHost label={t.ttMasterVolume}>
          <div style={styles.masterVolumeControl}>
            <Volume2 size={20} />
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={masterVolume}
              onChange={(e) => setMasterVolume(Number(e.target.value))}
              style={styles.volumeSlider}
            />
            <span style={{ fontSize: '12px', minWidth: '40px' }}>
              {Math.round(masterVolume * 100)}%
            </span>
          </div>
        </TooltipHost>

        {/* Tool selector */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '2px',
          backgroundColor: '#0f172a',
          borderRadius: '6px',
          padding: '2px',
          border: '1px solid #334155'
        }}>
          {[
            { id: 'select', icon: <MousePointer size={15} />, tip: t.ttSelectTool },
            { id: 'fade', icon: <Triangle size={15} />, tip: t.ttFadeTool },
          ].map(tool => (
            <TooltipHost key={tool.id} label={tool.tip}>
              <button
                type="button"
                onClick={() => setToolMode(tool.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '28px',
                  height: '26px',
                  borderRadius: '4px',
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: toolMode === tool.id ? '#3b82f6' : 'transparent',
                  color: toolMode === tool.id ? '#fff' : '#94a3b8',
                  transition: 'background-color 0.15s, color 0.15s',
                }}
              >
                {tool.icon}
              </button>
            </TooltipHost>
          ))}
        </div>

        <TooltipHost label={t.ttZoom}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '12px', color: '#9ca3af' }}>Zoom</span>
            <input
              type="range"
              min="10"
              max="500"
              step="1"
              value={pixelsPerBeat}
              onChange={(e) => setPixelsPerBeat(Number(e.target.value))}
              style={styles.volumeSlider}
            />
            <span style={{ fontSize: '12px', minWidth: '40px', textAlign: 'right' }}>
              {pixelsPerBeat}px
            </span>
          </div>
        </TooltipHost>

        <TooltipHost label={t.ttGridDivision}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <select
              value={gridSize}
              onChange={(e) => setGridSize(Number(e.target.value))}
              style={{
                backgroundColor: '#1e293b',
                color: '#e2e8f0',
                border: '1px solid #334155',
                borderRadius: '4px',
                padding: '2px 4px',
                fontSize: '11px',
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              {GRID_DIVISION_OPTIONS.map(opt => (
                <option key={opt.gridSize} value={opt.gridSize}>{opt.label}</option>
              ))}
            </select>
          </div>
        </TooltipHost>
      </div>

      {/* 主要内容区 */}
      <div style={{
        ...styles.mainContent,
        flexDirection: showPianoRoll ? 'column' : 'row'
      }}>
        {/* Track Zone Container */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          flex: showPianoRoll ? `0 0 ${100 - pianoRollHeight}%` : 1,
          overflow: 'hidden',
          position: 'relative'
        }}>
          {/* Unified Playhead - position is owned entirely by syncPlayheadDOM (DOM refs).
               The inline style here is just the initial mount state; all subsequent
               repositioning (scroll, beat change, zoom, playback) is done imperatively
               so React re-renders can never fight with the correct scroll-adjusted value. */}
          <>
            <div
              ref={playheadRef}
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: '280px',
                width: '2px',
                backgroundColor: '#facc15',
                zIndex: 100,
                cursor: 'col-resize',
                display: 'none'
              }}
              onMouseDown={handlePlayheadMouseDown}
              title="Drag to change playback position"
            />
            {/* Playhead drag handle — wider invisible grab area */}
            <div
              ref={playheadHandleRef}
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: '276px',
                width: '10px',
                backgroundColor: 'transparent',
                zIndex: 99,
                cursor: 'col-resize',
                display: 'none'
              }}
              onMouseDown={handlePlayheadMouseDown}
              title="Drag to change playback position"
            />
          </>
          {/* Scrollable Track Content Area - Track Controls + Timeline lanes scroll together */}
          <div
            ref={timelineRef}
            style={{
              flex: 1,
              display: 'flex',
              overflow: 'auto',
              position: 'relative'
            }}
            data-timeline
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleFileDrop(e, null)}
          >
            {/* Left Column - Track Controls (scrolls vertically with timeline, fixed horizontally) */}
            <div style={{
              width: '280px',
              minWidth: '280px',
              backgroundColor: '#1e293b',
              borderRight: '1px solid #334155',
              position: 'sticky',
              left: 0,
              zIndex: 25,
              display: 'flex',
              flexDirection: 'column'
            }}>
              {/* Track List Header - Sticky at top-left corner */}
              <div style={{
                width: '280px',
                minWidth: '280px',
                padding: '10px',
                borderBottom: '1px solid #334155',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: '#1e293b',
                height: '40px',
                boxSizing: 'border-box',
                position: 'sticky',
                top: 0,
                zIndex: 30
              }}>
                <h3 style={{ margin: 0, fontSize: '16px' }}>{t.tracks}</h3>
              </div>
              {showLyricsRow && (
                <div style={{
                  width: '280px',
                  minWidth: '280px',
                  height: `${LYRICS_ROW_HEIGHT}px`,
                  minHeight: `${LYRICS_ROW_HEIGHT}px`,
                  boxSizing: 'border-box',
                  padding: '0 10px',
                  borderBottom: '1px solid #334155',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  backgroundColor: '#172033',
                  flexShrink: 0
                }}>
                  <AlignLeft size={14} color="#94a3b8" />
                  <span style={{ flex: 1, fontSize: 11, color: '#94a3b8' }}>{t.lyrics}</span>
                  <button
                    type="button"
                    title={lyricsRowEditMode ? t.ttLyricsRowViewMode : t.ttLyricsRowEditMode}
                    onClick={() => {
                      setLyricsRowEditMode((v) => !v);
                      setTimeout(() => saveToHistory(), 0);
                    }}
                    style={{
                      border: 'none',
                      background: lyricsRowEditMode ? '#334155' : '#0ea5e9',
                      color: '#e2e8f0',
                      borderRadius: 4,
                      padding: '4px 6px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    {lyricsRowEditMode ? <Eye size={16} /> : <Edit3 size={16} />}
                  </button>
                  <button
                    type="button"
                    title={t.ttLyricsRow}
                    onClick={() => setShowLyricsRow(false)}
                    style={{
                      border: 'none',
                      background: '#334155',
                      color: '#e2e8f0',
                      borderRadius: 4,
                      padding: '4px 6px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <ChevronUp size={16} />
                  </button>
                </div>
              )}
              {tracks.map(track => (
                <div
                  key={track.id}
                  draggable
                  onDragStart={(e) => handleTrackReorderStart(e, track.id)}
                  onDragOver={(e) => handleTrackReorderOver(e, track.id)}
                  onDrop={(e) => handleTrackReorderDrop(e, track.id)}
                  onDragEnd={handleTrackReorderEnd}
                  style={{
                    ...styles.trackItem,
                    height: showAutomation ? '175px' : '100px',
                    backgroundColor: draggingTrackId === track.id ? '#1e40af' :
                      dragOverTrackId === track.id ? '#334155' :
                        selectedTrack === track.id ? '#334155' : 'transparent',
                    borderLeft: `4px solid ${track.color}`,
                    opacity: draggingTrackId === track.id ? 0.5 : 1,
                    cursor: 'grab',
                    transition: 'background-color 0.2s, opacity 0.2s',
                    flexShrink: 0
                  }}
                  onClick={() => setSelectedTrack(track.id)}
                >
                  <div style={{
                    ...styles.trackIcon,
                    backgroundColor: track.color
                  }}>
                    {track.type === 'midi' ? 'M' : 'A'}
                  </div>

                  <div style={{ flex: 1, marginLeft: '10px' }}>
                    <input
                      type="text"
                      value={track.name}
                      onChange={(e) => updateTrack(track.id, { name: e.target.value }, false)}
                      onBlur={() => saveToHistory()}
                      style={styles.trackNameInput}
                      onClick={(e) => e.stopPropagation()}
                      onDragStart={(e) => e.stopPropagation()}
                    />

                    <div style={styles.trackControls}>
                      <button
                        style={{
                          ...styles.trackControlBtn,
                          backgroundColor: track.mute ? '#ef4444' : '#334155',
                          color: track.mute ? 'white' : '#9ca3af'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          updateTrack(track.id, { mute: !track.mute });
                        }}
                      >
                        M
                      </button>
                      <button
                        style={{
                          ...styles.trackControlBtn,
                          backgroundColor: track.solo ? '#f59e0b' : '#334155',
                          color: track.solo ? 'white' : '#9ca3af'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          updateTrack(track.id, { solo: !track.solo });
                        }}
                      >
                        S
                      </button>
                      <button
                        style={{
                          ...styles.trackControlBtn,
                          backgroundColor: track.armed ? '#ef4444' : '#334155',
                          color: track.armed ? 'white' : '#9ca3af'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          updateTrack(track.id, { armed: !track.armed });
                          if (track.type !== 'midi') {
                            toggleAudioMonitoring(track.id);
                          }
                        }}
                      >
                        R
                      </button>
                      {track.type === 'midi' && (
                        <button
                          style={{
                            ...styles.trackControlBtn,
                            backgroundColor: '#10b981',
                            color: 'white',
                            fontSize: '8px'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            createMidiClip(track.id, 0);
                          }}
                          title="创建测试片段"
                        >
                          +
                        </button>
                      )}
                    </div>

                    <div style={styles.trackVolumeControl}>
                      <Volume2 size={12} style={{ color: '#9ca3af' }} />
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                        value={track.volume}
                        onChange={(e) => {
                          const vol = Number(e.target.value);
                          updateTrack(track.id, { volume: vol }, false);
                          // Write mode: capture automation node at current playhead position
                          if ((isPlaying || isRecording) && (automationWriteMode[track.id] === 'write')) {
                            const t = currentTimeRef.current;
                            setAutomationNodes(track.id, 'volume', [
                              ...getAutomationNodes(track.id, 'volume').filter(n => Math.abs(n.time - t) > 0.1),
                              { time: t, value: vol }
                            ].sort((a, b) => a.time - b.time));
                          }
                        }}
                        onMouseUp={() => saveToHistory()}
                        style={styles.trackVolumeSlider}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>

                    {/* Automation controls row — Logic-style mode button + param selector */}
                    {showAutomation && (() => {
                      const mode = getTrackAutomationMode(track.id);
                      const modeBg = mode === 'write' ? '#b91c1c' : '#15803d';
                      const modeBorder = mode === 'write' ? '#ef4444' : '#22c55e';
                      const modeText = 'white';
                      return (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                          padding: '3px 8px 2px',
                          borderTop: '1px solid #1e293b',
                        }}>
                          {/* Mode cycle button — matches Logic's green Read button style */}
                          <button
                            title="Automation mode: READ ↔ WRITE"
                            onClick={(e) => { e.stopPropagation(); cycleTrackAutomationMode(track.id); }}
                            style={{
                              fontSize: '10px',
                              fontWeight: 'bold',
                              letterSpacing: '0.04em',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              border: `1px solid ${modeBorder}`,
                              cursor: 'pointer',
                              backgroundColor: modeBg,
                              color: modeText,
                              minWidth: '44px',
                              textAlign: 'center',
                              boxShadow: `0 0 6px ${modeBorder}55`,
                              transition: 'background 0.15s, box-shadow 0.15s',
                            }}
                          >
                            {mode.toUpperCase()}
                          </button>

                          {/* Param selector — always visible when automation is shown */}
                          <select
                            value={getTrackAutomationParam(track.id)}
                            onChange={(e) => { e.stopPropagation(); setTrackAutomationParam(track.id, e.target.value); }}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              fontSize: '10px',
                              backgroundColor: '#0f172a',
                              color: '#94a3b8',
                              border: '1px solid #334155',
                              borderRadius: '4px',
                              padding: '2px 4px',
                              cursor: 'pointer',
                              flex: 1,
                            }}
                          >
                            <option value="volume">Volume</option>
                            <option value="pan">Pan</option>
                          </select>

                          {/* Live value readout */}
                          <span style={{
                            fontSize: '10px',
                            color: '#64748b',
                            minWidth: '32px',
                            textAlign: 'right',
                            fontVariantNumeric: 'tabular-nums',
                          }}>
                            {getTrackAutomationParam(track.id) === 'volume'
                              ? `${Math.round(track.volume * 100)}%`
                              : track.pan === 0 ? 'C'
                                : track.pan > 0 ? `R${Math.round(track.pan * 100)}`
                                  : `L${Math.round(Math.abs(track.pan) * 100)}`
                            }
                          </span>
                        </div>
                      );
                    })()}

                    <div style={{
                      ...styles.trackInfo,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <span>
                        {track.clips.length} clips | {track.clips.reduce((total, clip) => total + (clip.notes?.length || 0), 0)} notes
                      </span>

                      {/* Input level meter for Voice & Mic track */}
                      {track.name === 'Voice & Mic' && (
                        <div style={{
                          width: '120px',
                          height: '20px',
                          backgroundColor: '#1f2937',
                          borderRadius: '4px',
                          overflow: 'hidden',
                          marginLeft: '5px',
                          position: 'relative',
                          border: '1px solid #374151'
                        }}>
                          <div style={{
                            height: '100%',
                            width: `${monitoringTracks.has(track.id) ? (trackInputLevels[track.id] || 0) * 100 : 0}%`,
                            backgroundColor: (() => {
                              const level = trackInputLevels[track.id] || 0;
                              if (level > 0.8) return '#ef4444';
                              if (level > 0.5) return '#f59e0b';
                              return '#10b981';
                            })(),
                            transition: 'width 0.1s ease, background-color 0.1s ease'
                          }} />
                          <div style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '10px',
                            textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)'
                          }}>
                            {monitoringTracks.has(track.id) ?
                              (() => {
                                const level = trackInputLevels[track.id] || 0;
                                const db = (level * 60) - 60;
                                return db <= -60 ? '-∞ dB' : `${Math.round(db)} dB`;
                              })() :
                              'OFF'
                            }
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Add New Track Button */}
              <div
                style={{
                  padding: '12px 16px',
                  borderTop: '1px solid #334155',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: '#9ca3af',
                  fontSize: '14px',
                  transition: 'all 0.2s ease',
                  backgroundColor: '#1e293b',
                  flexShrink: 0
                }}
                onClick={() => setShowTrackSelectionModal(true)}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#334155';
                  e.target.style.color = '#e2e8f0';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#1e293b';
                  e.target.style.color = '#9ca3af';
                }}
                title={t.addNewTrack}
              >
                <Plus size={16} />
                {t.addNewTrack || 'Add New Track'}
              </div>
            </div>

            {/* Right Column - Timeline Lanes */}
            <div style={{
              position: 'relative',
              minWidth: `${timelineWidth}px`,
              flex: 1
            }}>
              {/* Time Ruler — position:absolute so it doesn't affect layout flow,
                  same coordinate system as the track lanes below */}
              <div
                style={{
                  position: 'sticky',
                  top: 0,
                  left: 0,
                  zIndex: 20,
                  width: `${timelineWidth}px`,
                  height: '40px',
                  backgroundColor: '#1e293b',
                  borderBottom: '1px solid #334155',
                  cursor: 'pointer',
                  userSelect: isSelectingLoop ? 'none' : 'auto',
                  flexShrink: 0,
                  overflow: 'hidden'
                }}
                onMouseDown={handleTimeRulerMouseDown}
                onClick={handleTimeRulerClick}
                title={t.clickToPlay}
              >
                {/* Loop region highlight */}
                {loopRegion && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      left: `${loopRegion.start * pixelsPerBeat}px`,
                      width: `${(loopRegion.end - loopRegion.start) * pixelsPerBeat}px`,
                      backgroundColor: loopEnabled ? 'rgba(16,185,129,0.18)' : 'rgba(59,130,246,0.18)',
                      border: loopEnabled ? '1px solid rgba(16,185,129,0.55)' : '1px solid rgba(59,130,246,0.45)',
                      boxSizing: 'border-box',
                      pointerEvents: 'none',
                      zIndex: 0
                    }}
                  />
                )}

                {/* Bar number labels — top half */}
                {Array.from({ length: timelineBars }, (_, barIndex) => (
                  <div
                    key={barIndex}
                    style={{
                      position: 'absolute',
                      left: `${barIndex * pixelsPerBar}px`,
                      top: 0,
                      height: '20px',
                      paddingLeft: '5px',
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '11px',
                      fontWeight: 700,
                      color: '#e2e8f0',
                      pointerEvents: 'none',
                      zIndex: 1
                    }}
                  >
                    {barIndex + 1}
                  </div>
                ))}

                {/* Tick marks — bottom half, CSS-drawn for performance */}
                <div style={{
                  position: 'absolute',
                  left: 0,
                  top: '20px',
                  width: `${timelineWidth}px`,
                  height: '20px',
                  borderTop: '1px solid #334155',
                  // bar ticks (full height of this zone)
                  backgroundImage: [
                    `repeating-linear-gradient(to right, transparent 0px, transparent ${pixelsPerBar - 1}px, rgba(226,232,240,0.55) ${pixelsPerBar - 1}px, rgba(226,232,240,0.55) ${pixelsPerBar}px)`,
                    `repeating-linear-gradient(to right, transparent 0px, transparent ${pixelsPerBeat - 1}px, rgba(148,163,184,0.4) ${pixelsPerBeat - 1}px, rgba(148,163,184,0.4) ${pixelsPerBeat}px)`,
                    ...(rulerTicks.pxPerTick < pixelsPerBeat
                      ? [`repeating-linear-gradient(to right, transparent 0px, transparent ${rulerTicks.pxPerTick - 1}px, rgba(100,116,139,0.25) ${rulerTicks.pxPerTick - 1}px, rgba(100,116,139,0.25) ${rulerTicks.pxPerTick}px)`]
                      : []
                    )
                  ].join(', '),
                  backgroundSize: [
                    `${pixelsPerBar}px 100%`,
                    `${pixelsPerBeat}px 100%`,
                    ...(rulerTicks.pxPerTick < pixelsPerBeat ? [`${rulerTicks.pxPerTick}px 100%`] : [])
                  ].join(', '),
                  pointerEvents: 'none'
                }} />
              </div>

              {showLyricsRow && (
                <LyricsTimelineRow
                  height={LYRICS_ROW_HEIGHT}
                  timelineWidthPx={timelineWidth}
                  pixelsPerBeat={pixelsPerBeat}
                  scrollParentRef={timelineRef}
                  snapBeatToGrid={snapBeatToGrid}
                  snapToGridEnabled={snapToGrid}
                  lyrics={projectLyrics}
                  onAddAtBeat={addLyricAtBeat}
                  onChangeText={updateLyricText}
                  onTextCommit={saveToHistory}
                  onDelete={deleteLyric}
                  onMoveLine={moveLyricToBeat}
                  backgroundImage={trackLaneGridCSS.backgroundImage}
                  backgroundSize={trackLaneGridCSS.backgroundSize}
                  hint={t.lyricsRowHint}
                  dragHint={t.lyricsDragHint}
                  editMode={lyricsRowEditMode}
                  playheadBeat={currentTime}
                  playheadBeatRef={currentTimeRef}
                  playheadAnimating={isPlaying || isRecording || isRecordingPlayback}
                />
              )}

              {tracks.map((track, index) => (
                <div key={track.id}>
                  {/* Main Track Lane */}
                  <div
                    style={{
                      ...styles.trackLane,
                      minWidth: `${timelineWidth}px`,
                      backgroundColor: dragOverTrack === track.id ? '#1e40af' :
                        (index % 2 === 0 ? '#0f172a' : '#1a2332'),
                      backgroundImage: trackLaneGridCSS.backgroundImage,
                      backgroundSize: trackLaneGridCSS.backgroundSize
                    }}
                    onDragOver={(e) => handleTrackDragOver(e, track.id)}
                    onDrop={(e) => handleFileDrop(e, track.id)}
                  >
                    {/* Render Clips */}
                    {track.clips.map((clip) => (
                      <div
                        key={clip.id}
                        style={{
                          ...styles.clip,
                          padding: '0',
                          left: `${clip.start * pixelsPerBeat + (draggingClip === clip.id ? dragOffset.x : 0)}px`,
                          width: `${clip.duration * pixelsPerBeat}px`,
                          backgroundColor: clip.type === 'audio' ? '#10b981' : track.color,
                          opacity: draggingClip === clip.id ? 0.5 : 0.8,
                          border: selectedClip === clip.id ? '2px solid white' : 'none',
                          transform: draggingClip === clip.id ? `translateY(${dragOffset.y}px)` : 'none',
                          zIndex: draggingClip === clip.id ? 1000 : 1,
                          // Change cursor to crosshair when using fade tool on audio clips
                          cursor: toolMode === 'fade' && clip.type === 'audio' ? 'crosshair' : 'move'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedClip(clip.id);
                        }}
                        onMouseDown={(e) => handleClipMouseDown(e, clip, track.id)}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedClip(clip.id);
                          setClipContextMenu({ clipId: clip.id, x: e.clientX, y: e.clientY });
                        }}
                      >
                        {/* Audio Waveform — one WaveformDisplay per loop segment */}
                        {clip.type === 'audio' && clip.waveform && (() => {
                          const fullDur = clip.audioLength || (clip.audioBuffer ? clip.audioBuffer.duration : 1);
                          const ts = typeof clip.trimStart === 'number' ? clip.trimStart : 0;
                          const te = typeof clip.trimEnd === 'number' ? clip.trimEnd : fullDur;

                          if (!clip.loop || !clip.sourceDuration) {
                            // Non-looping: single waveform
                            return (
                              <WaveformDisplay
                                waveform={clip.waveform}
                                width={clip.duration * pixelsPerBeat}
                                height={60}
                                color="#065f46"
                                trimStartRatio={ts / fullDur}
                                trimEndRatio={te / fullDur}
                              />
                            );
                          }

                          // Looping: one WaveformDisplay per cycle, clipped by parent overflow:hidden
                          const segW = clip.sourceDuration * pixelsPerBeat;
                          const totalW = clip.duration * pixelsPerBeat;
                          const numSegs = Math.ceil(totalW / segW);
                          return Array.from({ length: numSegs }, (_, i) => (
                            <div
                              key={i}
                              style={{
                                position: 'absolute',
                                left: `${i * segW}px`,
                                top: 0,
                                width: `${segW}px`,
                                height: '60px',
                                overflow: 'hidden',
                                // Loop copies get a slight dark overlay tint
                                backgroundColor: i > 0 ? 'rgba(0,0,0,0.18)' : 'transparent',
                              }}
                            >
                              <WaveformDisplay
                                waveform={clip.waveform}
                                width={segW}
                                height={60}
                                color={i === 0 ? '#065f46' : '#0a7a52'}
                                trimStartRatio={ts / fullDur}
                                trimEndRatio={te / fullDur}
                              />
                            </div>
                          ));
                        })()}

                        {/* MIDI Notes minimap — one segment per loop cycle */}
                        {clip.type === 'midi' && clip.notes && clip.notes.length > 0 && (() => {
                          const contentOffset = Number(clip.contentOffset) || 0;
                          const sourceDur = (clip.loop && clip.sourceDuration > 0)
                            ? clip.sourceDuration
                            : clip.duration;

                          // Source notes: only those inside the source window
                          const sourceNotes = clip.notes.filter(n =>
                            (n.start + (n.duration || 0)) > contentOffset &&
                            n.start < contentOffset + sourceDur
                          );
                          if (sourceNotes.length === 0) return null;

                          const minPitch = Math.min(...sourceNotes.map(n => n.pitch));
                          const maxPitch = Math.max(...sourceNotes.map(n => n.pitch));
                          const pitchRange = Math.max(12, maxPitch - minPitch + 4);

                          const segW = sourceDur * pixelsPerBeat;
                          const totalW = clip.duration * pixelsPerBeat;
                          const numSegs = clip.loop ? Math.ceil(totalW / segW) : 1;

                          const noteColor = selectedClip === clip.id ? '#ffffff' : '#cccccc';

                          return Array.from({ length: numSegs }, (_, segIdx) => (
                            <div
                              key={segIdx}
                              style={{
                                position: 'absolute',
                                left: `${segIdx * segW}px`,
                                top: 0,
                                width: `${segW}px`,
                                height: '100%',
                                pointerEvents: 'none',
                                overflow: 'hidden',
                                // Loop copies: subtle dark tint to distinguish from original
                                backgroundColor: segIdx > 0 ? 'rgba(0,0,0,0.18)' : 'transparent',
                              }}
                            >
                              {sourceNotes.map(note => {
                                const noteRelStart = note.start - contentOffset;
                                const noteLeft = noteRelStart * pixelsPerBeat;
                                const noteWidth = Math.max(3, note.duration * pixelsPerBeat);
                                const noteTop = 50 - ((note.pitch - minPitch) / pitchRange * 40);
                                return (
                                  <div
                                    key={note.id}
                                    style={{
                                      position: 'absolute',
                                      left: `${noteLeft}px`,
                                      top: `${noteTop}%`,
                                      width: `${noteWidth}px`,
                                      height: '4px',
                                      backgroundColor: noteColor,
                                      opacity: segIdx === 0 ? 0.9 : 0.6,
                                      borderRadius: '1px',
                                      transform: 'translateY(-50%)'
                                    }}
                                  />
                                );
                              })}
                            </div>
                          ));
                        })()}

                        {/* Fade-in / fade-out visual overlays + drag handles (audio only) */}
                        {clip.type === 'audio' && (() => {
                          const clipW = clip.duration * pixelsPerBeat;
                          const clipH = 60;
                          const fadeInSec = typeof clip.fadeIn === 'number' ? clip.fadeIn : 0;
                          const fadeOutSec = typeof clip.fadeOut === 'number' ? clip.fadeOut : 0;
                          const clipDurSec = clip.duration * 60 / bpm;
                          const fadeInPx = clipDurSec > 0 ? (fadeInSec / clipDurSec) * clipW : 0;
                          const fadeOutPx = clipDurSec > 0 ? (fadeOutSec / clipDurSec) * clipW : 0;
                          const isFadeTool = toolMode === 'fade';

                          return (
                            <>
                              {/* Fade-in triangle — always visible when fade is set */}
                              {fadeInPx > 1 && (
                                <svg
                                  style={{ position: 'absolute', left: 0, top: 0, width: `${fadeInPx}px`, height: `${clipH}px`, pointerEvents: 'none', overflow: 'visible' }}
                                  viewBox={`0 0 ${fadeInPx} ${clipH}`}
                                  preserveAspectRatio="none"
                                >
                                  <polygon points={`0,${clipH} ${fadeInPx},0 ${fadeInPx},${clipH}`} fill="rgba(0,0,0,0.45)" />
                                  <line x1="0" y1={clipH} x2={fadeInPx} y2="0" stroke="rgba(255,255,255,0.75)" strokeWidth="1.5" />
                                </svg>
                              )}

                              {/* Fade-out triangle — always visible when fade is set */}
                              {fadeOutPx > 1 && (
                                <svg
                                  style={{ position: 'absolute', right: 0, top: 0, width: `${fadeOutPx}px`, height: `${clipH}px`, pointerEvents: 'none', overflow: 'visible' }}
                                  viewBox={`0 0 ${fadeOutPx} ${clipH}`}
                                  preserveAspectRatio="none"
                                >
                                  <polygon points={`0,0 0,${clipH} ${fadeOutPx},${clipH}`} fill="rgba(0,0,0,0.45)" />
                                  <line x1="0" y1="0" x2={fadeOutPx} y2={clipH} stroke="rgba(255,255,255,0.75)" strokeWidth="1.5" />
                                </svg>
                              )}

                              {/* Fade drag handles — only shown in fade tool mode */}
                              {isFadeTool && (
                                <>
                                  {/* Fade-in handle: sits at the tip of the fade-in ramp (top-left → right) */}
                                  <div
                                    onMouseDown={(e) => handleFadeDragMouseDown(e, clip, 'in')}
                                    title="Drag right to increase fade-in"
                                    style={{
                                      position: 'absolute',
                                      left: `${Math.max(0, fadeInPx - 8)}px`,
                                      top: '4px',
                                      width: '14px',
                                      height: '14px',
                                      borderRadius: '50%',
                                      backgroundColor: '#fbbf24',
                                      border: '2px solid #fff',
                                      cursor: 'ew-resize',
                                      zIndex: 14,
                                      boxShadow: '0 1px 4px rgba(0,0,0,0.5)',
                                      transition: 'transform 0.1s',
                                    }}
                                  />
                                  {/* Fade-out handle: sits at the start of the fade-out ramp */}
                                  <div
                                    onMouseDown={(e) => handleFadeDragMouseDown(e, clip, 'out')}
                                    title="Drag left to increase fade-out"
                                    style={{
                                      position: 'absolute',
                                      right: `${Math.max(0, fadeOutPx - 8)}px`,
                                      top: '4px',
                                      width: '14px',
                                      height: '14px',
                                      borderRadius: '50%',
                                      backgroundColor: '#fbbf24',
                                      border: '2px solid #fff',
                                      cursor: 'ew-resize',
                                      zIndex: 14,
                                      boxShadow: '0 1px 4px rgba(0,0,0,0.5)',
                                      transition: 'transform 0.1s',
                                    }}
                                  />
                                </>
                              )}
                            </>
                          );
                        })()}

                        {/* Audio clip: trimmed duration label */}
                        {clip.type === 'audio' && clip.audioLength && (() => {
                          const ts = typeof clip.trimStart === 'number' ? clip.trimStart : 0;
                          const te = typeof clip.trimEnd === 'number' ? clip.trimEnd : clip.audioLength;
                          const visibleSecs = te - ts;
                          return (
                            <span style={styles.clipDuration}>
                              {Math.round(visibleSecs * 10) / 10}s
                            </span>
                          );
                        })()}

                        {/* Loop segment dividers — vertical line + small notch triangle at top */}
                        {clip.loop && clip.sourceDuration > 0 && (() => {
                          const src = clip.sourceDuration;
                          const total = clip.duration;
                          const numSegs = Math.ceil(total / src);
                          const dividers = [];
                          for (let i = 1; i < numSegs; i++) {
                            const x = i * src * pixelsPerBeat;
                            if (x >= total * pixelsPerBeat) break;
                            dividers.push(
                              <React.Fragment key={i}>
                                {/* Vertical separator line */}
                                <div style={{
                                  position: 'absolute',
                                  left: `${x}px`,
                                  top: 0,
                                  width: '1px',
                                  height: '100%',
                                  backgroundColor: 'rgba(255,255,255,0.45)',
                                  pointerEvents: 'none',
                                  zIndex: 4,
                                }} />
                                {/* Small downward triangle notch at top */}
                                <div style={{
                                  position: 'absolute',
                                  left: `${x - 4}px`,
                                  top: 0,
                                  width: 0,
                                  height: 0,
                                  borderLeft: '4px solid transparent',
                                  borderRight: '4px solid transparent',
                                  borderTop: '5px solid rgba(255,255,255,0.55)',
                                  pointerEvents: 'none',
                                  zIndex: 5,
                                }} />
                              </React.Fragment>
                            );
                          }
                          return dividers;
                        })()}

                        {/* Trim handles — left and right edges (both audio and MIDI clips) */}
                        {(clip.type === 'audio' || clip.type === 'midi') && (
                          <>
                            {/* Left trim handle */}
                            <div
                              onMouseDown={(e) => handleClipTrimMouseDown(e, clip, track.id, 'left')}
                              style={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                width: '8px',
                                height: '100%',
                                cursor: 'w-resize',
                                zIndex: 10,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <div style={{
                                width: '3px',
                                height: '60%',
                                borderRadius: '2px',
                                backgroundColor: selectedClip === clip.id ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.45)',
                              }} />
                            </div>

                            {/* Right trim handle — bottom half only (top half = loop handle) */}
                            <div
                              onMouseDown={(e) => handleClipTrimMouseDown(e, clip, track.id, 'right')}
                              style={{
                                position: 'absolute',
                                right: 0,
                                bottom: 0,
                                width: '10px',
                                height: '50%',
                                cursor: 'e-resize',
                                zIndex: 11,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <div style={{
                                width: '3px',
                                height: '60%',
                                borderRadius: '2px',
                                backgroundColor: selectedClip === clip.id ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.45)',
                              }} />
                            </div>

                            {/* Loop handle — upper-right corner, always visible */}
                            <div
                              onMouseDown={(e) => handleClipLoopMouseDown(e, clip, track.id)}
                              title="Drag to loop region"
                              style={{
                                position: 'absolute',
                                right: 0,
                                top: 0,
                                width: '14px',
                                height: '50%',
                                cursor: 'e-resize',
                                zIndex: 12,
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'flex-end',
                                paddingTop: '3px',
                                paddingRight: '2px',
                              }}
                            >
                              {/* Loop icon: curved arrow arc */}
                              <svg width="10" height="10" viewBox="0 0 10 10" style={{ pointerEvents: 'none', opacity: selectedClip === clip.id ? 0.95 : 0.55 }}>
                                <path
                                  d="M8.5 5 A3.5 3.5 0 1 1 5 1.5"
                                  fill="none"
                                  stroke="white"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                />
                                <polyline
                                  points="5,1.5 7,1.5 7,3.5"
                                  fill="none"
                                  stroke="white"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </div>

                            {/* Loop badge — small "∞" indicator when clip is looping */}
                            {clip.loop && (
                              <div style={{
                                position: 'absolute',
                                top: '3px',
                                left: '6px',
                                fontSize: '10px',
                                color: 'rgba(255,255,255,0.7)',
                                pointerEvents: 'none',
                                zIndex: 5,
                                lineHeight: 1,
                              }}>
                                ∞
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Automation Lane — always visible when showAutomation is on */}
                  {showAutomation && (
                    <AutomationLane
                      trackId={track.id}
                      param={getTrackAutomationParam(track.id)}
                      nodes={getAutomationNodes(track.id, getTrackAutomationParam(track.id))}
                      pixelsPerBeat={pixelsPerBeat}
                      timelineWidth={timelineWidth}
                      trackLaneGridCSS={trackLaneGridCSS}
                      currentTime={currentTime}
                      automationWriteMode={getTrackAutomationMode(track.id)}
                      onNodesChange={(newNodes) => setAutomationNodes(track.id, getTrackAutomationParam(track.id), newNodes)}
                    />
                  )}
                </div>
              ))}

              {/* Drag Indicator */}
              {isDragging && (
                <div style={styles.dragIndicator}>
                  <div style={styles.dragIndicatorContent}>
                    <Upload size={32} style={{ marginBottom: '10px' }} />
                    <br />
                    {t.dropAudioFiles}
                    <br />
                    <span style={{ fontSize: '14px', opacity: 0.8 }}>
                      {t.supportedFormats}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* End of Track Zone Container */}

        {/* Piano Roll Zone - Only show when piano roll is open */}
        {showPianoRoll && (
          <div style={{
            flex: `0 0 ${pianoRollHeight}%`,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#1a1a1a',
            borderTop: '2px solid #3a3a3a',
            overflow: 'hidden',
            position: 'relative'
          }}>
            {/* Resize Handle */}
            <div
              style={{
                position: 'absolute',
                top: '0px',
                left: 0,
                right: 0,
                height: '10px',
                cursor: 'ns-resize',
                zIndex: 1001,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'transparent'
              }}
              onMouseDown={handlePianoRollResizeStart}
            >
              <div style={{
                width: '40px',
                height: '4px',
                backgroundColor: '#666',
                borderRadius: '2px'
              }} />
            </div>

            {/* Header */}
            <div style={{
              height: '60px',
              backgroundColor: '#2a2a2a',
              borderBottom: '1px solid #3a3a3a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 20px',
              flexShrink: 0
            }}>
              {/* Left side - Title and track info */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                <h3 style={{ color: '#fff', fontSize: '16px', fontWeight: 'bold' }}>
                  Piano Roll - {tracks.find(t => t.id === selectedTrack)?.name || 'No Track Selected'}
                </h3>

                {/* Cursor Mode Selector */}
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button
                    onClick={() => setCursorMode('select')}
                    style={{
                      background: cursorMode === 'select' ? '#007AFF' : 'transparent',
                      border: 'none', color: '#fff', padding: '6px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px'
                    }}
                    title="Select Mode"
                  >
                    Select
                  </button>
                  <button
                    onClick={() => setCursorMode('edit')}
                    style={{
                      background: cursorMode === 'edit' ? '#007AFF' : 'transparent',
                      border: 'none', color: '#fff', padding: '6px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px'
                    }}
                    title="Edit Mode (click to add/remove notes)"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => setCursorMode('delete')}
                    style={{
                      background: cursorMode === 'delete' ? '#ef4444' : 'transparent',
                      border: 'none', color: '#fff', padding: '6px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px'
                    }}
                    title="Delete Mode (click notes to delete)"
                  >
                    Delete
                  </button>
                </div>

                {/* Drum key mapping */}
                {isDrumPianoRoll && (
                  <button
                    onClick={() => {
                      setShowDrumKeyMap(v => !v);
                      setAssigningDrumKeyForSample(null);
                    }}
                    style={{
                      background: showDrumKeyMap ? '#007AFF' : 'transparent',
                      border: '1px solid #3a3a3a',
                      color: '#fff',
                      padding: '6px 10px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                    title="Map keyboard keys to drum samples"
                  >
                    Key Map
                  </button>
                )}

                {/* Musical Typing key mapping for melodic instruments */}
                {!isDrumPianoRoll && (
                  <button
                    onClick={() => {
                      setShowPianoKeyMap(v => !v);
                      setAssigningPianoKeyForNote(null);
                    }}
                    style={{
                      background: showPianoKeyMap ? '#007AFF' : 'transparent',
                      border: '1px solid #3a3a3a',
                      color: '#fff',
                      padding: '6px 10px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                    title="Musical Typing - Map keyboard keys to notes"
                  >
                    Musical Typing
                  </button>
                )}
              </div>

              {/* Right side - Close button */}
              <div>
                <button
                  style={{
                    backgroundColor: '#666',
                    border: 'none',
                    color: '#fff',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                  onClick={() => setShowPianoRoll(false)}
                  title={t.ttPianoRollClose}
                >
                  Close
                </button>
              </div>
            </div>

            {/* Drum Key Map Panel - same as before */}
            {isDrumPianoRoll && showDrumKeyMap && pianoRollTrack && (
              <div
                style={{
                  position: 'absolute',
                  top: '70px',
                  left: '20px',
                  width: '360px',
                  backgroundColor: '#111827',
                  border: '1px solid #374151',
                  borderRadius: '10px',
                  padding: '12px',
                  zIndex: 1002,
                  boxShadow: '0 10px 25px rgba(0,0,0,0.45)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <div style={{ color: '#fff', fontWeight: '700', fontSize: '13px' }}>Drum Key Map</div>
                  <button
                    onClick={() => {
                      setShowDrumKeyMap(false);
                      setAssigningDrumKeyForSample(null);
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#9ca3af',
                      cursor: 'pointer',
                      fontSize: '16px',
                      lineHeight: 1
                    }}
                    title="Close"
                  >
                    ×
                  </button>
                </div>

                <div style={{ color: '#9ca3af', fontSize: '11px', marginBottom: '10px' }}>
                  {assigningDrumKeyForSample
                    ? `Press a key to assign to "${assigningDrumKeyForSample}" (Esc to cancel)`
                    : 'Click Set, then press a key to bind it to a drum sample.'}
                </div>

                <div style={{ display: 'grid', gap: '8px', maxHeight: '280px', overflowY: 'auto', paddingRight: '4px' }}>
                  {drumKeyMapSamples.map((drum) => {
                    const assignedKey = getAssignedKeyForSample(drum.name);
                    const isAssigningThis = assigningDrumKeyForSample === drum.name;
                    return (
                      <div
                        key={drum.name}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '10px',
                          backgroundColor: '#0b1220',
                          border: `1px solid ${isAssigningThis ? '#f59e0b' : '#243244'}`,
                          borderRadius: '8px',
                          padding: '10px'
                        }}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <div style={{ color: '#e5e7eb', fontSize: '12px', fontWeight: '600' }}>{drum.label}</div>
                          <div style={{ color: '#64748b', fontSize: '10px' }}>{drum.name}</div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <div style={{
                            minWidth: '34px',
                            textAlign: 'center',
                            padding: '3px 6px',
                            borderRadius: '6px',
                            backgroundColor: '#111827',
                            border: '1px solid #374151',
                            color: assignedKey ? '#e5e7eb' : '#6b7280',
                            fontSize: '12px',
                            fontWeight: '700'
                          }}>
                            {assignedKey || '—'}
                          </div>
                          <button
                            onClick={() => setAssigningDrumKeyForSample(drum.name)}
                            style={{
                              backgroundColor: isAssigningThis ? '#f59e0b' : '#334155',
                              border: 'none',
                              color: '#fff',
                              padding: '6px 8px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '11px',
                              fontWeight: '600'
                            }}
                            title="Assign a keyboard key"
                          >
                            {isAssigningThis ? 'Press…' : 'Set'}
                          </button>
                          <button
                            onClick={() => {
                              setTracks(prev => prev.map(t => {
                                if (t.id !== pianoRollTrack.id) return t;
                                const prevMap = t.drumKeyMap || {};
                                const nextMap = { ...prevMap };
                                Object.keys(nextMap).forEach(k => {
                                  if (nextMap[k] === drum.name) delete nextMap[k];
                                });
                                return { ...t, drumKeyMap: nextMap };
                              }));
                              if (isAssigningThis) setAssigningDrumKeyForSample(null);
                            }}
                            disabled={!assignedKey}
                            style={{
                              backgroundColor: assignedKey ? '#1f2937' : '#111827',
                              border: '1px solid #374151',
                              color: assignedKey ? '#e5e7eb' : '#4b5563',
                              padding: '6px 8px',
                              borderRadius: '6px',
                              cursor: assignedKey ? 'pointer' : 'not-allowed',
                              fontSize: '11px',
                              fontWeight: '600'
                            }}
                            title="Remove mapping"
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                  <button
                    onClick={() => {
                      setTracks(prev => prev.map(t => (
                        t.id === pianoRollTrack.id ? { ...t, drumKeyMap: {} } : t
                      )));
                      setAssigningDrumKeyForSample(null);
                    }}
                    style={{
                      backgroundColor: '#111827',
                      border: '1px solid #374151',
                      color: '#e5e7eb',
                      padding: '8px 10px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '11px',
                      fontWeight: '700'
                    }}
                    title="Remove all key mappings for this track"
                  >
                    Clear All
                  </button>
                </div>
              </div>
            )}

            {/* Musical Typing Key Map Panel */}
            {!isDrumPianoRoll && showPianoKeyMap && pianoRollTrack && (() => {
              const trackKeyMap = pianoRollTrack.pianoKeyMapCustom || { ...DEFAULT_MUSICAL_TYPING_MAP };
              const reverseMap = {};
              Object.entries(trackKeyMap).forEach(([key, semi]) => {
                reverseMap[semi] = key;
              });

              const whiteKeyRow = [
                { semi: 0, note: 'C' }, { semi: 2, note: 'D' }, { semi: 4, note: 'E' },
                { semi: 5, note: 'F' }, { semi: 7, note: 'G' }, { semi: 9, note: 'A' },
                { semi: 11, note: 'B' }, { semi: 12, note: 'C' }, { semi: 14, note: 'D' },
                { semi: 16, note: 'E' }, { semi: 17, note: 'F' }
              ];
              const blackKeyRow = [
                { semi: 1, note: 'C#' }, { semi: 3, note: 'D#' }, { semi: null },
                { semi: 6, note: 'F#' }, { semi: 8, note: 'G#' }, { semi: 10, note: 'A#' },
                { semi: null }, { semi: 13, note: 'C#' }, { semi: 15, note: 'D#' }
              ];

              const getOctaveForSemi = (semi) => musicalTypingOctave + Math.floor(semi / 12);
              const getMidiForSemi = (semi) => (musicalTypingOctave + 1) * 12 + semi;
              const isAssigning = assigningPianoKeyForNote !== null;

              return (
                <div
                  style={{
                    position: 'absolute',
                    top: '70px',
                    left: '20px',
                    width: '620px',
                    backgroundColor: '#1e1e2e',
                    border: '1px solid #374151',
                    borderRadius: '12px',
                    padding: '16px',
                    zIndex: 1002,
                    boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
                    userSelect: 'none'
                  }}
                >
                  {/* Header */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <div style={{ color: '#fff', fontWeight: '700', fontSize: '14px' }}>
                      Musical Typing - {pianoRollTrack.name || 'Track'}
                    </div>
                    <button
                      onClick={() => {
                        setShowPianoKeyMap(false);
                        setAssigningPianoKeyForNote(null);
                      }}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#9ca3af',
                        cursor: 'pointer',
                        fontSize: '18px',
                        lineHeight: 1
                      }}
                      title="Close"
                    >
                      ×
                    </button>
                  </div>

                  <div style={{ color: '#9ca3af', fontSize: '11px', marginBottom: '12px' }}>
                    {isAssigning
                      ? `Press a key to assign to ${NOTE_NAMES[assigningPianoKeyForNote % 12]}${getOctaveForSemi(assigningPianoKeyForNote)} (Esc to cancel)`
                      : 'Click a key box to reassign it. Z/X = Octave down/up, C/V = Velocity down/up.'}
                  </div>

                  {/* Octave & Velocity controls */}
                  <div style={{ display: 'flex', gap: '20px', marginBottom: '14px', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: '#9ca3af', fontSize: '12px' }}>Octave:</span>
                      <span style={{ color: '#fff', fontSize: '14px', fontWeight: '700', minWidth: '24px', textAlign: 'center' }}>
                        C{musicalTypingOctave}
                      </span>
                      <button
                        onClick={() => setMusicalTypingOctave(prev => Math.max(0, prev - 1))}
                        style={{
                          backgroundColor: '#d4a017', border: 'none', color: '#000', padding: '4px 10px',
                          borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '700'
                        }}
                      >
                        - Z
                      </button>
                      <button
                        onClick={() => setMusicalTypingOctave(prev => Math.min(8, prev + 1))}
                        style={{
                          backgroundColor: '#d4a017', border: 'none', color: '#000', padding: '4px 10px',
                          borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '700'
                        }}
                      >
                        + X
                      </button>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: '#9ca3af', fontSize: '12px' }}>Velocity:</span>
                      <span style={{ color: '#fff', fontSize: '14px', fontWeight: '700', minWidth: '30px', textAlign: 'center' }}>
                        {musicalTypingVelocity}
                      </span>
                      <button
                        onClick={() => setMusicalTypingVelocity(prev => Math.max(1, prev - 10))}
                        style={{
                          backgroundColor: '#d4a017', border: 'none', color: '#000', padding: '4px 10px',
                          borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '700'
                        }}
                      >
                        - C
                      </button>
                      <button
                        onClick={() => setMusicalTypingVelocity(prev => Math.min(127, prev + 10))}
                        style={{
                          backgroundColor: '#d4a017', border: 'none', color: '#000', padding: '4px 10px',
                          borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '700'
                        }}
                      >
                        + V
                      </button>
                    </div>
                  </div>

                  {/* Black keys row */}
                  <div style={{ display: 'flex', gap: '4px', marginBottom: '4px', paddingLeft: '28px' }}>
                    {blackKeyRow.map((item, idx) => {
                      if (item.semi === null) {
                        return <div key={`gap-${idx}`} style={{ width: '54px', flexShrink: 0 }} />;
                      }
                      const assignedKey = reverseMap[item.semi] || '';
                      const isAssigningThis = assigningPianoKeyForNote === item.semi;
                      const midi = getMidiForSemi(item.semi);
                      const isPressed = assignedKey && pressedKeys.has(assignedKey.toLowerCase());
                      return (
                        <div
                          key={item.semi}
                          onClick={() => setAssigningPianoKeyForNote(item.semi)}
                          style={{
                            width: '54px',
                            height: '48px',
                            backgroundColor: isAssigningThis ? '#f59e0b' : isPressed ? '#6366f1' : '#7c3aed',
                            border: `2px solid ${isAssigningThis ? '#fbbf24' : '#9333ea'}`,
                            borderRadius: '6px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            flexShrink: 0,
                            transition: 'background-color 0.1s'
                          }}
                          title={`${item.note}${getOctaveForSemi(item.semi)} (MIDI ${midi}) — Click to reassign`}
                        >
                          <div style={{ color: '#fff', fontSize: '13px', fontWeight: '700' }}>
                            {assignedKey || '—'}
                          </div>
                          <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '8px', marginTop: '1px' }}>
                            {item.note}{getOctaveForSemi(item.semi)}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* White keys row */}
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {whiteKeyRow.map((item) => {
                      const assignedKey = reverseMap[item.semi] || '';
                      const isAssigningThis = assigningPianoKeyForNote === item.semi;
                      const midi = getMidiForSemi(item.semi);
                      const isPressed = assignedKey && pressedKeys.has(assignedKey.toLowerCase());
                      return (
                        <div
                          key={item.semi}
                          onClick={() => setAssigningPianoKeyForNote(item.semi)}
                          style={{
                            width: '54px',
                            height: '48px',
                            backgroundColor: isAssigningThis ? '#f59e0b' : isPressed ? '#d1d5db' : '#f3f4f6',
                            border: `2px solid ${isAssigningThis ? '#fbbf24' : '#d1d5db'}`,
                            borderRadius: '6px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            flexShrink: 0,
                            transition: 'background-color 0.1s'
                          }}
                          title={`${item.note}${getOctaveForSemi(item.semi)} (MIDI ${midi}) — Click to reassign`}
                        >
                          <div style={{ color: '#1f2937', fontSize: '13px', fontWeight: '700' }}>
                            {assignedKey || '—'}
                          </div>
                          <div style={{ color: '#6b7280', fontSize: '8px', marginTop: '1px' }}>
                            {item.note}{getOctaveForSemi(item.semi)}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Bottom actions */}
                  <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                    <button
                      onClick={() => {
                        setTracks(prev => prev.map(t => (
                          t.id === pianoRollTrack.id ? { ...t, pianoKeyMapCustom: { ...DEFAULT_MUSICAL_TYPING_MAP } } : t
                        )));
                        setAssigningPianoKeyForNote(null);
                      }}
                      style={{
                        backgroundColor: '#111827',
                        border: '1px solid #374151',
                        color: '#e5e7eb',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '11px',
                        fontWeight: '700'
                      }}
                      title="Reset to default Musical Typing layout"
                    >
                      Reset Default
                    </button>
                    <button
                      onClick={() => {
                        setTracks(prev => prev.map(t => (
                          t.id === pianoRollTrack.id ? { ...t, pianoKeyMapCustom: {} } : t
                        )));
                        setAssigningPianoKeyForNote(null);
                      }}
                      style={{
                        backgroundColor: '#111827',
                        border: '1px solid #374151',
                        color: '#e5e7eb',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '11px',
                        fontWeight: '700'
                      }}
                      title="Remove all key mappings"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* Piano Roll Component - scrollable content */}
            <div style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
              <PianoRoll
                pixelsPerBeat={pixelsPerBeat}
                gridSize={gridSize}
                timeSignature={timeSignature}
                clipStart={selectedClipData?.start || 0}
                clipDuration={selectedClipData?.duration || 0}
                notes={selectedClipData?.notes || []}
                sourceDuration={selectedClipData?.sourceDuration ?? selectedClipData?.duration ?? 0}
                clipLoop={selectedClipData?.loop ?? false}
                selectedNotes={selectedNotes}
                onNoteClick={(e, note) => {
                  e.stopPropagation();

                  // Handle resize operation
                  if (e.type === 'resize' && note.id) {
                    // Update the note duration in the clip
                    setTracks(prev => prev.map(track => ({
                      ...track,
                      clips: track.clips.map(clip =>
                        clip.id === selectedClip
                          ? {
                            ...clip,
                            notes: clip.notes.map(n =>
                              n.id === note.id
                                ? { ...n, duration: note.duration }
                                : n
                            )
                          }
                          : clip
                      )
                    })));
                    return;
                  }

                  // Handle multi-select from drag selection
                  if (e.type === 'multi-select' && e.selectedIds) {
                    setSelectedNotes(e.selectedIds);
                    return;
                  }

                  // Clear selection when clicking empty space in the piano roll
                  if (e.type === 'clear-selection') {
                    setSelectedNotes([]);
                    return;
                  }

                  // Move one or more notes (drag in select mode)
                  if (e.type === 'move' && Array.isArray(e.notes) && selectedClip) {
                    const updatesById = new Map(e.notes.map(n => [n.id, n]));

                    setTracks(prev => prev.map(track => ({
                      ...track,
                      clips: track.clips.map(clip => {
                        if (clip.id !== selectedClip) return clip;

                        const notes = (clip.notes || []).map(n => updatesById.get(n.id) || n);

                        // Only expand (never shrink) the clip to fit all notes
                        const gridStep = getGridStepBeatsFromGridSize(gridSize);
                        const maxEnd = notes.reduce((m, n) => Math.max(m, (n.start || 0) + (n.duration || 0)), 0);
                        const requiredDuration = Math.max(clip.duration || 0, Math.ceil(maxEnd / gridStep) * gridStep);

                        return {
                          ...clip,
                          duration: requiredDuration,
                          notes
                        };
                      })
                    })));
                    return;
                  }

                  // Handle selection
                  if (e.ctrlKey || e.metaKey) {
                    setSelectedNotes(prev =>
                      prev.includes(note.id)
                        ? prev.filter(id => id !== note.id)
                        : [...prev, note.id]
                    );
                  } else {
                    setSelectedNotes([note.id]);
                  }
                }}
                onNoteContextMenu={(e, note) => {
                  // In delete mode, remove immediately without confirmation
                  if (cursorMode === 'delete' || e?.type === 'delete') {
                    deleteNoteFromClip(note.id);
                    return;
                  }
                  // Otherwise show a confirmation context action
                  e.preventDefault();
                  if (window.confirm(`Delete note?`)) {
                    deleteNoteFromClip(note.id);
                  }
                }}
                onGridClick={(midiNote, time) => {
                  if (selectedClip) {
                    addNoteToClip(midiNote, time, currentNoteLength, currentVelocity / 127);
                  }
                }}
                onGridRightClick={(midiNote, time) => {
                  if (selectedClip) {
                    addNoteToClip(midiNote, time, currentNoteLength, currentVelocity / 127);
                  }
                }}
                onPianoKeyClick={handlePianoKeyClick}
                cursorMode={cursorMode}
                currentTrack={tracks.find(t => t.id === selectedTrack)}
                selectedClip={selectedClip}
                onInstrumentChange={(trackId, instrumentName) => {
                  // Store the soundfont instrument with the track
                  setTracks(prev => prev.map(track =>
                    track.id === trackId
                      ? { ...track, soundfontInstrument: instrumentName }
                      : track
                  ));

                  // Load the instrument in WebAudioEngine for playback
                  if (audioEngineReady && webAudioEngineRef.current) {
                    webAudioEngineRef.current.loadSoundfontInstrument(instrumentName).then(() => {
                      // Recreate the synth with the new instrument so playback uses it
                      const track = tracks.find(t => t.id === trackId);
                      if (track) {
                        webAudioEngineRef.current.createSynth(trackId, track.instrument, instrumentName);
                        console.log(`🎹 Updated synth for track ${trackId} to use ${instrumentName}`);
                      }
                    }).catch(err => {
                      console.error('Failed to load instrument for playback:', err);
                    });
                  }
                }}
                onNoteUpdate={(noteId, updates) => {
                  // Update note properties (velocity, etc.)
                  if (!selectedClip) return;

                  setTracks(prev => prev.map(track => ({
                    ...track,
                    clips: track.clips.map(clip => {
                      if (clip.id !== selectedClip) return clip;
                      return {
                        ...clip,
                        notes: clip.notes.map(note =>
                          note.id === noteId
                            ? { ...note, ...updates }
                            : note
                        )
                      };
                    })
                  })));
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* 横向混音台面板 */}
      {showMixer && (
        <div style={{
          ...styles.mixerPanel,
          height: `${mixerHeight}px`
        }}>
          {/* Resize Handle */}
          <div
            style={{
              ...styles.mixerResizeHandle,
              cursor: isDraggingMixerHandle ? 'ns-resize' : 'ns-resize'
            }}
            onMouseDown={handleMixerHandleMouseDown}
            onMouseEnter={() => setIsMixerHandleHovered(true)}
            onMouseLeave={() => setIsMixerHandleHovered(false)}
            title={t.ttMixerResize}
          >
            <div style={{
              ...styles.mixerResizeHandleBar,
              opacity: isMixerHandleHovered || isDraggingMixerHandle ? 1 : 0.6,
              backgroundColor: isMixerHandleHovered || isDraggingMixerHandle ? '#3b82f6' : '#64748b'
            }} />
          </div>

          <div style={styles.mixerHeader}>
            <span>{t.mixer} {selectedTrack ? `- ${tracks.find(t => t.id === selectedTrack)?.name || 'Unknown Track'}` : ''}</span>
            <button
              style={styles.closeBtn}
              onClick={() => setShowMixer(false)}
              title={t.ttCloseMixer}
            >
              <X size={20} />
            </button>
          </div>

          <div
            style={{
              padding: '10px 16px 6px',
              borderBottom: '1px solid #334155',
              backgroundColor: '#152030',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
              <Mic size={14} color="#94a3b8" />
              <span style={{ fontSize: '12px', fontWeight: 600, color: '#e2e8f0' }}>{t.vocalPresets}</span>
            </div>
            <p style={{ fontSize: '11px', color: '#64748b', margin: '0 0 8px 0', maxWidth: '720px', lineHeight: 1.4 }}>
              {t.vocalPresetHint}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {VOCAL_PRESETS.map((preset) => {
                const titleKey = vocalPresetTitleKeys[preset.id] || 'vocalPresetClean';
                const active = selectedTrack && tracks.find(tr => tr.id === selectedTrack)?.vocalPresetId === preset.id;
                return (
                  <TooltipHost key={preset.id} label={t[titleKey]} placement="below">
                    <button
                      type="button"
                      disabled={!selectedTrack}
                      onClick={() => handleApplyVocalPreset(preset)}
                      style={{
                        padding: '6px 12px',
                        fontSize: '12px',
                        borderRadius: '6px',
                        border: active ? '1px solid #3b82f6' : '1px solid #475569',
                        backgroundColor: active ? 'rgba(59, 130, 246, 0.2)' : '#1e293b',
                        color: selectedTrack ? '#e2e8f0' : '#64748b',
                        cursor: selectedTrack ? 'pointer' : 'not-allowed',
                        fontWeight: 500,
                      }}
                    >
                      {t[titleKey]}
                    </button>
                  </TooltipHost>
                );
              })}
            </div>
          </div>

          <div style={styles.mixerContent}>
            {selectedTrack && tracks.filter(track => track.id === selectedTrack).map(track => (
              <React.Fragment key={track.id}>
                {/* Volume & Pan Pedal */}
                <div style={{
                  ...styles.mixerChannel,
                  ...styles.volumePanPedal,
                  border: `2px solid ${track.color}`,
                  borderOpacity: 0.3
                }}>
                  <h4 style={{
                    ...styles.mixerChannelTitle,
                    color: track.color
                  }}>
                    <div style={{
                      ...styles.mixerChannelIcon,
                      backgroundColor: track.color
                    }} />
                    {track.name} - Volume & Pan
                  </h4>

                  <div style={styles.mixerChannelControl}>
                    <label style={styles.mixerLabel}>{t.volume}</label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={track.volume}
                      onChange={(e) => updateTrack(track.id, { volume: Number(e.target.value) }, false)}
                      onMouseUp={() => saveToHistory()}
                      style={styles.mixerSlider}
                    />
                    <span style={styles.mixerValue}>
                      {Math.round(track.volume * 100)}%
                    </span>
                  </div>

                  <div style={styles.mixerChannelControl}>
                    <label style={styles.mixerLabel}>{t.pan}</label>
                    <input
                      type="range"
                      min="-1"
                      max="1"
                      step="0.01"
                      value={track.pan}
                      onChange={(e) => {
                        const pan = Number(e.target.value);
                        updateTrack(track.id, { pan }, false);
                        // Write mode: capture pan automation node
                        if ((isPlaying || isRecording) && (automationWriteMode[track.id] === 'write')) {
                          const t2 = currentTimeRef.current;
                          setAutomationNodes(track.id, 'pan', [
                            ...getAutomationNodes(track.id, 'pan').filter(n => Math.abs(n.time - t2) > 0.1),
                            { time: t2, value: pan }
                          ].sort((a, b) => a.time - b.time));
                        }
                      }}
                      onMouseUp={() => saveToHistory()}
                      style={styles.mixerSlider}
                    />
                    <span style={styles.mixerValue}>
                      {track.pan === 0 ? 'C' :
                        track.pan < 0 ?
                          `L${Math.abs(Math.round(track.pan * 100))}%` :
                          `R${Math.round(track.pan * 100)}%`}
                    </span>
                  </div>
                </div>

                {/* EQ Pedal */}
                <div style={{
                  ...styles.mixerChannel,
                  ...styles.eqPedal,
                  background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 50%, #1d4ed8 100%)',
                  border: '2px solid #1e3a8a'
                }}>
                  <h4 style={{
                    ...styles.mixerChannelTitle,
                    color: '#ffffff',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    marginBottom: '15px',
                    fontSize: '12px'
                  }}>
                    EQ
                  </h4>

                  <div style={styles.eqKnobsContainer}>
                    <div style={styles.eqKnobRow}>
                      {/* Low Knob */}
                      <div style={styles.eqKnobGroup}>
                        <div
                          style={styles.eqKnob}
                          onMouseDown={(e) => handleKnobDrag(e, track.id, 'eqLow', track.effects?.eq?.low || 0)}
                        >
                          <div style={{
                            ...styles.eqKnobPointer,
                            transform: `rotate(${((track.effects?.eq?.low || 0) + 15) / 30 * 270 - 135}deg)`
                          }}></div>
                        </div>
                        <label style={styles.eqKnobLabel}>LOW</label>
                        <span style={styles.eqKnobValue}>
                          {(track.effects?.eq?.low || 0) >= 0 ? '+' : ''}{Math.round(track.effects?.eq?.low || 0)}dB
                        </span>
                      </div>

                      {/* Mid Knob */}
                      <div style={styles.eqKnobGroup}>
                        <div
                          style={styles.eqKnob}
                          onMouseDown={(e) => handleKnobDrag(e, track.id, 'eqMid', track.effects?.eq?.mid || 0)}
                        >
                          <div style={{
                            ...styles.eqKnobPointer,
                            transform: `rotate(${((track.effects?.eq?.mid || 0) + 15) / 30 * 270 - 135}deg)`
                          }}></div>
                        </div>
                        <label style={styles.eqKnobLabel}>MID</label>
                        <span style={styles.eqKnobValue}>
                          {(track.effects?.eq?.mid || 0) >= 0 ? '+' : ''}{Math.round(track.effects?.eq?.mid || 0)}dB
                        </span>
                      </div>

                      {/* High Knob */}
                      <div style={styles.eqKnobGroup}>
                        <div
                          style={styles.eqKnob}
                          onMouseDown={(e) => handleKnobDrag(e, track.id, 'eqHigh', track.effects?.eq?.high || 0)}
                        >
                          <div style={{
                            ...styles.eqKnobPointer,
                            transform: `rotate(${((track.effects?.eq?.high || 0) + 15) / 30 * 270 - 135}deg)`
                          }}></div>
                        </div>
                        <label style={styles.eqKnobLabel}>HIGH</label>
                        <span style={styles.eqKnobValue}>
                          {(track.effects?.eq?.high || 0) >= 0 ? '+' : ''}{Math.round(track.effects?.eq?.high || 0)}dB
                        </span>
                      </div>
                    </div>

                    {/* LED Indicator */}
                    <div style={styles.eqLED}></div>
                  </div>
                </div>

                {/* Compressor Pedal */}
                <div style={{
                  ...styles.mixerChannel,
                  ...styles.compressorPedal,
                  background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 50%, #7f1d1d 100%)',
                  border: '2px solid #7f1d1d'
                }}>
                  <h4 style={{
                    ...styles.mixerChannelTitle,
                    color: '#ffffff',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    marginBottom: '15px',
                    fontSize: '12px'
                  }}>
                    COMPRESSOR
                  </h4>

                  <div style={styles.compressorKnobsContainer}>
                    {/* Top Row: Threshold, Ratio, Attack */}
                    <div style={styles.compressorKnobRow}>
                      {/* Threshold Knob */}
                      <div style={styles.compressorKnobGroup}>
                        <div
                          style={{
                            ...styles.compressorKnob,
                            transform: `rotate(${((track.effects?.compressor?.threshold || -24) + 60) / 60 * 270 - 135}deg)`
                          }}
                          onMouseDown={(e) => handleKnobDrag(e, track.id, 'compressorThreshold', track.effects?.compressor?.threshold || -24)}
                        >
                          <div style={styles.compressorKnobPointer}></div>
                        </div>
                        <label style={styles.compressorKnobLabel}>THRESH</label>
                        <span style={styles.compressorKnobValue}>
                          {Math.round(track.effects?.compressor?.threshold || -24)}dB
                        </span>
                      </div>

                      {/* Ratio Knob */}
                      <div style={styles.compressorKnobGroup}>
                        <div
                          style={{
                            ...styles.compressorKnob,
                            transform: `rotate(${((track.effects?.compressor?.ratio || 4) - 1) / 19 * 270 - 135}deg)`
                          }}
                          onMouseDown={(e) => handleKnobDrag(e, track.id, 'compressorRatio', track.effects?.compressor?.ratio || 4)}
                        >
                          <div style={styles.compressorKnobPointer}></div>
                        </div>
                        <label style={styles.compressorKnobLabel}>RATIO</label>
                        <span style={styles.compressorKnobValue}>
                          {(track.effects?.compressor?.ratio || 4).toFixed(1)}:1
                        </span>
                      </div>

                      {/* Attack Knob */}
                      <div style={styles.compressorKnobGroup}>
                        <div
                          style={{
                            ...styles.compressorKnob,
                            transform: `rotate(${((track.effects?.compressor?.attack || 0.003) / 0.1) * 270 - 135}deg)`
                          }}
                          onMouseDown={(e) => handleKnobDrag(e, track.id, 'compressorAttack', track.effects?.compressor?.attack || 0.003)}
                        >
                          <div style={styles.compressorKnobPointer}></div>
                        </div>
                        <label style={styles.compressorKnobLabel}>ATTACK</label>
                        <span style={styles.compressorKnobValue}>
                          {((track.effects?.compressor?.attack || 0.003) * 1000).toFixed(1)}ms
                        </span>
                      </div>
                    </div>

                    {/* Bottom Row: Release, Makeup, Output */}
                    <div style={styles.compressorKnobRow}>
                      {/* Release Knob */}
                      <div style={styles.compressorKnobGroup}>
                        <div
                          style={{
                            ...styles.compressorKnob,
                            transform: `rotate(${((track.effects?.compressor?.release || 0.25) / 1) * 270 - 135}deg)`
                          }}
                          onMouseDown={(e) => handleKnobDrag(e, track.id, 'compressorRelease', track.effects?.compressor?.release || 0.25)}
                        >
                          <div style={styles.compressorKnobPointer}></div>
                        </div>
                        <label style={styles.compressorKnobLabel}>RELEASE</label>
                        <span style={styles.compressorKnobValue}>
                          {Math.round((track.effects?.compressor?.release || 0.25) * 1000)}ms
                        </span>
                      </div>

                      {/* Makeup Gain Knob */}
                      <div style={styles.compressorKnobGroup}>
                        <div
                          style={{
                            ...styles.compressorKnob,
                            transform: `rotate(${((track.effects?.compressor?.makeupGain || 0) + 20) / 40 * 270 - 135}deg)`
                          }}
                          onMouseDown={(e) => handleKnobDrag(e, track.id, 'compressorMakeupGain', track.effects?.compressor?.makeupGain || 0)}
                        >
                          <div style={styles.compressorKnobPointer}></div>
                        </div>
                        <label style={styles.compressorKnobLabel}>MAKEUP</label>
                        <span style={styles.compressorKnobValue}>
                          {(track.effects?.compressor?.makeupGain || 0) >= 0 ? '+' : ''}{Math.round(track.effects?.compressor?.makeupGain || 0)}dB
                        </span>
                      </div>

                      {/* Output Gain Knob */}
                      <div style={styles.compressorKnobGroup}>
                        <div
                          style={{
                            ...styles.compressorKnob,
                            transform: `rotate(${((track.effects?.compressor?.outputGain || 0) + 20) / 40 * 270 - 135}deg)`
                          }}
                          onMouseDown={(e) => handleKnobDrag(e, track.id, 'compressorOutputGain', track.effects?.compressor?.outputGain || 0)}
                        >
                          <div style={styles.compressorKnobPointer}></div>
                        </div>
                        <label style={styles.compressorKnobLabel}>OUTPUT</label>
                        <span style={styles.compressorKnobValue}>
                          {(track.effects?.compressor?.outputGain || 0) >= 0 ? '+' : ''}{Math.round(track.effects?.compressor?.outputGain || 0)}dB
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* LED Indicator */}
                  <div style={{
                    ...styles.compressorLED,
                    backgroundColor: '#ef4444'
                  }}></div>
                </div>

                {/* Distortion Pedal */}
                <div style={{
                  ...styles.mixerChannel,
                  ...styles.distortionPedal,
                  background: 'linear-gradient(135deg, #f97316 0%, #ea580c 50%, #c2410c 100%)',
                  border: '2px solid #c2410c'
                }}>
                  <h4 style={{
                    ...styles.mixerChannelTitle,
                    color: '#ffffff',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    marginBottom: '15px',
                    fontSize: '12px'
                  }}>
                    DISTORTION
                  </h4>

                  <div style={styles.distortionKnobsContainer}>
                    {/* Top Row: Pre Gain, Drive, Tone */}
                    <div style={styles.distortionKnobRow}>
                      {/* Pre Gain Knob */}
                      <div style={styles.distortionKnobGroup}>
                        <div
                          style={{
                            ...styles.distortionKnob,
                            transform: `rotate(${((track.effects?.distortion?.preGain || 0) + 20) / 40 * 270 - 135}deg)`
                          }}
                          onMouseDown={(e) => handleKnobDrag(e, track.id, 'distortionPreGain', track.effects?.distortion?.preGain || 0)}
                        >
                          <div style={styles.distortionKnobPointer}></div>
                        </div>
                        <label style={styles.distortionKnobLabel}>PRE GAIN</label>
                        <span style={styles.distortionKnobValue}>
                          {(track.effects?.distortion?.preGain || 0) >= 0 ? '+' : ''}{Math.round(track.effects?.distortion?.preGain || 0)}dB
                        </span>
                      </div>

                      {/* Drive Knob */}
                      <div style={styles.distortionKnobGroup}>
                        <div
                          style={{
                            ...styles.distortionKnob,
                            transform: `rotate(${((track.effects?.distortion?.drive || 0) + 20) / 40 * 270 - 135}deg)`
                          }}
                          onMouseDown={(e) => handleKnobDrag(e, track.id, 'distortionDrive', track.effects?.distortion?.drive || 0)}
                        >
                          <div style={styles.distortionKnobPointer}></div>
                        </div>
                        <label style={styles.distortionKnobLabel}>DRIVE</label>
                        <span style={styles.distortionKnobValue}>
                          {(track.effects?.distortion?.drive || 0) >= 0 ? '+' : ''}{Math.round(track.effects?.distortion?.drive || 0)}dB
                        </span>
                      </div>

                      {/* Tone Knob */}
                      <div style={styles.distortionKnobGroup}>
                        <div
                          style={{
                            ...styles.distortionKnob,
                            transform: `rotate(${((track.effects?.distortion?.tone || 5000) - 200) / 7800 * 270 - 135}deg)`
                          }}
                          onMouseDown={(e) => handleKnobDrag(e, track.id, 'distortionTone', track.effects?.distortion?.tone || 5000)}
                        >
                          <div style={styles.distortionKnobPointer}></div>
                        </div>
                        <label style={styles.distortionKnobLabel}>TONE</label>
                        <span style={styles.distortionKnobValue}>
                          {Math.round((track.effects?.distortion?.tone || 5000) / 1000 * 10) / 10}kHz
                        </span>
                      </div>
                    </div>

                    {/* Mode Selector */}
                    <div style={styles.distortionModeContainer}>
                      <div style={styles.distortionModeSelector}>
                        {['Clean', 'Overdrive', 'Distortion', 'Nasty'].map((mode) => (
                          <button
                            key={mode}
                            style={{
                              ...styles.distortionModeButton,
                              backgroundColor: (track.effects?.distortion?.mode || 'clean').toLowerCase() === mode.toLowerCase() ? '#f97316' : '#374151',
                              color: (track.effects?.distortion?.mode || 'clean').toLowerCase() === mode.toLowerCase() ? '#ffffff' : '#9ca3af'
                            }}
                            onClick={() => {
                              updateTrack(track.id, {
                                effects: {
                                  ...track.effects,
                                  distortion: {
                                    ...track.effects?.distortion,
                                    mode: mode.toLowerCase()
                                  }
                                }
                              });

                              if (audioEngineReady && webAudioEngineRef.current) {
                                try {
                                  webAudioEngineRef.current.updateTrackEffects(track.id, {
                                    ...track.effects,
                                    distortion: {
                                      ...track.effects?.distortion,
                                      mode: mode.toLowerCase()
                                    }
                                  });
                                } catch (error) {
                                  console.error('Error updating distortion mode:', error);
                                }
                              }
                            }}
                          >
                            {mode}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* LED Indicator */}
                  <div style={{
                    ...styles.distortionLED,
                    backgroundColor: ((track.effects?.distortion?.preGain || 0) > 0 || (track.effects?.distortion?.drive || 0) > 0) ? '#f97316' : '#374151'
                  }}></div>
                </div>

                {/* Chorus Pedal */}
                <div style={{
                  ...styles.mixerChannel,
                  ...styles.chorusPedal,
                  background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 50%, #4c1d95 100%)',
                  border: '2px solid #4c1d95'
                }}>
                  <h4 style={{
                    ...styles.mixerChannelTitle,
                    color: '#ffffff',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    marginBottom: '20px'
                  }}>
                    CHORUS
                  </h4>

                  <div style={styles.chorusKnobsContainer}>
                    {/* Chorus Level Knob */}
                    <div style={styles.chorusKnobGroup}>
                      <div
                        style={{
                          ...styles.chorusKnob,
                          transform: `rotate(${(track.effects?.chorus || 0) * 270 - 135}deg)`
                        }}
                        onMouseDown={(e) => handleKnobDrag(e, track.id, 'chorus', track.effects?.chorus || 0)}
                      >
                        <div style={styles.chorusKnobPointer}></div>
                      </div>
                      <label style={styles.chorusKnobLabel}>LEVEL</label>
                      <span style={styles.chorusKnobValue}>
                        {Math.round((track.effects?.chorus || 0) * 100)}
                      </span>
                    </div>

                    {/* Chorus Rate Knob */}
                    {(track.effects?.chorus > 0) && (
                      <div style={styles.chorusKnobGroup}>
                        <div
                          style={{
                            ...styles.chorusKnob,
                            transform: `rotate(${((track.effects?.chorusParams?.rate || 0.5) - 0.1) / 9.9 * 270 - 135}deg)`
                          }}
                          onMouseDown={(e) => handleKnobDrag(e, track.id, 'chorusRate', (track.effects?.chorusParams?.rate || 0.5))}
                        >
                          <div style={styles.chorusKnobPointer}></div>
                        </div>
                        <label style={styles.chorusKnobLabel}>RATE</label>
                        <span style={styles.chorusKnobValue}>
                          {(track.effects?.chorusParams?.rate || 0.5).toFixed(1)}Hz
                        </span>
                      </div>
                    )}

                    {/* Chorus Depth Knob */}
                    {(track.effects?.chorus > 0) && (
                      <div style={styles.chorusKnobGroup}>
                        <div
                          style={{
                            ...styles.chorusKnob,
                            transform: `rotate(${((track.effects?.chorusParams?.depth || 0.5) * 270 - 135)}deg)`
                          }}
                          onMouseDown={(e) => handleKnobDrag(e, track.id, 'chorusDepth', (track.effects?.chorusParams?.depth || 0.5))}
                        >
                          <div style={styles.chorusKnobPointer}></div>
                        </div>
                        <label style={styles.chorusKnobLabel}>DEPTH</label>
                        <span style={styles.chorusKnobValue}>
                          {Math.round((track.effects?.chorusParams?.depth || 0.5) * 100)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* LED Indicator */}
                  <div style={{
                    ...styles.chorusLED,
                    backgroundColor: (track.effects?.chorus > 0) ? '#8b5cf6' : '#374151'
                  }}></div>
                </div>

                {/* Delay Pedal */}
                <div style={{
                  ...styles.mixerChannel,
                  ...styles.delayPedal,
                  background: 'linear-gradient(135deg, #059669 0%, #047857 50%, #064e3b 100%)',
                  border: '2px solid #064e3b'
                }}>
                  <h4 style={{
                    ...styles.mixerChannelTitle,
                    color: '#ffffff',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    marginBottom: '20px'
                  }}>
                    DELAY
                  </h4>

                  <div style={styles.delayKnobsContainer}>
                    {/* Delay Level Knob */}
                    <div style={styles.delayKnobGroup}>
                      <div
                        style={{
                          ...styles.delayKnob,
                          transform: `rotate(${(track.effects?.delay || 0) * 270 - 135}deg)`
                        }}
                        onMouseDown={(e) => handleKnobDrag(e, track.id, 'delay', track.effects?.delay || 0)}
                      >
                        <div style={styles.delayKnobPointer}></div>
                      </div>
                      <label style={styles.delayKnobLabel}>LEVEL</label>
                      <span style={styles.delayKnobValue}>
                        {Math.round((track.effects?.delay || 0) * 100)}
                      </span>
                    </div>

                    {/* Delay Time Knob */}
                    {(track.effects?.delay > 0) && (
                      <div style={styles.delayKnobGroup}>
                        <div
                          style={{
                            ...styles.delayKnob,
                            transform: `rotate(${((track.effects?.delayParams?.delayTime || 0.25) - 0.01) / 1.99 * 270 - 135}deg)`
                          }}
                          onMouseDown={(e) => handleKnobDrag(e, track.id, 'delayTime', (track.effects?.delayParams?.delayTime || 0.25))}
                        >
                          <div style={styles.delayKnobPointer}></div>
                        </div>
                        <label style={styles.delayKnobLabel}>TIME</label>
                        <span style={styles.delayKnobValue}>
                          {((track.effects?.delayParams?.delayTime || 0.25) * 1000).toFixed(0)}ms
                        </span>
                      </div>
                    )}

                    {/* Feedback Knob */}
                    {(track.effects?.delay > 0) && (
                      <div style={styles.delayKnobGroup}>
                        <div
                          style={{
                            ...styles.delayKnob,
                            transform: `rotate(${((track.effects?.delayParams?.feedback || 0.3) / 0.95) * 270 - 135}deg)`
                          }}
                          onMouseDown={(e) => handleKnobDrag(e, track.id, 'delayFeedback', (track.effects?.delayParams?.feedback || 0.3))}
                        >
                          <div style={styles.delayKnobPointer}></div>
                        </div>
                        <label style={styles.delayKnobLabel}>FEEDBACK</label>
                        <span style={styles.delayKnobValue}>
                          {Math.round((track.effects?.delayParams?.feedback || 0.3) * 100)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* LED Indicator */}
                  <div style={{
                    ...styles.delayLED,
                    backgroundColor: (track.effects?.delay > 0) ? '#10b981' : '#374151'
                  }}></div>
                </div>

                {/* Reverb Pedal */}
                <div style={{
                  ...styles.mixerChannel,
                  ...styles.reverbPedal,
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 50%, #92400e 100%)',
                  border: '2px solid #92400e'
                }}>
                  <h4 style={{
                    ...styles.mixerChannelTitle,
                    color: '#1f2937',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    marginBottom: '20px'
                  }}>
                    REVERB
                  </h4>

                  <div style={styles.reverbKnobsContainer}>
                    {/* Reverb Level Knob */}
                    <div style={styles.reverbKnobGroup}>
                      <div
                        style={{
                          ...styles.reverbKnob,
                          transform: `rotate(${(track.effects?.reverb || 0) * 270 - 135}deg)`
                        }}
                        onMouseDown={(e) => handleKnobDrag(e, track.id, 'reverb', track.effects?.reverb || 0)}
                      >
                        <div style={styles.reverbKnobPointer}></div>
                      </div>
                      <label style={styles.reverbKnobLabel}>LEVEL</label>
                      <span style={styles.reverbKnobValue}>
                        {Math.round((track.effects?.reverb || 0) * 100)}
                      </span>
                    </div>

                    {/* Decay Time Knob */}
                    {(track.effects?.reverb > 0) && (
                      <div style={styles.reverbKnobGroup}>
                        <div
                          style={{
                            ...styles.reverbKnob,
                            transform: `rotate(${((track.effects?.reverbParams?.decayTime || 1.5) - 0.1) / 9.9 * 270 - 135}deg)`
                          }}
                          onMouseDown={(e) => handleKnobDrag(e, track.id, 'decay', (track.effects?.reverbParams?.decayTime || 1.5))}
                        >
                          <div style={styles.reverbKnobPointer}></div>
                        </div>
                        <label style={styles.reverbKnobLabel}>DECAY</label>
                        <span style={styles.reverbKnobValue}>
                          {(track.effects?.reverbParams?.decayTime || 1.5).toFixed(1)}s
                        </span>
                      </div>
                    )}

                    {/* Pre-delay Knob */}
                    {(track.effects?.reverb > 0) && (
                      <div style={styles.reverbKnobGroup}>
                        <div
                          style={{
                            ...styles.reverbKnob,
                            transform: `rotate(${((track.effects?.reverbParams?.preDelay || 0.03) / 0.2) * 270 - 135}deg)`
                          }}
                          onMouseDown={(e) => handleKnobDrag(e, track.id, 'preDelay', (track.effects?.reverbParams?.preDelay || 0.03))}
                        >
                          <div style={styles.reverbKnobPointer}></div>
                        </div>
                        <label style={styles.reverbKnobLabel}>PRE-DLY</label>
                        <span style={styles.reverbKnobValue}>
                          {Math.round((track.effects?.reverbParams?.preDelay || 0.03) * 1000)}ms
                        </span>
                      </div>
                    )}
                  </div>

                  {/* LED Indicator */}
                  <div style={{
                    ...styles.reverbLED,
                    backgroundColor: (track.effects?.reverb > 0) ? '#10b981' : '#374151'
                  }}></div>
                </div>
              </React.Fragment>
            ))}



            {/* Show message when no track is selected */}
            {!selectedTrack && (
              <div style={styles.mixerNoTrackMessage}>
                <p style={{ color: '#9ca3af', fontSize: '14px', textAlign: 'center' }}>
                  {language === 'zh' ? '请选择一个轨道以显示混音控制' : 'Select a track to show mixer controls'}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 底部状态栏 */}
      {/* <div style={styles.statusBar}>
        <div style={styles.statusInfo}>
          <span>{t.cpu}: {Math.round(Math.random() * 20 + 10)}%</span>
          <span>{t.memory}: 2.1GB</span>
          <span>{t.sampleRate}: 48kHz</span>
          <span>{t.buffer}: 512</span>
          <span style={{ color: '#3b82f6', fontWeight: 'bold' }}>
            {t.tracks}: {timelineLength} {t.beats} ({Math.round(timelineLength / 4)} {t.bars})
          </span>
          <span style={{ color: '#3b82f6', fontWeight: 'bold' }}>
            {t.tracks}: {tracks.length} | {t.notes}: {tracks.reduce((total, track) =>
              total + track.clips.reduce((clipTotal, clip) =>
                clipTotal + (clip.notes?.length || 0), 0), 0)} | {t.audio}: {tracks.reduce((total, track) =>
                  total + track.clips.filter(clip => clip.type === 'audio').length, 0)}
          </span>
          {audioPlayerManagerRef.current.isGlobalPlaying && (
            <span style={{ color: '#10b981' }}>
              🎵 {t.audioPlaying}: {audioPlayerManagerRef.current.activePlayersCount}
            </span>
          )}
          {selectedClip && (
            <span style={{ color: '#10b981' }}>
              {t.selectedClip}: {(() => {
                const track = tracks.find(t => t.clips.some(c => c.id === selectedClip));
                const clip = track?.clips.find(c => c.id === selectedClip);
                return clip ? `${clip.name} (${clip.notes?.length || 0} ${t.notes})` : '';
              })()}
            </span>
          )}
          {showAutomation && (
            <span style={{ color: '#60a5fa', fontSize: '11px' }}>
              Automation: click track OFF button to enable per track
            </span>
          )}
          {snapToGrid && (
            <span style={{ color: '#10b981' }}>
              {t.snap}: 1/{gridSize}
            </span>
          )}
        </div>
        <div style={styles.statusRight}>
          {isRecording && (
            <>
              <div style={styles.recordingIndicator} />
              <span style={{ color: '#ef4444', fontWeight: 'bold' }}>{t.recording}</span>
            </>
          )}
          <Activity size={14} style={{ color: audioEngineReady ? '#10b981' : '#ef4444' }} />
          <span style={{ color: audioEngineReady ? '#10b981' : '#ef4444' }}>
            {audioEngineReady ? t.audioEngine : t.audioEngineNotReady}
          </span>
        </div>
      </div> */}

      {/* Track Selection Modal */}
      <TrackSelectionModal
        isOpen={showTrackSelectionModal}
        onClose={() => setShowTrackSelectionModal(false)}
        onSelectTrack={handleTrackSelection}
        t={t}
      />

      {/* Clip right-click context menu */}
      {clipContextMenu && (
        <>
          {/* Invisible backdrop — click anywhere to dismiss */}
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
            onMouseDown={() => setClipContextMenu(null)}
          />
          <div
            style={{
              position: 'fixed',
              top: clipContextMenu.y,
              left: clipContextMenu.x,
              zIndex: 9999,
              backgroundColor: '#1e293b',
              border: '1px solid #334155',
              borderRadius: '6px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
              minWidth: '160px',
              overflow: 'hidden',
              fontSize: '13px'
            }}
          >
            <div
              style={{
                padding: '8px 14px',
                cursor: 'pointer',
                color: '#f87171',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'background 0.15s'
              }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.15)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
              onMouseDown={e => {
                e.stopPropagation();
                deleteClip(clipContextMenu.clipId);
                setClipContextMenu(null);
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
              </svg>
              {language === 'zh' ? '删除片段' : 'Delete Clip'}
            </div>
          </div>
        </>
      )}

    </div>
  );
};

// Wrap the main component with LanguageProvider
const App = () => (
  <LanguageProvider>
    <DAWWorkstation />
  </LanguageProvider>
);

export default App;
